import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import Swal from 'sweetalert2';
import {
  FaBoxOpen,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaCopy,
  FaCreditCard,
  FaEdit,
  FaFileExcel,
  FaFilePdf,
  FaMoneyBillWave,
  FaPaperPlane,
  FaPlay,
  FaPlus,
  FaPrint,
  FaRedoAlt,
  FaSlidersH,
  FaTrash,
  FaUserFriends,
  FaUserTie,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import { getAdminActorType, hasAnyAllowedPath, readStoredAdminUser } from './adminAccessControl';
import { formatReportDateTime } from './reportDateTime';
import './PTPhase15Manager.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const PT_TABS = [
  { key: 'staff', label: 'Session Staff', icon: FaUserTie },
  { key: 'clients', label: 'Session Clients', icon: FaUserFriends },
  { key: 'progress', label: 'Client Progress', icon: FaChartLine },
  { key: 'sales', label: 'Session Sales', icon: FaCreditCard },
  { key: 'schedule', label: 'Session Schedule', icon: FaCalendarAlt },
  { key: 'whatsapp-admin', label: 'WhatsApp Admin', icon: FaPaperPlane },
  { key: 'knowledge-base', label: 'Knowledge Base', icon: FaSlidersH },
  { key: 'coach-payable', label: 'Coach Payable', icon: FaMoneyBillWave },
  { key: 'migration', label: 'Legacy Migration', icon: FaCalendarAlt },
];

const SALE_STATUS_OPTIONS = [
  { value: '', label: 'Semua status' },
  { value: 'pending', label: 'Pending' },
  { value: 'partial_paid', label: 'Partial Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const PT_SCHEDULE_STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'on_schedule', label: 'On Schedule' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
];

const PT_CONTRACT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatDateInput = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createClientForm = () => ({
  id: null,
  member_id: '',
  full_name: '',
  client_type: 'non_member',
  phone: '',
  email: '',
  notes: '',
  is_active: true,
});

const createPTUserForm = () => ({
  id: null,
  pt_name: '',
  username: '',
  password: '',
  is_active: true,
  package_share_mode: '',
  package_share_value: '',
  session_share_mode: '',
  session_share_value: '',
});

const createSaleForm = () => ({
  client_id: '',
  pt_user_id: '',
  product_id: '',
  payment_method: '',
  discount_amount: '',
  notes: '',
});

const formatDateTimeLocalInput = (value = new Date()) => {
  const date = value instanceof Date
    ? value
    : new Date(String(value || '').replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const formatClientProgressSessionStatus = item => {
  const note = String(item?.session_status_note || '').trim();
  if (note) {
    return note;
  }

  const parts = [
    item?.session_consumed ? 'Sesi terpotong' : 'Belum potong sesi',
  ];

  if (item?.remaining_sessions_known !== false && typeof item?.remaining_sessions !== 'undefined') {
    parts.push(`Sisa ${Number(item.remaining_sessions || 0).toLocaleString('id-ID')}`);
  }

  return parts.join(' • ');
};

const createSalePaymentForm = sale => ({
  amount: sale?.outstanding_amount ? String(Number(sale.outstanding_amount || 0)) : '',
  payment_method: sale?.payment_method || '',
  payment_reference: sale?.invoice_no || '',
  notes: '',
  paid_at: formatDateTimeLocalInput(new Date()),
  auto_release: true,
});

const createScheduleForm = () => ({
  id: null,
  pt_name: '',
  client_name: '',
  schedule_date: formatDateInput(),
  schedule_time: '07:00',
  status: 'scheduled',
  notes: '',
});

const createCoachPayoutForm = () => ({
  coach_name: '',
  amount: '',
  payment_account_id: '',
  payout_date: formatDateInput(),
  deduct_from_shift_cash: false,
  shift_cashier_id: '',
  shift_cashier_name: '',
  notes: '',
});

const createCoachAdjustmentForm = () => ({
  coach_name: '',
  adjustment_type: 'decrease',
  amount: '',
  adjustment_date: formatDateInput(),
  notes: '',
});

const createOpenClawSettingsForm = () => ({
  is_enabled: false,
  auto_send_pt_completed: true,
  openclaw_bin: 'openclaw',
  channel: 'whatsapp',
  account_id: '',
  control_url: '',
  webhook_secret: '',
});

const createOpenClawKnowledgeForm = () => ({
  id: null,
  category: 'general',
  question: '',
  answer: '',
  bullets: '',
  keywords: '',
  example_questions: '',
  priority: '100',
  is_active: true,
  notes: '',
});

const createSessionBalanceForm = clientID => ({
  client_id: clientID ? String(clientID) : '',
  package_key: '',
  contract_id: '',
  legacy_package_id: '',
  total_sessions: '',
  notes: '',
});

const createContractEditForm = () => ({
  id: '',
  sale_id: '',
  contract_no: '',
  product_name: '',
  pt_name: '',
  status: 'active',
  start_at: '',
  expire_at: '',
  total_sessions: 0,
  used_sessions: 0,
  remaining_sessions: 0,
  notes: '',
});

const normalizeTextAreaLines = value =>
  String(value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

const normalizeKeywordLines = value =>
  String(value || '')
    .split(/\n|,/)
    .map(item => item.trim())
    .filter(Boolean);

const joinTextAreaLines = values =>
  Array.isArray(values) ? values.filter(Boolean).join('\n') : '';

const buildSessionBalancePackageKey = item => {
  if (!item) {
    return '';
  }
  if (String(item.source || '').trim().toLowerCase() === 'legacy') {
    return `legacy:${item.legacy_package_id || item.id || 0}`;
  }
  return `contract:${item.contract_id || item.id || 0}`;
};

const getSessionBalancePackagePriority = item => {
  if (!item) {
    return 99;
  }

  const status = String(item.status || '').trim().toLowerCase();
  const remainingSessions = Number(item.remaining_sessions || 0);
  const totalSessions = Number(item.total_sessions || 0);
  const isLegacy = String(item.source || '').trim().toLowerCase() === 'legacy_package'
    || String(item.source || '').trim().toLowerCase() === 'legacy';
  const openStatus = status === 'active' || status === 'frozen' || status === 'inactive';

  if (remainingSessions > 0 && (openStatus || isLegacy || item.is_active)) {
    return 0;
  }
  if (remainingSessions > 0) {
    return 1;
  }
  if (totalSessions > 0 && openStatus) {
    return 2;
  }
  if (status === 'completed' || status === 'expired') {
    return 5;
  }
  return 4;
};

const sortSessionBalancePackages = packages =>
  [...(Array.isArray(packages) ? packages : [])].sort((left, right) => {
    const priorityDiff = getSessionBalancePackagePriority(left) - getSessionBalancePackagePriority(right);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    const remainingDiff = Number(right?.remaining_sessions || 0) - Number(left?.remaining_sessions || 0);
    if (remainingDiff !== 0) {
      return remainingDiff;
    }

    const totalDiff = Number(right?.total_sessions || 0) - Number(left?.total_sessions || 0);
    if (totalDiff !== 0) {
      return totalDiff;
    }

    return Number(right?.contract_id || right?.legacy_package_id || right?.id || 0) -
      Number(left?.contract_id || left?.legacy_package_id || left?.id || 0);
  });

const formatSessionBalancePackageLabel = item => {
  if (!item) {
    return '-';
  }
  const statusLabel = String(item.status || '').trim();
  const sourceLabel = String(item.source || '').trim().toLowerCase() === 'legacy'
    ? 'Legacy package'
    : `Contract ${statusLabel || 'active'}`;
  return [
    sourceLabel,
    item.product_name || 'PT Session',
    item.pt_name || 'Tanpa PT',
    `${Number(item.total_sessions || 0).toLocaleString('id-ID')} total`,
    `${Number(item.used_sessions || 0).toLocaleString('id-ID')} terpakai`,
    `${Number(item.remaining_sessions || 0).toLocaleString('id-ID')} sisa`,
  ].join(' • ');
};

const normalizeCoachPayableKey = value => String(value || '').trim().toLowerCase();

const resolveCoachPayableName = item => {
  if (!item || typeof item !== 'object') {
    return '';
  }

  const candidates = [
    item.coach_name,
    item.coachName,
    item.coach,
    item.staff_name,
    item.staffName,
    item.full_name,
    item.name,
  ];

  return candidates
    .map(value => String(value || '').trim())
    .find(Boolean) || '';
};

const normalizeCoachPayableItem = (item, index = 0) => {
  const normalizedCoachName = resolveCoachPayableName(item);
  const normalizedKey = normalizeCoachPayableKey(normalizedCoachName) || `coach-payable-${index}`;

  return {
    ...item,
    coach_name: normalizedCoachName,
    coach_payable_key: item?.coach_payable_key || normalizedKey,
  };
};

const isCashLikeAccount = account => {
  const candidate = `${account?.name || ''} ${account?.id || ''}`.toLowerCase();
  return ['cash', 'kas', 'tunai', 'petty'].some(keyword => candidate.includes(keyword));
};

const isApiSuccess = data =>
  data && (data.error === 0 || data.error === '0' || typeof data.error === 'undefined');

const memberSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '50px',
    borderRadius: '16px',
    borderColor: state.isFocused ? 'rgba(132, 175, 61, 0.45)' : 'rgba(24, 38, 30, 0.1)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(132, 175, 61, 0.38)',
    },
  }),
  valueContainer: base => ({
    ...base,
    padding: '0.18rem 0.65rem',
  }),
  placeholder: base => ({
    ...base,
    color: '#728273',
  }),
  singleValue: base => ({
    ...base,
    color: '#1d3024',
  }),
  input: base => ({
    ...base,
    color: '#1d3024',
  }),
  menu: base => ({
    ...base,
    zIndex: 30,
    borderRadius: '18px',
    overflow: 'hidden',
    border: '1px solid rgba(24, 38, 30, 0.08)',
    boxShadow: '0 24px 48px rgba(15, 30, 18, 0.12)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'rgba(176, 223, 92, 0.26)'
      : state.isFocused
        ? 'rgba(176, 223, 92, 0.16)'
        : '#fff',
    color: '#1d3024',
    padding: '0.82rem 0.95rem',
  }),
  noOptionsMessage: base => ({
    ...base,
    color: '#617067',
  }),
  loadingMessage: base => ({
    ...base,
    color: '#617067',
  }),
};

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const formatSignedCurrency = value => {
  const amount = Number(value || 0);
  const prefix = amount > 0 ? '+' : amount < 0 ? '-' : '';
  return `${prefix}${formatCurrency(Math.abs(amount))}`;
};

const formatDateTime = value => {
  return formatReportDateTime(value);
};

const formatPTProgressBookingLabel = value => {
  const normalized = String(value || '').trim().toLowerCase();
  return {
    requested: 'Requested',
    confirmed: 'Confirmed',
    rescheduled: 'Rescheduled',
    cancelled: 'Cancelled',
  }[normalized] || value || '-';
};

const formatPTProgressAttendanceLabel = value => {
  const normalized = String(value || '').trim().toLowerCase();
  return {
    pending: 'Pending',
    checked_in: 'Checked In',
    completed: 'Completed',
    no_show: 'No Show',
    cancelled: 'Cancelled',
  }[normalized] || value || '-';
};

const formatFitnessCoachTransactionTypeLabel = value => {
  const normalized = String(value || '').trim().toLowerCase();
  return {
    class_sale: 'Class Sale',
    pt_package_sale: 'PT Package Sale',
    pt_session_sale: 'PT Session Sale',
    coach_adjustment: 'Koreksi Hutang',
    coach_payout: 'Coach Payout',
  }[normalized] || value || '-';
};

const sanitizeCoachPayableDetailFilename = value =>
  (String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'coach');

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildPTClientProgressShareText = report => {
  if (!report?.client) {
    return '';
  }

  const client = report.client;
  const summary = report.summary || {};
  const clientName = String(client.full_name || '').trim() || 'Client';

  const lines = [
    `Halo Kak ${clientName}, berikut progress latihan kakak:`,
    '',
    `Terakhir kakak hadir tanggal: ${formatDateTime(summary.last_attendance_at)}.`,
    `Sisa sesi aktif: ${Number(summary.remaining_sessions || 0).toLocaleString('id-ID')} sesi.`,
    `Jadwal berikutnya: ${formatDateTime(summary.next_session_at)}.`,
  ];

  lines.push('', 'Terima kasih Kak, mohon berikan testimoni/saran/masukan untuk PT kami.');
  return lines.join('\n');
};

const buildPTClientProgressFollowUpText = (report, workspace) => {
  const client = workspace?.client || report?.client || null;
  if (!client) {
    return '';
  }

  const summary = workspace?.summary || {};
  const goalProfile = workspace?.goal_profile || {};
  const metrics = Array.isArray(workspace?.metrics) ? workspace.metrics : [];
  const suggestions = Array.isArray(workspace?.suggestions) ? workspace.suggestions.filter(Boolean) : [];
  const alerts = Array.isArray(workspace?.alerts) ? workspace.alerts.filter(Boolean) : [];
  const latestMeasurement = workspace?.latest_measurement || null;
  const clientName = String(client.full_name || '').trim() || 'Client PT';
  const remainingSessions = Number(report?.summary?.remaining_sessions ?? client?.remaining_sessions ?? 0);
  const targetDateLabel = goalProfile?.target_date ? formatDateTime(goalProfile.target_date) : '-';
  const nextSessionLabel = report?.summary?.next_session_at ? formatDateTime(report.summary.next_session_at) : '-';

  const measurementParts = [
    latestMeasurement?.body_weight_kg ? `Berat ${formatGoalMetricValue(latestMeasurement.body_weight_kg, 1, 'kg')}` : '',
    latestMeasurement?.body_fat_percent ? `Body fat ${formatGoalMetricValue(latestMeasurement.body_fat_percent, 1, '%')}` : '',
    latestMeasurement?.waist_cm ? `Waist ${formatGoalMetricValue(latestMeasurement.waist_cm, 1, 'cm')}` : '',
    latestMeasurement?.muscle_mass_kg ? `Muscle ${formatGoalMetricValue(latestMeasurement.muscle_mass_kg, 1, 'kg')}` : '',
  ].filter(Boolean);

  const metricHighlights = metrics
    .filter(item => item?.label)
    .slice(0, 3)
    .map(item => (
      `- ${item.label}: ${formatGoalMetricValue(item.current_value, 1, item.unit || '')} ` +
      `(target ${formatGoalMetricValue(item.target_value, 1, item.unit || '')} | progress ${formatGoalMetricValue(item.progress_percent, 1, '%')})`
    ));

  const lines = [
    'FOLLOW UP CLIENT PT',
    `Nama client: ${clientName}`,
    `Goal utama: ${formatGoalTypeLabel(goalProfile?.primary_goal || 'fat_loss')}`,
    `Sisa sesi aktif: ${remainingSessions.toLocaleString('id-ID')} sesi`,
    `Status progress: ${summary.goal_status_label || 'Belum ada status'}`,
    `Progress goal: ${formatGoalMetricValue(summary.overall_progress_percent, 1, '%')}`,
    `Adherence 28 hari: ${formatGoalMetricValue(summary.adherence_percent, 1, '%')}`,
    `Pace gap: ${formatGoalSignedMetricValue(summary.pace_gap_percent, 1, '%')}`,
    `Target date: ${targetDateLabel}`,
    `Next session: ${nextSessionLabel}`,
  ];

  if (measurementParts.length) {
    lines.push(`Measurement terakhir: ${measurementParts.join(' • ')}`);
  }

  if (metricHighlights.length) {
    lines.push('', 'Highlight progress:');
    lines.push(...metricHighlights);
  }

  if (alerts.length) {
    lines.push('', 'Alert utama:');
    lines.push(...alerts.slice(0, 3).map(item => `- ${item}`));
  }

  if (suggestions.length) {
    lines.push('', 'Prioritas follow up:');
    lines.push(...suggestions.slice(0, 3).map(item => `- ${item}`));
  }

  lines.push(
    '',
    'Template follow up:',
    `Halo Kak ${clientName}, kami bantu kirim update progress PT Kakak. Saat ini progress goal ada di ${formatGoalMetricValue(summary.overall_progress_percent, 1, '%')} dengan status ${summary.goal_status_label || 'monitoring berjalan'}. Sisa sesi aktif Kakak ${remainingSessions.toLocaleString('id-ID')} sesi. Kalau berkenan, kita lanjut follow up untuk rapikan jadwal, target mingguan, dan next step program ya Kak.`
  );

  return lines.join('\n');
};

const formatMemberLabel = member => {
  const fullName = [member?.first_name, member?.last_name].filter(Boolean).join(' ').trim();
  const displayName = fullName || `Member #${member?.id || '-'}`;
  const contact = [member?.hp, member?.email, member?.identity_no].filter(Boolean).join(' • ');
  return contact ? `${displayName} • ${contact}` : displayName;
};

const formatMemberName = member =>
  [member?.first_name, member?.last_name].filter(Boolean).join(' ').trim();

const buildMemberOption = member => ({
  value: String(member?.id ?? ''),
  label: formatMemberLabel(member),
  data: member,
});

const PT_GOAL_TREND_META = {
  body_fat_percent: {
    label: 'Body Fat',
    unit: '%',
    digits: 1,
    color: '#ff6f6f',
    gradientFrom: 'rgba(255, 111, 111, 0.26)',
    gradientTo: 'rgba(255, 111, 111, 0.03)',
  },
  body_weight_kg: {
    label: 'Berat',
    unit: 'kg',
    digits: 1,
    color: '#58b7ff',
    gradientFrom: 'rgba(88, 183, 255, 0.26)',
    gradientTo: 'rgba(88, 183, 255, 0.03)',
  },
  waist_cm: {
    label: 'Waist',
    unit: 'cm',
    digits: 1,
    color: '#f5ae45',
    gradientFrom: 'rgba(245, 174, 69, 0.26)',
    gradientTo: 'rgba(245, 174, 69, 0.03)',
  },
  muscle_mass_kg: {
    label: 'Muscle',
    unit: 'kg',
    digits: 1,
    color: '#8f7dff',
    gradientFrom: 'rgba(143, 125, 255, 0.26)',
    gradientTo: 'rgba(143, 125, 255, 0.03)',
  },
};

const clampPercentage = value => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.max(0, Math.min(100, numeric));
};

const formatGoalMetricValue = (value, digits = 1, unit = '') => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
};

const formatGoalSignedMetricValue = (value, digits = 1, unit = '') => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${numeric.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
};

const formatGoalTypeLabel = value => {
  const normalized = String(value || '').trim().toLowerCase();
  return {
    fat_loss: 'Fat Loss',
    body_recomposition: 'Body Recomposition',
    muscle_gain: 'Muscle Gain',
    weight_gain: 'Weight Gain',
    maintenance: 'Maintenance',
  }[normalized] || value || '-';
};

const formatShortDateLabel = value => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }
  const parsed = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) {
    return raw.slice(5, 10);
  }
  return `${String(parsed.getDate()).padStart(2, '0')}/${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

const buildGoalTrendSeries = (workspace, metricKey) => {
  const rows = Array.isArray(workspace?.recent_measurements) ? workspace.recent_measurements : [];
  return rows
    .slice()
    .reverse()
    .map(item => ({
      label: formatShortDateLabel(item?.measured_at),
      rawLabel: item?.measured_at || '',
      value: Number(item?.[metricKey]),
    }))
    .filter(item => Number.isFinite(item.value));
};

const getPreferredGoalTrendMetric = workspace => {
  const goalType = String(workspace?.goal_profile?.primary_goal || '').trim().toLowerCase();
  const preferredMap = {
    fat_loss: ['body_fat_percent', 'waist_cm', 'body_weight_kg'],
    body_recomposition: ['body_fat_percent', 'muscle_mass_kg', 'waist_cm'],
    muscle_gain: ['muscle_mass_kg', 'body_weight_kg', 'body_fat_percent'],
    weight_gain: ['body_weight_kg', 'muscle_mass_kg'],
    maintenance: ['body_weight_kg', 'body_fat_percent', 'waist_cm'],
  };

  const candidates = preferredMap[goalType] || Object.keys(PT_GOAL_TREND_META);
  for (const key of candidates) {
    if (buildGoalTrendSeries(workspace, key).length > 0) {
      return key;
    }
  }
  return 'body_fat_percent';
};

const getGoalStatusTone = value => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'on_track') {
    return 'good';
  }
  if (normalized === 'needs_attention') {
    return 'warn';
  }
  if (normalized === 'off_track') {
    return 'danger';
  }
  return 'default';
};

function PTGoalTrendChart({ workspace, metricKey, onMetricChange }) {
  const availableMetricKeys = Object.keys(PT_GOAL_TREND_META).filter(key => buildGoalTrendSeries(workspace, key).length > 0);
  const activeMetricKey = availableMetricKeys.includes(metricKey)
    ? metricKey
    : (availableMetricKeys[0] || 'body_fat_percent');
  const config = PT_GOAL_TREND_META[activeMetricKey];
  const points = buildGoalTrendSeries(workspace, activeMetricKey);

  if (!availableMetricKeys.length || !config) {
    return (
      <div className="pt-phase15-goal-trend-card">
        <div className="pt-phase15-goal-trend-head">
          <div>
            <div className="pt-phase15-goal-trend-kicker">Trend Measurement</div>
            <h5>Belum ada chart measurement</h5>
          </div>
        </div>
        <div className="pt-phase15-goal-trend-empty">
          Tambahkan measurement client agar admin bisa membaca tren progress secara visual.
        </div>
      </div>
    );
  }

  if (points.length === 1) {
    return (
      <div className="pt-phase15-goal-trend-card">
        <div className="pt-phase15-goal-trend-head">
          <div>
            <div className="pt-phase15-goal-trend-kicker">Trend Measurement</div>
            <h5>{config.label} • 1 snapshot</h5>
          </div>
          <div className="pt-phase15-goal-trend-tabs">
            {Object.entries(PT_GOAL_TREND_META).map(([key, item]) => (
              <button
                key={key}
                type="button"
                className={`pt-phase15-goal-trend-tab ${activeMetricKey === key ? 'is-active' : ''}`}
                onClick={() => onMetricChange(key)}
                disabled={!availableMetricKeys.includes(key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="pt-phase15-goal-trend-empty">
          Baru ada 1 measurement untuk {config.label.toLowerCase()}. Tambahkan snapshot berikutnya supaya tren terlihat.
        </div>
      </div>
    );
  }

  const width = 720;
  const height = 220;
  const paddingX = 24;
  const paddingTop = 18;
  const paddingBottom = 28;
  const innerWidth = width - (paddingX * 2);
  const innerHeight = height - paddingTop - paddingBottom;
  const values = points.map(item => item.value);
  let minValue = Math.min(...values);
  let maxValue = Math.max(...values);
  if (minValue === maxValue) {
    const pad = Math.max(1, Math.abs(minValue) * 0.08);
    minValue -= pad;
    maxValue += pad;
  }
  const range = maxValue - minValue;
  const xStep = points.length > 1 ? innerWidth / (points.length - 1) : innerWidth;
  const coords = points.map((item, index) => {
    const x = paddingX + (index * xStep);
    const normalized = range === 0 ? 0.5 : (item.value - minValue) / range;
    const y = paddingTop + innerHeight - (normalized * innerHeight);
    return { ...item, x, y };
  });

  const linePath = coords
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${coords[coords.length - 1].x.toFixed(2)} ${(paddingTop + innerHeight).toFixed(2)} L ${coords[0].x.toFixed(2)} ${(paddingTop + innerHeight).toFixed(2)} Z`;
  const gridLines = [0, 0.33, 0.66, 1].map(fraction => paddingTop + (innerHeight * fraction));
  const visibleLabels = coords.filter((item, index) => {
    if (coords.length <= 6) {
      return true;
    }
    const stride = Math.ceil(coords.length / 5);
    return index === 0 || index === coords.length - 1 || index % stride === 0;
  });
  const firstValue = points[0].value;
  const lastValue = points[points.length - 1].value;
  const deltaValue = lastValue - firstValue;
  const deltaLabel = `${deltaValue > 0 ? '+' : ''}${deltaValue.toFixed(config.digits)} ${config.unit}`;

  return (
    <div className="pt-phase15-goal-trend-card">
      <div className="pt-phase15-goal-trend-head">
        <div>
          <div className="pt-phase15-goal-trend-kicker">Trend Measurement</div>
          <h5>{config.label} • {points.length} snapshot</h5>
        </div>
        <div className="pt-phase15-goal-trend-tabs">
          {Object.entries(PT_GOAL_TREND_META).map(([key, item]) => (
            <button
              key={key}
              type="button"
              className={`pt-phase15-goal-trend-tab ${activeMetricKey === key ? 'is-active' : ''}`}
              onClick={() => onMetricChange(key)}
              disabled={!availableMetricKeys.includes(key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pt-phase15-goal-trend-stage">
        <div className="pt-phase15-goal-trend-legend">
          <span className="pt-phase15-goal-trend-legend-dot" style={{ backgroundColor: config.color }} />
          <span>{config.label}</span>
        </div>
        <svg className="pt-phase15-goal-trend-svg" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id={`ptGoalTrendGradient-${activeMetricKey}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={config.gradientFrom} />
              <stop offset="100%" stopColor={config.gradientTo} />
            </linearGradient>
          </defs>
          <g className="pt-phase15-goal-trend-grid">
            {gridLines.map(y => (
              <line key={y} x1={paddingX} y1={y} x2={width - paddingX} y2={y} />
            ))}
          </g>
          <path
            className="pt-phase15-goal-trend-area"
            d={areaPath}
            fill={`url(#ptGoalTrendGradient-${activeMetricKey})`}
          />
          <path
            className="pt-phase15-goal-trend-line"
            d={linePath}
            stroke={config.color}
            pathLength="100"
          />
          {coords.map((point, index) => (
            <circle
              key={`${point.rawLabel}-${index}`}
              className="pt-phase15-goal-trend-point"
              cx={point.x}
              cy={point.y}
              r="4"
              style={{ animationDelay: `${140 + (index * 85)}ms`, stroke: config.color }}
            />
          ))}
          {visibleLabels.map(point => (
            <text
              key={`label-${point.rawLabel}-${point.x}`}
              className="pt-phase15-goal-trend-label"
              x={point.x}
              y={height - 5}
              textAnchor="middle"
            >
              {point.label}
            </text>
          ))}
        </svg>
      </div>

      <div className="pt-phase15-goal-trend-meta">
        <div className="pt-phase15-goal-trend-meta-card">
          <span>Awal</span>
          <strong>{formatGoalMetricValue(firstValue, config.digits, config.unit)}</strong>
        </div>
        <div className="pt-phase15-goal-trend-meta-card">
          <span>Sekarang</span>
          <strong>{formatGoalMetricValue(lastValue, config.digits, config.unit)}</strong>
        </div>
        <div className="pt-phase15-goal-trend-meta-card">
          <span>Perubahan</span>
          <strong>{deltaLabel}</strong>
        </div>
        <div className="pt-phase15-goal-trend-meta-card">
          <span>Snapshot</span>
          <strong>{points.length}</strong>
        </div>
      </div>
    </div>
  );
}

function PTPhase15Manager({ pageMode = 'workspace' }) {
  const token = useMemo(() => localStorage.getItem('sessionToken') || '', []);
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const actorType = useMemo(() => getAdminActorType(currentUser), [currentUser]);
  const isScopedActor = actorType === 'outlet' || actorType === 'karyawan';
  const isClientsOnlyMode = pageMode === 'clients-only';
  const canManageCoachPayables = actorType !== 'karyawan';
  const canManageOpenClaw = actorType !== 'karyawan';
  const canAdjustSessionBalance = actorType !== 'karyawan';
  const canUseMemberLookup = useMemo(
    () => isScopedActor || hasAnyAllowedPath('/members', currentUser),
    [currentUser, isScopedActor]
  );
  const initialOutlet = useMemo(
    () =>
      String(
        currentUser?.outlet_id ||
        currentUser?.outlet ||
        currentUser?.outletId ||
        ''
      ),
    [currentUser]
  );

  const [activeTab, setActiveTab] = useState('clients');
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(initialOutlet);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [ptUsers, setPTUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [salePayments, setSalePayments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [coachPayables, setCoachPayables] = useState([]);
  const [coachPayableDetails, setCoachPayableDetails] = useState([]);
  const [coachPayouts, setCoachPayouts] = useState([]);
  const [coachShiftStaffOptions, setCoachShiftStaffOptions] = useState([]);
  const [openClawSettingsForm, setOpenClawSettingsForm] = useState(createOpenClawSettingsForm);
  const [openClawKnowledgeItems, setOpenClawKnowledgeItems] = useState([]);
  const [openClawConversations, setOpenClawConversations] = useState([]);
  const [openClawMessages, setOpenClawMessages] = useState([]);
  const [migrationSummary, setMigrationSummary] = useState(null);
  const [isMigrationSummaryLoading, setIsMigrationSummaryLoading] = useState(false);
  const [lastMigrationActionName, setLastMigrationActionName] = useState('');
  const [lastMigrationResult, setLastMigrationResult] = useState(null);

  const [clientSearch, setClientSearch] = useState('');
  const [saleSearch, setSaleSearch] = useState('');
  const [saleStatus, setSaleStatus] = useState('');
  const [scheduleFilterDate, setScheduleFilterDate] = useState(formatDateInput());
  const [clientProgressSearch, setClientProgressSearch] = useState('');
  const [openClawKnowledgeSearch, setOpenClawKnowledgeSearch] = useState('');
  const [openClawConversationSearch, setOpenClawConversationSearch] = useState('');
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [selectedProgressClientId, setSelectedProgressClientId] = useState(null);
  const [selectedOpenClawConversationKey, setSelectedOpenClawConversationKey] = useState('');
  const [selectedCoachPayableDetail, setSelectedCoachPayableDetail] = useState(null);
  const [isSalePaymentLoading, setIsSalePaymentLoading] = useState(false);
  const [isClientProgressLoading, setIsClientProgressLoading] = useState(false);
  const [isCoachPayableDetailLoading, setIsCoachPayableDetailLoading] = useState(false);
  const [isCoachPayableAdjustmentSaving, setIsCoachPayableAdjustmentSaving] = useState(false);
  const [isOpenClawSettingsLoading, setIsOpenClawSettingsLoading] = useState(false);
  const [isOpenClawSettingsSaving, setIsOpenClawSettingsSaving] = useState(false);
  const [isOpenClawKnowledgeLoading, setIsOpenClawKnowledgeLoading] = useState(false);
  const [isOpenClawKnowledgeSaving, setIsOpenClawKnowledgeSaving] = useState(false);
  const [isOpenClawInboxLoading, setIsOpenClawInboxLoading] = useState(false);
  const [isOpenClawMessagesLoading, setIsOpenClawMessagesLoading] = useState(false);
  const [isOpenClawReplySending, setIsOpenClawReplySending] = useState(false);
  const [clientProgressReport, setClientProgressReport] = useState(null);
  const [clientGoalWorkspace, setClientGoalWorkspace] = useState(null);
  const [clientProgressDetailTab, setClientProgressDetailTab] = useState('overview');
  const [clientGoalTrendMetric, setClientGoalTrendMetric] = useState('body_fat_percent');
  const [sessionBalanceClient, setSessionBalanceClient] = useState(null);
  const [sessionBalancePackages, setSessionBalancePackages] = useState([]);
  const [isSessionBalanceLoading, setIsSessionBalanceLoading] = useState(false);
  const [isSessionBalanceSaving, setIsSessionBalanceSaving] = useState(false);
  const [isContractEditSaving, setIsContractEditSaving] = useState(false);

  const [clientForm, setClientForm] = useState(createClientForm);
  const [ptUserForm, setPTUserForm] = useState(createPTUserForm);
  const [saleForm, setSaleForm] = useState(createSaleForm);
  const [salePaymentForm, setSalePaymentForm] = useState(createSalePaymentForm());
  const [scheduleForm, setScheduleForm] = useState(createScheduleForm);
  const [coachPayoutForm, setCoachPayoutForm] = useState(createCoachPayoutForm);
  const [coachAdjustmentForm, setCoachAdjustmentForm] = useState(createCoachAdjustmentForm);
  const [sessionBalanceForm, setSessionBalanceForm] = useState(createSessionBalanceForm());
  const [contractEditForm, setContractEditForm] = useState(createContractEditForm());
  const [openClawKnowledgeForm, setOpenClawKnowledgeForm] = useState(createOpenClawKnowledgeForm());
  const [openClawReplyBody, setOpenClawReplyBody] = useState('');
  const [selectedCoachPayableNames, setSelectedCoachPayableNames] = useState([]);
  const [selectedClientMemberOption, setSelectedClientMemberOption] = useState(null);
  const [showCoachPayableDetailModal, setShowCoachPayableDetailModal] = useState(false);
  const [showCoachAdjustmentModal, setShowCoachAdjustmentModal] = useState(false);
  const [showOpenClawKnowledgeModal, setShowOpenClawKnowledgeModal] = useState(false);
  const [showSessionBalanceModal, setShowSessionBalanceModal] = useState(false);
  const [showContractEditModal, setShowContractEditModal] = useState(false);

  const availableTabs = useMemo(
    () =>
      PT_TABS.filter(item => {
        if (item.key === 'coach-payable' && !canManageCoachPayables) {
          return false;
        }
        if (item.key === 'whatsapp-admin' && !canManageOpenClaw) {
          return false;
        }
        if (item.key === 'knowledge-base' && !canManageOpenClaw) {
          return false;
        }
        if (isClientsOnlyMode) {
          return item.key === 'clients';
        }
        return true;
      }),
    [canManageCoachPayables, canManageOpenClaw, isClientsOnlyMode]
  );

  const availableTabKeys = useMemo(
    () => new Set(availableTabs.map(item => item.key)),
    [availableTabs]
  );

  const shouldLoadMembers = availableTabKeys.has('clients') && canUseMemberLookup;
  const shouldLoadClients = ['clients', 'progress', 'sales', 'schedule'].some(key => availableTabKeys.has(key));
  const shouldLoadPTUsers = ['staff', 'sales', 'schedule'].some(key => availableTabKeys.has(key));
  const shouldLoadProducts = availableTabKeys.has('sales');
  const shouldLoadSales = availableTabKeys.has('sales');
  const shouldLoadSchedules = availableTabKeys.has('schedule');
  const shouldLoadCoachPayables = availableTabKeys.has('coach-payable') && canManageCoachPayables;
  const shouldLoadOpenClaw = availableTabKeys.has('whatsapp-admin') && canManageOpenClaw;
  const shouldLoadOpenClawKnowledge = availableTabKeys.has('knowledge-base') && canManageOpenClaw;
  const shouldLoadMigration = availableTabKeys.has('migration');
  const shouldLoadPaymentAccounts = availableTabKeys.has('sales') || shouldLoadCoachPayables;
  const canOpenClientProgress = availableTabKeys.has('progress');

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const selectedOutletNumber = useMemo(() => {
    const parsed = Number(selectedOutlet || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [selectedOutlet]);

  const outletNameMap = useMemo(() => {
    const map = new Map();
    outlets.forEach(item => {
      map.set(String(item.id), item.outlet_name);
    });
    return map;
  }, [outlets]);

  const selectedPaymentAccount = useMemo(
    () => paymentAccounts.find(item => String(item.id) === String(saleForm.payment_method)),
    [paymentAccounts, saleForm.payment_method]
  );

  const selectedSale = useMemo(
    () => sales.find(item => String(item.id) === String(selectedSaleId)) || null,
    [sales, selectedSaleId]
  );

  const selectedProgressClient = useMemo(
    () => clients.find(item => String(item.id) === String(selectedProgressClientId)) || null,
    [clients, selectedProgressClientId]
  );

  const clientGoalWorkspaceSummary = useMemo(
    () => clientGoalWorkspace?.summary || null,
    [clientGoalWorkspace]
  );

  const clientGoalWorkspaceMetrics = useMemo(
    () => (Array.isArray(clientGoalWorkspace?.metrics) ? clientGoalWorkspace.metrics : []),
    [clientGoalWorkspace]
  );

  const clientGoalWorkspaceSuggestions = useMemo(
    () => (Array.isArray(clientGoalWorkspace?.suggestions) ? clientGoalWorkspace.suggestions : []),
    [clientGoalWorkspace]
  );

  const clientGoalWorkspaceAlerts = useMemo(
    () => (Array.isArray(clientGoalWorkspace?.alerts) ? clientGoalWorkspace.alerts : []),
    [clientGoalWorkspace]
  );

  const availableGoalTrendMetricKeys = useMemo(
    () => Object.keys(PT_GOAL_TREND_META).filter(key => buildGoalTrendSeries(clientGoalWorkspace, key).length > 0),
    [clientGoalWorkspace]
  );

  const activeGoalTrendMetric = useMemo(() => {
    if (!availableGoalTrendMetricKeys.length) {
      return 'body_fat_percent';
    }
    if (availableGoalTrendMetricKeys.includes(clientGoalTrendMetric)) {
      return clientGoalTrendMetric;
    }
    return getPreferredGoalTrendMetric(clientGoalWorkspace);
  }, [availableGoalTrendMetricKeys, clientGoalTrendMetric, clientGoalWorkspace]);

  const clientGoalHeroClient = useMemo(
    () => clientGoalWorkspace?.client || clientProgressReport?.client || selectedProgressClient || null,
    [clientGoalWorkspace, clientProgressReport, selectedProgressClient]
  );

  const clientGoalFocusMeters = useMemo(() => {
    const summary = clientGoalWorkspaceSummary || {};
    return [
      {
        key: 'overall',
        label: 'Progress goal',
        value: clampPercentage(summary.overall_progress_percent),
        valueLabel: formatGoalMetricValue(summary.overall_progress_percent, 1, '%'),
        note: 'Gabungan hasil metrik utama terhadap target client.',
      },
      {
        key: 'adherence',
        label: 'Adherence',
        value: clampPercentage(summary.adherence_percent),
        valueLabel: formatGoalMetricValue(summary.adherence_percent, 1, '%'),
        note: 'Kedisiplinan hadir dan menyelesaikan sesi 28 hari terakhir.',
      },
      {
        key: 'completeness',
        label: 'Kelengkapan data',
        value: clampPercentage(summary.measurement_completeness_percent),
        valueLabel: formatGoalMetricValue(summary.measurement_completeness_percent, 1, '%'),
        note: 'Semakin lengkap measurement, semakin presisi insight AI.',
      },
    ];
  }, [clientGoalWorkspaceSummary]);

  const clientGoalOverviewCards = useMemo(() => {
    const summary = clientGoalWorkspaceSummary || {};
    const profile = clientGoalWorkspace?.goal_profile || null;
    return [
      {
        key: 'status',
        label: 'Status goal',
        value: summary.goal_status_label || '-',
        tone: getGoalStatusTone(summary.goal_status),
      },
      {
        key: 'pace',
        label: 'Pace gap',
        value: formatGoalSignedMetricValue(summary.pace_gap_percent, 1, '%'),
        tone: Number(summary.pace_gap_percent || 0) >= 0 ? 'good' : 'warn',
      },
      {
        key: 'fat',
        label: 'Fat mass',
        value: formatGoalSignedMetricValue(summary.fat_mass_change_kg, 2, 'kg'),
        tone: Number(summary.fat_mass_change_kg || 0) <= 0 ? 'good' : 'danger',
      },
      {
        key: 'lean',
        label: 'Lean mass',
        value: formatGoalSignedMetricValue(summary.lean_mass_change_kg, 2, 'kg'),
        tone: Number(summary.lean_mass_change_kg || 0) >= 0 ? 'good' : 'warn',
      },
      {
        key: 'target',
        label: 'Target date',
        value: profile?.target_date ? formatDateTime(profile.target_date) : '-',
        tone: 'default',
      },
      {
        key: 'days',
        label: 'Hari ke target',
        value: summary.days_to_target === null || typeof summary.days_to_target === 'undefined'
          ? '-'
          : Number(summary.days_to_target || 0).toLocaleString('id-ID'),
        tone: 'default',
      },
    ];
  }, [clientGoalWorkspace, clientGoalWorkspaceSummary]);

  const clientGoalMeasurementHighlights = useMemo(() => {
    const latest = clientGoalWorkspace?.latest_measurement || null;
    const baseline = clientGoalWorkspace?.baseline_measurement || null;
    return [
      {
        key: 'latest-weight',
        label: 'Berat terakhir',
        value: formatGoalMetricValue(latest?.body_weight_kg, 1, 'kg'),
        helper: baseline?.body_weight_kg ? `Baseline ${formatGoalMetricValue(baseline.body_weight_kg, 1, 'kg')}` : 'Belum ada baseline',
      },
      {
        key: 'latest-bodyfat',
        label: 'Body fat terakhir',
        value: formatGoalMetricValue(latest?.body_fat_percent, 1, '%'),
        helper: baseline?.body_fat_percent ? `Baseline ${formatGoalMetricValue(baseline.body_fat_percent, 1, '%')}` : 'Belum ada baseline',
      },
      {
        key: 'latest-waist',
        label: 'Waist terakhir',
        value: formatGoalMetricValue(latest?.waist_cm, 1, 'cm'),
        helper: latest?.measured_at ? `Update ${formatDateTime(latest.measured_at)}` : 'Belum ada measurement',
      },
      {
        key: 'latest-muscle',
        label: 'Muscle terakhir',
        value: formatGoalMetricValue(latest?.muscle_mass_kg, 1, 'kg'),
        helper: latest?.protein_g_avg ? `Protein ${formatGoalMetricValue(latest.protein_g_avg, 0, 'g')}/hari` : 'Protein harian belum diisi',
      },
    ];
  }, [clientGoalWorkspace]);

  const selectedSalePaymentAccount = useMemo(
    () => paymentAccounts.find(item => String(item.id) === String(salePaymentForm.payment_method)),
    [paymentAccounts, salePaymentForm.payment_method]
  );

  const selectedCoachPayable = useMemo(
    () => coachPayables.find(item => String(item.coach_name) === String(coachPayoutForm.coach_name)) || null,
    [coachPayables, coachPayoutForm.coach_name]
  );

  const selectedCoachPaymentAccount = useMemo(
    () => paymentAccounts.find(item => String(item.id) === String(coachPayoutForm.payment_account_id)) || null,
    [coachPayoutForm.payment_account_id, paymentAccounts]
  );

  const selectedCoachPaymentAccountIsCash = useMemo(
    () => isCashLikeAccount(selectedCoachPaymentAccount),
    [selectedCoachPaymentAccount]
  );

  const selectedCoachShiftStaffOption = useMemo(
    () =>
      coachShiftStaffOptions.find(
        item => String(item.staff_id) === String(coachPayoutForm.shift_cashier_id)
      ) || null,
    [coachPayoutForm.shift_cashier_id, coachShiftStaffOptions]
  );

  const selectedOpenClawConversation = useMemo(
    () => openClawConversations.find(item => item.conversation_key === selectedOpenClawConversationKey) || null,
    [openClawConversations, selectedOpenClawConversationKey]
  );

  const selectedCoachPayableKeySet = useMemo(
    () => new Set(selectedCoachPayableNames.map(normalizeCoachPayableKey).filter(Boolean)),
    [selectedCoachPayableNames]
  );

  const selectedCoachPayables = useMemo(
    () =>
      coachPayables.filter(item => selectedCoachPayableKeySet.has(normalizeCoachPayableKey(item.coach_name))),
    [coachPayables, selectedCoachPayableKeySet]
  );

  const coachPayableDetailPrintRows = useMemo(
    () =>
      coachPayableDetails.map(item => ({
        date: formatDateTime(item.created_at),
        client: item.detail_type === 'sale' ? item.client_name || '-' : '-',
        channel: formatFitnessCoachTransactionTypeLabel(item.transaction_type),
        program: item.detail_type === 'adjustment'
          ? [
              item.adjustment_type === 'increase' ? 'Tambah hutang coach' : 'Kurangi hutang coach',
              item.notes || '',
            ].filter(Boolean).join(' • ') || '-'
          : item.detail_type === 'payout'
            ? [item.notes || '', item.payment_method || '']
              .filter(Boolean)
              .join(' • ') || 'Pembayaran coach'
            : item.class_name || item.pt_name || '-',
        gross: formatCurrency(item.gross_amount),
        share: item.detail_type === 'sale'
          ? formatCurrency(item.coach_share_amount)
          : formatSignedCurrency(item.coach_share_amount),
        balance: `${formatCurrency(item.outstanding_before_amount)} -> ${formatCurrency(item.outstanding_after_amount)}`,
        payment: item.detail_type === 'adjustment'
          ? [item.reference_no, item.journal_id ? `Jurnal ${item.journal_id}` : '']
            .filter(Boolean)
            .join(' • ') || '-'
          : item.detail_type === 'payout'
            ? [item.reference_no, item.payment_method || '-', item.journal_id ? `Jurnal ${item.journal_id}` : '']
              .filter(Boolean)
              .join(' • ')
          : item.payment_method || '-',
        status: item.status || '-',
      })),
    [coachPayableDetails]
  );

  const selectedCoachPayableTotal = useMemo(
    () => selectedCoachPayables.reduce((total, item) => total + Number(item.outstanding_amount || 0), 0),
    [selectedCoachPayables]
  );

  const isBatchCoachPayoutMode = selectedCoachPayables.length > 0;

  const areAllCoachPayablesSelected = useMemo(
    () => coachPayables.length > 0 && selectedCoachPayables.length === coachPayables.length,
    [coachPayables.length, selectedCoachPayables.length]
  );

  const totalCoachPayableOutstanding = useMemo(
    () => coachPayables.reduce((total, item) => total + Number(item.outstanding_amount || 0), 0),
    [coachPayables]
  );

  const totalCoachPayableAccrued = useMemo(
    () => coachPayables.reduce((total, item) => total + Number(item.accrued_amount || 0), 0),
    [coachPayables]
  );

  const totalCoachPayablePaid = useMemo(
    () => coachPayables.reduce((total, item) => total + Number(item.paid_amount || 0), 0),
    [coachPayables]
  );

  const totalCoachPayableDetailSalesShare = useMemo(
    () => coachPayableDetails
      .filter(item => item.detail_type === 'sale')
      .reduce((total, item) => total + Number(item.coach_share_amount || 0), 0),
    [coachPayableDetails]
  );

  const totalCoachPayableDetailAdjustment = useMemo(
    () => coachPayableDetails
      .filter(item => item.detail_type === 'adjustment')
      .reduce((total, item) => total + Number(item.coach_share_amount || 0), 0),
    [coachPayableDetails]
  );

  const totalCoachPayableDetailPayout = useMemo(
    () => coachPayableDetails
      .filter(item => item.detail_type === 'payout')
      .reduce((total, item) => total + Math.abs(Number(item.coach_share_amount || 0)), 0),
    [coachPayableDetails]
  );

  const totalCoachPayableDetailNet = useMemo(
    () => totalCoachPayableDetailSalesShare + totalCoachPayableDetailAdjustment - totalCoachPayableDetailPayout,
    [totalCoachPayableDetailAdjustment, totalCoachPayableDetailPayout, totalCoachPayableDetailSalesShare]
  );

  const totalOpenClawUnread = useMemo(
    () => openClawConversations.reduce((total, item) => total + Number(item.unread_count || 0), 0),
    [openClawConversations]
  );

  const openClawSyncURL = useMemo(
    () => `${API_BASE_URL}public/openclaw/sync`,
    []
  );

  const sortedCoachPayouts = useMemo(
    () =>
      [...coachPayouts].sort((left, right) => {
        const leftDate = left?.payout_date ? new Date(left.payout_date) : null;
        const rightDate = right?.payout_date ? new Date(right.payout_date) : null;
        const leftTime = leftDate && !Number.isNaN(leftDate.getTime()) ? leftDate.getTime() : 0;
        const rightTime = rightDate && !Number.isNaN(rightDate.getTime()) ? rightDate.getTime() : 0;

        if (rightTime !== leftTime) {
          return rightTime - leftTime;
        }

        return Number(right?.id || 0) - Number(left?.id || 0);
      }),
    [coachPayouts]
  );

  const memberOptions = useMemo(
    () => members.map(buildMemberOption),
    [members]
  );

  const clientOptions = useMemo(
    () =>
      clients
        .filter(item => item.is_active)
        .map(item => ({
          value: String(item.id),
          label: item.member_name
            ? `${item.full_name} • ${item.member_name}`
            : item.full_name,
        })),
    [clients]
  );

  const saleProductOptions = useMemo(
    () =>
      products
        .filter(
          item =>
            item.is_active &&
            saleForm.pt_user_id &&
            String(item.pt_user_id) === String(saleForm.pt_user_id)
        )
        .map(item => ({
          value: String(item.id),
          label: `${item.product_name} • ${item.session_count} sesi`,
        })),
    [products, saleForm.pt_user_id]
  );

  const ptUserOptions = useMemo(
    () =>
      ptUsers
        .filter(item => item.is_active)
        .map(item => ({
          value: String(item.id),
          label: item.pt_name,
        })),
    [ptUsers]
  );

  const schedulePTOptions = useMemo(() => {
    const names = new Set(
      ptUsers
        .filter(item => item?.is_active)
        .map(item => String(item.pt_name || '').trim())
        .filter(Boolean)
    );

    if (scheduleForm.pt_name) {
      names.add(String(scheduleForm.pt_name).trim());
    }

    return [...names].sort((left, right) => left.localeCompare(right));
  }, [ptUsers, scheduleForm.pt_name]);

  const scheduleClientOptions = useMemo(() => {
    const names = new Set(
      clients
        .filter(item => item?.is_active)
        .map(item => String(item.full_name || '').trim())
        .filter(Boolean)
    );

    schedules.forEach(item => {
      const value = String(item.client_name || '').trim();
      if (value) {
        names.add(value);
      }
    });

    if (scheduleForm.client_name) {
      names.add(String(scheduleForm.client_name).trim());
    }

    return [...names].sort((left, right) => left.localeCompare(right));
  }, [clients, scheduleForm.client_name, schedules]);

  const filteredProgressClients = useMemo(() => {
    const keyword = String(clientProgressSearch || '').trim().toLowerCase();
    const baseRows = keyword
      ? clients.filter(item => (
        String(item.full_name || '').toLowerCase().includes(keyword) ||
        String(item.member_name || '').toLowerCase().includes(keyword) ||
        String(item.phone || '').toLowerCase().includes(keyword) ||
        String(item.email || '').toLowerCase().includes(keyword)
      ))
      : clients;

    return [...baseRows].sort((left, right) => {
      const leftRemaining = Number(left?.remaining_sessions || 0);
      const rightRemaining = Number(right?.remaining_sessions || 0);
      if (leftRemaining !== rightRemaining) {
        return leftRemaining - rightRemaining;
      }

      return String(left?.full_name || '').localeCompare(String(right?.full_name || ''), 'id');
    });
  }, [clientProgressSearch, clients]);

  const selectedSessionBalancePackage = useMemo(
    () =>
      sessionBalancePackages.find(
        item => buildSessionBalancePackageKey(item) === String(sessionBalanceForm.package_key || '')
      ) || null,
    [sessionBalanceForm.package_key, sessionBalancePackages]
  );

  const contractEditConflictingPackages = useMemo(() => {
    if (!clientProgressReport?.packages?.length || !contractEditForm.id) {
      return [];
    }

    return clientProgressReport.packages.filter(item => {
      const source = String(item.source || '').trim().toLowerCase();
      const status = String(item.status || '').trim().toLowerCase();
      const contractID = String(item.contract_id || item.id || '');
      return source !== 'legacy_package'
        && ['active', 'frozen'].includes(status)
        && contractID
        && contractID !== String(contractEditForm.id);
    });
  }, [clientProgressReport, contractEditForm.id]);

  const contractEditComputedTotalSessions = useMemo(() => {
    const usedSessions = Number(contractEditForm.used_sessions || 0);
    const remainingSessions = Number(contractEditForm.remaining_sessions || 0);
    if (!Number.isFinite(usedSessions) || !Number.isFinite(remainingSessions)) {
      return 0;
    }
    return Math.max(usedSessions, 0) + Math.max(remainingSessions, 0);
  }, [contractEditForm.remaining_sessions, contractEditForm.used_sessions]);

  const clientProgressShareText = useMemo(
    () => buildPTClientProgressFollowUpText(clientProgressReport, clientGoalWorkspace) || buildPTClientProgressShareText(clientProgressReport),
    [clientGoalWorkspace, clientProgressReport]
  );

  const hasSelectedOutletForOpenClaw = isScopedActor || selectedOutletNumber > 0;

  const migrationResultMetrics = useMemo(() => {
    if (!lastMigrationResult) {
      return [];
    }

    return [
      ['Completed Legacy Terdeteksi', lastMigrationResult.completed_schedules_total],
      ['Package Tersentuh', lastMigrationResult.packages_touched],
      ['Clients Created', lastMigrationResult.clients_created],
      ['Placeholder Staff', lastMigrationResult.placeholder_pt_users_created],
      ['Legacy Sales Created', lastMigrationResult.legacy_sales_created],
      ['Contracts Created', lastMigrationResult.contracts_created],
      ['Credit Ledger Added', lastMigrationResult.ledger_credits_created],
      ['Bookings Created', lastMigrationResult.bookings_created],
      ['Consume Ledger Added', lastMigrationResult.ledger_consumes_created],
      ['Contracts Reconciled', lastMigrationResult.contracts_reconciled],
      ['Orphan Schedules Skipped', lastMigrationResult.orphan_schedules_skipped],
    ].filter(([, value]) => typeof value !== 'undefined');
  }, [lastMigrationResult]);

  const parseApiResponse = useCallback(async response => {
    const raw = await response.text();
    if (!raw) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw);
      if (!response.ok) {
        throw new Error(parsed.message || `HTTP ${response.status}`);
      }
      return parsed;
    } catch (error) {
      if (!response.ok) {
        throw new Error(raw || `HTTP ${response.status}`);
      }
      return { message: raw };
    }
  }, []);

  const performRequest = useCallback(
    async (path, options = {}, fallbackMessage = 'Gagal memproses permintaan') => {
      const response = await fetch(`${API_BASE_URL}${path}`, options);
      const data = await parseApiResponse(response);
      if (!isApiSuccess(data)) {
        throw new Error(data.message || fallbackMessage);
      }
      return data;
    },
    [parseApiResponse]
  );

  const resetClientForm = () => {
    setClientForm(createClientForm());
    setSelectedClientMemberOption(null);
  };
  const resetPTUserForm = () => setPTUserForm(createPTUserForm());
  const resetSaleForm = () => setSaleForm(createSaleForm());
  const resetScheduleForm = () => setScheduleForm(createScheduleForm());

  const fetchOutlets = useCallback(async () => {
    if (!token || isScopedActor) {
      if (selectedOutlet) {
        setOutlets(prev =>
          prev.length > 0
            ? prev
            : [{ id: selectedOutlet, outlet_name: `Outlet #${selectedOutlet}` }]
        );
      }
      return;
    }

    const data = await performRequest(
      'outletlist',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ limit: 100, start: 0 }),
      },
      'Gagal mengambil daftar outlet'
    );

    const normalized = (Array.isArray(data.results) ? data.results : []).map(item => ({
      id: String(item.id ?? item.ID ?? ''),
      outlet_name:
        item.outlet_name || item.OutletName || item.name || `Outlet #${item.id || item.ID}`,
    }));

    setOutlets(normalized);
    if (!selectedOutlet && normalized[0]?.id) {
      setSelectedOutlet(normalized[0].id);
    }
  }, [authHeaders, isScopedActor, performRequest, selectedOutlet, token]);

  const fetchPaymentAccounts = useCallback(async () => {
    if (!shouldLoadPaymentAccounts) {
      setPaymentAccounts([]);
      return;
    }

    const data = await performRequest(
      'getassetaccounts',
      {
        method: 'POST',
        headers: authHeaders,
      },
      'Gagal mengambil metode pembayaran'
    );

    setPaymentAccounts(
      (Array.isArray(data.results) ? data.results : []).map(item => ({
        id: String(item.account_id ?? item.AccountID ?? item.id ?? ''),
        name: item.account_name || item.AccountName || item.accountname || '',
      }))
    );
  }, [authHeaders, performRequest, shouldLoadPaymentAccounts]);

  const fetchPTUsers = useCallback(async () => {
    if (!shouldLoadPTUsers) {
      setPTUsers([]);
      return;
    }

    const data = await performRequest(
      'pt-user/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ limit: 200, offset: 0, search: '' }),
      },
      'Gagal mengambil daftar staff layanan'
    );
    setPTUsers(Array.isArray(data.results) ? data.results : []);
  }, [authHeaders, performRequest, shouldLoadPTUsers]);

  const fetchMembers = useCallback(
    async (search = '') => {
      const normalizedSearch = String(search || '').trim();
      if (!canUseMemberLookup || !selectedOutletNumber) {
        if (!normalizedSearch) {
          setMembers([]);
        }
        return [];
      }
      const data = await performRequest(
        'member/list',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: normalizedSearch ? 50 : 80,
            offset: 0,
            outlet: selectedOutletNumber,
            search: normalizedSearch,
          }),
        },
        'Gagal mengambil daftar member'
      );
      const nextMembers = Array.isArray(data.results) ? data.results : [];
      if (!normalizedSearch) {
        setMembers(nextMembers);
      }
      return nextMembers;
    },
    [authHeaders, canUseMemberLookup, performRequest, selectedOutletNumber]
  );

  const loadMemberOptions = useCallback(
    async inputValue => {
      try {
        const results = await fetchMembers(inputValue);
        return results.map(buildMemberOption);
      } catch (error) {
        console.error('Gagal mencari session member:', error);
        return [];
      }
    },
    [fetchMembers]
  );

  const syncClientFormWithMember = useCallback(option => {
    setSelectedClientMemberOption(option || null);
    setClientForm(prev => {
      if (!option?.data) {
        return {
          ...prev,
          member_id: '',
          full_name: '',
          phone: '',
          email: '',
        };
      }

      return {
        ...prev,
        member_id: option.value,
        full_name: formatMemberName(option.data),
        phone: option.data.hp || '',
        email: option.data.email || '',
      };
    });
  }, []);

  const fetchClients = useCallback(async (options = {}) => {
    if (!shouldLoadClients) {
      setClients([]);
      return;
    }

    const effectiveSearch = typeof options.search === 'string' ? options.search : clientSearch;
    const data = await performRequest(
      'pt-client/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 200,
          offset: 0,
          outlet_id: selectedOutletNumber,
          search: effectiveSearch,
        }),
      },
      'Gagal mengambil daftar session client'
    );
    setClients(Array.isArray(data.results) ? data.results : []);
  }, [authHeaders, clientSearch, performRequest, selectedOutletNumber, shouldLoadClients]);

  const fetchClientByID = useCallback(async id => {
    if (!id) {
      return null;
    }

    const data = await performRequest(
      'pt-client/get',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ id: Number(id) }),
      },
      'Gagal mengambil detail session client'
    );
    return data.data || null;
  }, [authHeaders, performRequest]);

  const fetchClientProgressReport = useCallback(async clientID => {
    if (!clientID) {
      setClientProgressReport(null);
      setClientGoalWorkspace(null);
      return null;
    }

    setIsClientProgressLoading(true);
    try {
      const [reportData, workspaceData] = await Promise.all([
        performRequest(
          'pt-client/progress-report',
          {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              client_id: Number(clientID),
              outlet_id: selectedOutletNumber,
              attendance_limit: 120,
            }),
          },
          'Gagal mengambil report progress client'
        ),
        performRequest(
          'pt-client/goal-workspace',
          {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              client_id: Number(clientID),
              outlet_id: selectedOutletNumber,
            }),
          },
          'Gagal mengambil workspace goal client'
        ),
      ]);
      const nextReport = reportData.data || null;
      const nextWorkspace = workspaceData.data || null;
      setClientProgressReport(nextReport);
      setClientGoalWorkspace(nextWorkspace);
      return nextReport;
    } finally {
      setIsClientProgressLoading(false);
    }
  }, [authHeaders, performRequest, selectedOutletNumber]);

  const applySessionBalancePackageSelection = useCallback(targetPackage => {
    const packageKey = buildSessionBalancePackageKey(targetPackage);
    setSessionBalanceForm(prev => ({
      ...prev,
      package_key: packageKey,
      contract_id: targetPackage?.source === 'legacy' ? '' : String(targetPackage?.contract_id || targetPackage?.id || ''),
      legacy_package_id: targetPackage?.source === 'legacy'
        ? String(targetPackage?.legacy_package_id || targetPackage?.id || '')
        : '',
      total_sessions: typeof targetPackage?.total_sessions !== 'undefined'
        ? String(Number(targetPackage.total_sessions || 0))
        : '',
    }));
  }, []);

  const handleCloseSessionBalanceModal = useCallback(() => {
    setShowSessionBalanceModal(false);
    setSessionBalanceClient(null);
    setSessionBalancePackages([]);
    setSessionBalanceForm(createSessionBalanceForm());
  }, []);

  const handleOpenSessionBalanceModal = useCallback(async item => {
    if (!item?.id) {
      return;
    }

    setSessionBalanceClient(item);
    setSessionBalancePackages([]);
    setSessionBalanceForm(createSessionBalanceForm(item.id));
    setShowSessionBalanceModal(true);
    setIsSessionBalanceLoading(true);

    try {
      const data = await performRequest(
        'pt-client/progress-report',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            client_id: Number(item.id),
            outlet_id: selectedOutletNumber,
            attendance_limit: 120,
          }),
        },
        'Gagal mengambil detail paket sesi client'
      );
      const report = data?.data || null;
      const packages = sortSessionBalancePackages(report?.packages);
      setSessionBalancePackages(packages);
      if (packages.length > 0) {
        applySessionBalancePackageSelection(packages[0]);
      }
    } catch (error) {
      setShowSessionBalanceModal(false);
      setSessionBalanceClient(null);
      Swal.fire('Error', error.message || 'Gagal mengambil detail paket sesi client', 'error');
    } finally {
      setIsSessionBalanceLoading(false);
    }
  }, [applySessionBalancePackageSelection, authHeaders, performRequest, selectedOutletNumber]);

  const handleSubmitSessionBalanceAdjustment = useCallback(async event => {
    event.preventDefault();
    if (!sessionBalanceClient?.id) {
      Swal.fire('Warning', 'Pilih session client terlebih dahulu.', 'warning');
      return;
    }
    if (!selectedSessionBalancePackage) {
      Swal.fire('Warning', 'Pilih paket sesi yang ingin dikoreksi.', 'warning');
      return;
    }

    const totalSessions = Number(sessionBalanceForm.total_sessions || 0);
    if (!Number.isFinite(totalSessions) || totalSessions < 0 || !Number.isInteger(totalSessions)) {
      Swal.fire('Warning', 'Total sesi baru harus berupa angka bulat 0 atau lebih.', 'warning');
      return;
    }

    setIsSessionBalanceSaving(true);
    try {
      const data = await performRequest(
        'pt-client/session-balance-adjust',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            client_id: Number(sessionBalanceClient.id),
            contract_id: Number(sessionBalanceForm.contract_id || 0),
            legacy_package_id: Number(sessionBalanceForm.legacy_package_id || 0),
            total_sessions: totalSessions,
            notes: String(sessionBalanceForm.notes || '').trim(),
          }),
        },
        'Gagal memperbarui balance sesi'
      );

      await fetchClients({ search: clientSearch });
      if (selectedProgressClientId && String(selectedProgressClientId) === String(sessionBalanceClient.id)) {
        await fetchClientProgressReport(sessionBalanceClient.id);
      }

      Swal.fire('Berhasil', data.message || 'Balance sesi berhasil diperbarui', 'success');
      handleCloseSessionBalanceModal();
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memperbarui balance sesi', 'error');
    } finally {
      setIsSessionBalanceSaving(false);
    }
  }, [
    authHeaders,
    clientSearch,
    fetchClientProgressReport,
    fetchClients,
    handleCloseSessionBalanceModal,
    performRequest,
    selectedProgressClientId,
    selectedSessionBalancePackage,
    sessionBalanceClient,
    sessionBalanceForm,
  ]);

  const fetchProducts = useCallback(async () => {
    if (!shouldLoadProducts) {
      setProducts([]);
      return;
    }

    const data = await performRequest(
      'pt-product/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 200,
          offset: 0,
          outlet_id: selectedOutletNumber,
        }),
      },
      'Gagal mengambil daftar layanan session'
    );
    setProducts(Array.isArray(data.results) ? data.results : []);
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadProducts]);

  const fetchSales = useCallback(async () => {
    if (!shouldLoadSales) {
      setSales([]);
      return;
    }

    const data = await performRequest(
      'pt-sale/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 200,
          offset: 0,
          outlet_id: selectedOutletNumber,
          search: saleSearch,
          payment_status: saleStatus,
        }),
      },
      'Gagal mengambil daftar session sale'
    );
    setSales(Array.isArray(data.results) ? data.results : []);
  }, [authHeaders, performRequest, saleSearch, saleStatus, selectedOutletNumber, shouldLoadSales]);

  const handleCloseContractEditModal = useCallback(() => {
    setShowContractEditModal(false);
    setContractEditForm(createContractEditForm());
  }, []);

  const handleOpenContractEditModal = useCallback(item => {
    if (!item || String(item.source || '').trim().toLowerCase() === 'legacy_package') {
      return;
    }

    setContractEditForm({
      id: String(item.contract_id || item.id || ''),
      sale_id: String(item.sale_id || ''),
      contract_no: item.contract_no || '',
      product_name: item.product_name || '',
      pt_name: item.pt_name || '',
      status: String(item.status || 'active').trim().toLowerCase() || 'active',
      start_at: formatDateTimeLocalInput(item.start_at),
      expire_at: formatDateTimeLocalInput(item.expire_at),
      total_sessions: Number(item.total_sessions || 0),
      used_sessions: Number(item.used_sessions || 0),
      remaining_sessions: Number(item.remaining_sessions || 0),
      notes: item.notes || '',
    });
    setShowContractEditModal(true);
  }, []);

  const handleSubmitContractEdit = useCallback(async event => {
    event.preventDefault();

    const contractID = Number(contractEditForm.id || 0);
    const nextStatus = String(contractEditForm.status || '').trim().toLowerCase();
    const startAt = String(contractEditForm.start_at || '').trim();
    const expireAt = String(contractEditForm.expire_at || '').trim();
    const usedSessions = Number(contractEditForm.used_sessions || 0);
    const remainingSessions = Number(contractEditForm.remaining_sessions || 0);
    const totalSessions = usedSessions + remainingSessions;

    if (contractID <= 0) {
      Swal.fire('Warning', 'Contract PT yang dipilih tidak valid.', 'warning');
      return;
    }
    if (!nextStatus) {
      Swal.fire('Warning', 'Status contract wajib dipilih.', 'warning');
      return;
    }
    if (!startAt) {
      Swal.fire('Warning', 'Tanggal mulai contract wajib diisi.', 'warning');
      return;
    }
    if (!Number.isFinite(remainingSessions) || !Number.isInteger(remainingSessions) || remainingSessions < 0) {
      Swal.fire('Warning', 'Sisa sesi tersedia harus berupa angka bulat 0 atau lebih.', 'warning');
      return;
    }
    if (!Number.isFinite(totalSessions) || !Number.isInteger(totalSessions) || totalSessions < usedSessions) {
      Swal.fire('Warning', 'Total sesi contract tidak valid.', 'warning');
      return;
    }

    const startTime = new Date(startAt).getTime();
    const expireTime = expireAt ? new Date(expireAt).getTime() : 0;
    if (expireAt && !Number.isNaN(startTime) && !Number.isNaN(expireTime) && expireTime < startTime) {
      Swal.fire('Warning', 'Tanggal expire tidak boleh lebih awal dari tanggal mulai.', 'warning');
      return;
    }

    if (['active', 'frozen'].includes(nextStatus) && remainingSessions <= 0) {
      Swal.fire('Warning', 'Contract tanpa sisa sesi tidak bisa dibuat active atau frozen.', 'warning');
      return;
    }

    if (nextStatus === 'active' && contractEditConflictingPackages.length > 0) {
      const confirmation = await Swal.fire({
        title: 'Masih ada contract aktif lain',
        text: `Masih ada ${contractEditConflictingPackages.length} contract active/frozen lain untuk client ini. Lanjut aktifkan contract ini?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Lanjutkan',
        cancelButtonText: 'Batal',
      });
      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setIsContractEditSaving(true);
    try {
      const data = await performRequest(
        'pt-contract/update',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: contractID,
            status: nextStatus,
            start_at: startAt,
            expire_at: expireAt,
            total_sessions: totalSessions,
            notes: String(contractEditForm.notes || '').trim(),
          }),
        },
        'Gagal memperbarui contract PT'
      );

      await fetchClients({ search: clientSearch });
      if (shouldLoadSales) {
        await fetchSales();
      }

      if (selectedProgressClientId) {
        await fetchClientProgressReport(selectedProgressClientId);
      }

      Swal.fire('Berhasil', data.message || 'Contract PT berhasil diperbarui', 'success');
      handleCloseContractEditModal();
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memperbarui contract PT', 'error');
    } finally {
      setIsContractEditSaving(false);
    }
  }, [
    authHeaders,
    clientSearch,
    contractEditConflictingPackages.length,
    contractEditForm,
    fetchClientProgressReport,
    fetchClients,
    fetchSales,
    handleCloseContractEditModal,
    performRequest,
    selectedProgressClientId,
    shouldLoadSales,
  ]);

  const fetchSalePayments = useCallback(async saleID => {
    if (!saleID) {
      setSalePayments([]);
      return [];
    }

    setIsSalePaymentLoading(true);
    try {
      const data = await performRequest(
        'pt-sale-payment/list',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 200,
            offset: 0,
            sale_id: Number(saleID),
            outlet_id: selectedOutletNumber,
          }),
        },
        'Gagal mengambil riwayat payment session sale'
      );
      const results = Array.isArray(data.results) ? data.results : [];
      setSalePayments(results);
      return results;
    } finally {
      setIsSalePaymentLoading(false);
    }
  }, [authHeaders, performRequest, selectedOutletNumber]);

  const fetchSchedules = useCallback(async () => {
    if (!shouldLoadSchedules) {
      setSchedules([]);
      return;
    }

    const data = await performRequest(
      'pt-schedule/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 300,
          offset: 0,
          date: scheduleFilterDate,
        }),
      },
      'Gagal mengambil daftar jadwal session'
    );
    setSchedules(Array.isArray(data.results) ? data.results : []);
  }, [authHeaders, performRequest, scheduleFilterDate, shouldLoadSchedules]);

  const fetchCoachPayables = useCallback(async () => {
    if (!shouldLoadCoachPayables || !selectedOutletNumber) {
      setCoachPayables([]);
      return [];
    }

    const data = await performRequest(
      'fitness-coach-payable/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 200,
          offset: 0,
          outlet_id: selectedOutletNumber,
        }),
      },
      'Gagal mengambil daftar hutang coach'
    );

    const results = Array.isArray(data.results)
      ? data.results.map((item, index) => normalizeCoachPayableItem(item, index))
      : [];
    setCoachPayables(results);
    return results;
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadCoachPayables]);

  const fetchCoachPayableDetails = useCallback(async coachName => {
    const normalizedCoachName = String(coachName || '').trim();
    if (!shouldLoadCoachPayables || !selectedOutletNumber || !normalizedCoachName) {
      setCoachPayableDetails([]);
      return [];
    }

    setIsCoachPayableDetailLoading(true);
    try {
      const data = await performRequest(
        'fitness-coach-payable/detail',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            limit: 300,
            offset: 0,
            outlet_id: selectedOutletNumber,
            coach_name: normalizedCoachName,
          }),
        },
        'Gagal mengambil detail transaksi coach'
      );

      const results = Array.isArray(data.results)
        ? data.results.map(item => ({
          ...item,
          coach_name: resolveCoachPayableName(item) || normalizedCoachName,
        }))
        : [];
      setCoachPayableDetails(results);
      return results;
    } finally {
      setIsCoachPayableDetailLoading(false);
    }
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadCoachPayables]);

  const fetchCoachPayouts = useCallback(async () => {
    if (!shouldLoadCoachPayables || !selectedOutletNumber) {
      setCoachPayouts([]);
      return [];
    }

    const data = await performRequest(
      'fitness-coach-payout/list',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 200,
          offset: 0,
          outlet_id: selectedOutletNumber,
        }),
      },
      'Gagal mengambil riwayat coach payout'
    );

    const results = Array.isArray(data.results) ? data.results : [];
    setCoachPayouts(results);
    return results;
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadCoachPayables]);

  const fetchCoachShiftStaffOptions = useCallback(async () => {
    if (!shouldLoadCoachPayables || !selectedOutletNumber) {
      setCoachShiftStaffOptions([]);
      return [];
    }

    const data = await performRequest(
      'fitness-coach-payout/staff-options',
      {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          limit: 200,
          outlet_id: selectedOutletNumber,
        }),
      },
      'Gagal mengambil daftar staff shift'
    );

    const results = Array.isArray(data.results) ? data.results : [];
    setCoachShiftStaffOptions(results);
    return results;
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadCoachPayables]);

  const fetchOpenClawSettings = useCallback(async () => {
    if (!shouldLoadOpenClaw || !selectedOutletNumber) {
      setOpenClawSettingsForm(createOpenClawSettingsForm());
      return null;
    }

    setIsOpenClawSettingsLoading(true);
    try {
      const data = await performRequest(
        'openclaw/settings/get',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ outlet_id: selectedOutletNumber }),
        },
        'Gagal mengambil pengaturan OpenClaw'
      );

      const nextSettings = {
        ...createOpenClawSettingsForm(),
        ...(data.data || {}),
      };
      setOpenClawSettingsForm({
        is_enabled: Boolean(nextSettings.is_enabled),
        auto_send_pt_completed: typeof nextSettings.auto_send_pt_completed === 'undefined'
          ? true
          : Boolean(nextSettings.auto_send_pt_completed),
        openclaw_bin: nextSettings.openclaw_bin || 'openclaw',
        channel: nextSettings.channel || 'whatsapp',
        account_id: nextSettings.account_id || '',
        control_url: nextSettings.control_url || '',
        webhook_secret: nextSettings.webhook_secret || '',
      });
      return nextSettings;
    } finally {
      setIsOpenClawSettingsLoading(false);
    }
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadOpenClaw]);

  const fetchOpenClawKnowledge = useCallback(async (searchOverride = null) => {
    if (!shouldLoadOpenClawKnowledge || !selectedOutletNumber) {
      setOpenClawKnowledgeItems([]);
      return [];
    }

    setIsOpenClawKnowledgeLoading(true);
    try {
      const effectiveSearch = typeof searchOverride === 'string'
        ? searchOverride
        : openClawKnowledgeSearch;
      const data = await performRequest(
        'openclaw/knowledge/list',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            search: effectiveSearch,
            limit: 300,
            include_inactive: true,
          }),
        },
        'Gagal mengambil knowledge base WhatsApp'
      );

      const results = Array.isArray(data.results) ? data.results : [];
      setOpenClawKnowledgeItems(results);
      return results;
    } finally {
      setIsOpenClawKnowledgeLoading(false);
    }
  }, [authHeaders, openClawKnowledgeSearch, performRequest, selectedOutletNumber, shouldLoadOpenClawKnowledge]);

  const fetchOpenClawConversations = useCallback(async (searchOverride = null) => {
    if (!shouldLoadOpenClaw || !selectedOutletNumber) {
      setOpenClawConversations([]);
      return [];
    }

    setIsOpenClawInboxLoading(true);
    try {
      const effectiveSearch = typeof searchOverride === 'string'
        ? searchOverride
        : openClawConversationSearch;
      const data = await performRequest(
        'openclaw/inbox/conversations',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            search: effectiveSearch,
            limit: 120,
          }),
        },
        'Gagal mengambil inbox WhatsApp'
      );

      const results = Array.isArray(data.results) ? data.results : [];
      setOpenClawConversations(results);
      return results;
    } finally {
      setIsOpenClawInboxLoading(false);
    }
  }, [authHeaders, openClawConversationSearch, performRequest, selectedOutletNumber, shouldLoadOpenClaw]);

  const fetchOpenClawMessages = useCallback(async (conversationKey, options = {}) => {
    const normalizedKey = String(conversationKey || '').trim();
    if (!shouldLoadOpenClaw || !selectedOutletNumber || !normalizedKey) {
      setOpenClawMessages([]);
      return [];
    }

    setIsOpenClawMessagesLoading(true);
    try {
      const data = await performRequest(
        'openclaw/inbox/messages',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            conversation_key: normalizedKey,
            limit: 300,
            offset: 0,
            mark_read: typeof options.markRead === 'undefined' ? true : Boolean(options.markRead),
          }),
        },
        'Gagal mengambil pesan WhatsApp'
      );

      const results = Array.isArray(data.results) ? data.results : [];
      setOpenClawMessages(results);
      if (typeof options.markRead === 'undefined' || options.markRead) {
        setOpenClawConversations(previous =>
          previous.map(item => (
            item.conversation_key === normalizedKey
              ? { ...item, unread_count: 0 }
              : item
          ))
        );
      }
      return results;
    } finally {
      setIsOpenClawMessagesLoading(false);
    }
  }, [authHeaders, performRequest, selectedOutletNumber, shouldLoadOpenClaw]);

  const fetchMigrationSummary = useCallback(async (options = {}) => {
    if (!shouldLoadMigration) {
      setMigrationSummary(null);
      return null;
    }

    const shouldThrow = options.throwOnError !== false;
    setIsMigrationSummaryLoading(true);
    try {
      const data = await performRequest(
        'pt-migration/summary',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({}),
        },
        'Gagal mengambil ringkasan migrasi session'
      );
      setMigrationSummary(data.data || null);
      return data.data || null;
    } catch (error) {
      if (shouldThrow) {
        throw error;
      }
      return null;
    } finally {
      setIsMigrationSummaryLoading(false);
    }
  }, [authHeaders, performRequest, shouldLoadMigration]);

  const bootstrapPage = useCallback(async () => {
    if (!token) {
      window.location.href = '/loginadmin';
      return;
    }

    setIsBootstrapping(true);
    try {
      await fetchOutlets();
      await Promise.all([
        shouldLoadPaymentAccounts ? fetchPaymentAccounts() : Promise.resolve(),
        shouldLoadPTUsers ? fetchPTUsers() : Promise.resolve(),
        shouldLoadMembers ? fetchMembers() : Promise.resolve(),
        shouldLoadClients ? fetchClients() : Promise.resolve(),
        shouldLoadProducts ? fetchProducts() : Promise.resolve(),
        shouldLoadSales ? fetchSales() : Promise.resolve(),
        shouldLoadSchedules ? fetchSchedules() : Promise.resolve(),
        shouldLoadCoachPayables ? fetchCoachPayables() : Promise.resolve(),
        shouldLoadCoachPayables ? fetchCoachPayouts() : Promise.resolve(),
        shouldLoadCoachPayables ? fetchCoachShiftStaffOptions() : Promise.resolve(),
      ]);
      if (shouldLoadMigration) {
        fetchMigrationSummary({ throwOnError: false }).catch(() => null);
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memuat halaman private session', 'error');
    } finally {
      setIsBootstrapping(false);
    }
  }, [
    fetchClients,
    fetchMembers,
    fetchMigrationSummary,
    fetchOutlets,
    fetchPTUsers,
    fetchPaymentAccounts,
    fetchProducts,
    fetchSchedules,
    fetchSales,
    fetchCoachPayables,
    fetchCoachPayouts,
    fetchCoachShiftStaffOptions,
    shouldLoadClients,
    shouldLoadCoachPayables,
    shouldLoadMembers,
    shouldLoadMigration,
    shouldLoadPTUsers,
    shouldLoadPaymentAccounts,
    shouldLoadProducts,
    shouldLoadSales,
    shouldLoadSchedules,
    token,
  ]);

  useEffect(() => {
    bootstrapPage();
  }, [bootstrapPage]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (!shouldLoadClients) {
      return;
    }
    fetchClients().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar session client', 'error');
    });
  }, [fetchClients, shouldLoadClients, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (!shouldLoadProducts) {
      return;
    }
    fetchProducts().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar layanan session', 'error');
    });
  }, [fetchProducts, shouldLoadProducts, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (!shouldLoadSales) {
      return;
    }
    fetchSales().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar session sale', 'error');
    });
  }, [fetchSales, shouldLoadSales, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (!shouldLoadSchedules) {
      return;
    }
    fetchSchedules().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar jadwal session', 'error');
    });
  }, [fetchSchedules, shouldLoadSchedules, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (!shouldLoadMembers) {
      return;
    }
    fetchMembers().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar member', 'error');
    });
  }, [fetchMembers, shouldLoadMembers, token]);

  useEffect(() => {
    if (!token || !shouldLoadCoachPayables) {
      return;
    }
    fetchCoachPayables().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar hutang coach', 'error');
    });
  }, [fetchCoachPayables, shouldLoadCoachPayables, token]);

  useEffect(() => {
    if (!token || !shouldLoadCoachPayables) {
      return;
    }
    fetchCoachPayouts().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil riwayat coach payout', 'error');
    });
  }, [fetchCoachPayouts, shouldLoadCoachPayables, token]);

  useEffect(() => {
    if (!token || !shouldLoadCoachPayables) {
      return;
    }
    fetchCoachShiftStaffOptions().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil daftar staff shift', 'error');
    });
  }, [fetchCoachShiftStaffOptions, shouldLoadCoachPayables, token]);

  useEffect(() => {
    if (!coachPayoutForm.shift_cashier_id) {
      return;
    }

    const stillExists = coachShiftStaffOptions.some(
      item => String(item.staff_id) === String(coachPayoutForm.shift_cashier_id)
    );
    if (stillExists) {
      return;
    }

    setCoachPayoutForm(previous => ({
      ...previous,
      shift_cashier_id: '',
      shift_cashier_name: '',
    }));
  }, [coachPayoutForm.shift_cashier_id, coachShiftStaffOptions]);

  useEffect(() => {
    if (!token || !shouldLoadOpenClaw) {
      return;
    }
    if (!selectedOutletNumber) {
      setOpenClawSettingsForm(createOpenClawSettingsForm());
      setOpenClawConversations([]);
      setOpenClawMessages([]);
      setSelectedOpenClawConversationKey('');
      return;
    }

    fetchOpenClawSettings().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil pengaturan OpenClaw', 'error');
    });
  }, [fetchOpenClawSettings, selectedOutletNumber, shouldLoadOpenClaw, token]);

  useEffect(() => {
    if (!token || !shouldLoadOpenClaw) {
      return;
    }
    if (!selectedOutletNumber) {
      setOpenClawConversations([]);
      return;
    }

    fetchOpenClawConversations().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil inbox WhatsApp', 'error');
    });
  }, [fetchOpenClawConversations, selectedOutletNumber, shouldLoadOpenClaw, token]);

  useEffect(() => {
    if (!token || !shouldLoadOpenClawKnowledge) {
      return;
    }
    if (!selectedOutletNumber) {
      setOpenClawKnowledgeItems([]);
      return;
    }

    fetchOpenClawKnowledge().catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil knowledge base WhatsApp', 'error');
    });
  }, [fetchOpenClawKnowledge, selectedOutletNumber, shouldLoadOpenClawKnowledge, token]);

  useEffect(() => {
    setSelectedCoachPayableNames(previous => {
      if (!previous.length) {
        return previous;
      }

      const availableNames = new Map(
        coachPayables.map(item => [normalizeCoachPayableKey(item.coach_name), String(item.coach_name || '').trim()])
      );
      const nextSelection = previous
        .map(item => availableNames.get(normalizeCoachPayableKey(item)))
        .filter(Boolean);

      const hasChanged =
        nextSelection.length !== previous.length ||
        nextSelection.some((item, index) => item !== previous[index]);

      return hasChanged ? nextSelection : previous;
    });
  }, [coachPayables]);

  useEffect(() => {
    if (!shouldLoadOpenClaw) {
      return;
    }
    if (!openClawConversations.length) {
      setSelectedOpenClawConversationKey('');
      setOpenClawMessages([]);
      return;
    }

    const stillExists = openClawConversations.some(
      item => item.conversation_key === selectedOpenClawConversationKey
    );
    if (!stillExists) {
      setSelectedOpenClawConversationKey(openClawConversations[0].conversation_key || '');
    }
  }, [openClawConversations, selectedOpenClawConversationKey, shouldLoadOpenClaw]);

  useEffect(() => {
    if (!sales.length) {
      setSelectedSaleId(null);
      setSalePayments([]);
      return;
    }

    const stillExists = sales.some(item => String(item.id) === String(selectedSaleId));
    if (!stillExists) {
      setSelectedSaleId(sales[0].id);
    }
  }, [sales, selectedSaleId]);

  useEffect(() => {
    if (!selectedSale) {
      setSalePaymentForm(createSalePaymentForm());
      setSalePayments([]);
      return;
    }

    setSalePaymentForm(createSalePaymentForm(selectedSale));
    fetchSalePayments(selectedSale.id).catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil riwayat payment session sale', 'error');
    });
  }, [fetchSalePayments, selectedSale]);

  useEffect(() => {
    setSelectedClientMemberOption(null);
    setClientForm(prev => (
      prev.client_type === 'member'
        ? {
            ...prev,
            member_id: '',
            full_name: '',
            phone: '',
            email: '',
          }
        : prev
    ));
    setSelectedSaleId(null);
    setSelectedProgressClientId(null);
    setClientProgressReport(null);
    setClientGoalWorkspace(null);
    setClientProgressDetailTab('overview');
    setClientGoalTrendMetric('body_fat_percent');
    setClientProgressSearch('');
    setSalePayments([]);
    setSalePaymentForm(createSalePaymentForm());
    setCoachPayables([]);
    setCoachPayableDetails([]);
    setCoachPayouts([]);
    setCoachPayoutForm(createCoachPayoutForm());
    setCoachAdjustmentForm(createCoachAdjustmentForm());
    setSelectedCoachPayableDetail(null);
    setShowCoachPayableDetailModal(false);
    setShowCoachAdjustmentModal(false);
    setSessionBalanceClient(null);
    setSessionBalancePackages([]);
    setSessionBalanceForm(createSessionBalanceForm());
    setShowSessionBalanceModal(false);
    setIsContractEditSaving(false);
    setContractEditForm(createContractEditForm());
    setShowContractEditModal(false);
    setOpenClawSettingsForm(createOpenClawSettingsForm());
    setOpenClawKnowledgeItems([]);
    setOpenClawKnowledgeForm(createOpenClawKnowledgeForm());
    setOpenClawKnowledgeSearch('');
    setOpenClawConversations([]);
    setOpenClawMessages([]);
    setOpenClawConversationSearch('');
    setSelectedOpenClawConversationKey('');
    setOpenClawReplyBody('');
    setShowOpenClawKnowledgeModal(false);
    setPTUserForm(createPTUserForm());
  }, [selectedOutlet]);

  useEffect(() => {
    const selectedCoachName = resolveCoachPayableName(selectedCoachPayableDetail);
    if (!selectedCoachName) {
      return;
    }

    const refreshedCoachPayable = coachPayables.find(
      item => normalizeCoachPayableKey(resolveCoachPayableName(item)) === normalizeCoachPayableKey(selectedCoachName)
    );

    if (refreshedCoachPayable) {
      setSelectedCoachPayableDetail(refreshedCoachPayable);
      return;
    }

    setSelectedCoachPayableDetail(null);
    setCoachPayableDetails([]);
    setShowCoachPayableDetailModal(false);
  }, [coachPayables, selectedCoachPayableDetail]);

  useEffect(() => {
    if (!token || !shouldLoadOpenClaw || !selectedOutletNumber || !selectedOpenClawConversationKey) {
      return;
    }

    fetchOpenClawMessages(selectedOpenClawConversationKey).catch(error => {
      Swal.fire('Error', error.message || 'Gagal mengambil percakapan WhatsApp', 'error');
    });
  }, [fetchOpenClawMessages, selectedOpenClawConversationKey, selectedOutletNumber, shouldLoadOpenClaw, token]);

  useEffect(() => {
    if (!availableTabs.some(item => item.key === activeTab)) {
      setActiveTab(availableTabs[0]?.key || 'clients');
    }
  }, [activeTab, availableTabs]);

  useEffect(() => {
    if (!coachPayoutForm.coach_name) {
      return;
    }

    const matchedCoach = coachPayables.find(
      item => String(item.coach_name) === String(coachPayoutForm.coach_name)
    );
    if (matchedCoach) {
      return;
    }

    setCoachPayoutForm(prev => ({
      ...prev,
      coach_name: '',
      amount: '',
    }));
  }, [coachPayables, coachPayoutForm.coach_name]);

  useEffect(() => {
    if (!selectedProgressClientId) {
      return;
    }

    const stillExists = clients.some(item => String(item.id) === String(selectedProgressClientId));
    if (!stillExists) {
      setSelectedProgressClientId(null);
      setClientProgressReport(null);
      setClientGoalWorkspace(null);
    }
  }, [clients, selectedProgressClientId]);

  useEffect(() => {
    if (!selectedProgressClientId) {
      return;
    }

    const stillVisible = filteredProgressClients.some(
      item => String(item.id) === String(selectedProgressClientId)
    );
    if (!stillVisible) {
      setSelectedProgressClientId(null);
      setClientProgressReport(null);
      setClientGoalWorkspace(null);
    }
  }, [filteredProgressClients, selectedProgressClientId]);

  useEffect(() => {
    if (!shouldLoadSchedules || scheduleForm.id) {
      return;
    }

    setScheduleForm(prev => {
      const next = { ...prev };
      let changed = false;

      if (!next.pt_name && schedulePTOptions.length > 0) {
        next.pt_name = schedulePTOptions[0];
        changed = true;
      }

      if (!next.client_name && scheduleClientOptions.length > 0) {
        next.client_name = scheduleClientOptions[0];
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [scheduleClientOptions, scheduleForm.id, schedulePTOptions, shouldLoadSchedules]);

  useEffect(() => {
    if (!saleForm.product_id) {
      return;
    }

    const stillValid = saleProductOptions.some(
      item => String(item.value) === String(saleForm.product_id)
    );
    if (!stillValid) {
      setSaleForm(prev => ({ ...prev, product_id: '' }));
    }
  }, [saleForm.product_id, saleProductOptions]);

  const handleSaveClient = async event => {
    event.preventDefault();
    const effectiveClientType = canUseMemberLookup
      ? clientForm.client_type
      : (clientForm.member_id ? 'member' : clientForm.client_type || 'non_member');
    if (effectiveClientType === 'member' && !clientForm.member_id) {
      Swal.fire('Warning', 'Pilih member terlebih dahulu.', 'warning');
      return;
    }
    if (effectiveClientType === 'non_member' && !String(clientForm.full_name || '').trim()) {
      Swal.fire('Warning', 'Nama client wajib diisi untuk non member.', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      const data = await performRequest(
        clientForm.id ? 'pt-client/update' : 'pt-client/add',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: clientForm.id,
            outlet_id: selectedOutletNumber,
            member_id:
              effectiveClientType === 'member' && clientForm.member_id
                ? Number(clientForm.member_id)
                : undefined,
            full_name: clientForm.full_name,
            client_type: effectiveClientType,
            phone: clientForm.phone,
            email: clientForm.email,
            notes: clientForm.notes,
            is_active: clientForm.is_active,
          }),
        },
        'Gagal menyimpan session client'
      );

      const savedClient = data?.id ? await fetchClientByID(data.id).catch(() => null) : null;
      resetClientForm();
      setClientSearch('');
      if (savedClient) {
        setClients(prev => {
          const next = prev.filter(item => String(item.id) !== String(savedClient.id));
          return [savedClient, ...next];
        });
      }
      await fetchClients({ search: '' });
      Swal.fire('Berhasil', data.message || 'Session client berhasil disimpan', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan session client', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSavePTUser = async event => {
    event.preventDefault();
    if (!String(ptUserForm.pt_name || '').trim()) {
      Swal.fire('Warning', 'Nama PT wajib diisi.', 'warning');
      return;
    }
    if (!String(ptUserForm.username || '').trim()) {
      Swal.fire('Warning', 'Username login PT wajib diisi.', 'warning');
      return;
    }
    if (!ptUserForm.id && !String(ptUserForm.password || '').trim()) {
      Swal.fire('Warning', 'Password wajib diisi saat menambahkan akun PT baru.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await performRequest(
        ptUserForm.id ? 'pt-user/update' : 'pt-user/add',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: ptUserForm.id,
            pt_name: String(ptUserForm.pt_name || '').trim(),
            username: String(ptUserForm.username || '').trim(),
            password: String(ptUserForm.password || ''),
            is_active: Boolean(ptUserForm.is_active),
            package_share_mode: String(ptUserForm.package_share_mode || '').trim(),
            package_share_value: Number(ptUserForm.package_share_value || 0),
            session_share_mode: String(ptUserForm.session_share_mode || '').trim(),
            session_share_value: Number(ptUserForm.session_share_value || 0),
          }),
        },
        'Gagal menyimpan akun PT'
      );

      resetPTUserForm();
      await fetchPTUsers();
      Swal.fire('Berhasil', data.message || 'Akun PT berhasil disimpan', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan akun PT', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPTUser = item => {
    setPTUserForm({
      id: item.id,
      pt_name: item.pt_name || '',
      username: item.username || '',
      password: '',
      is_active: Boolean(item.is_active),
      package_share_mode: item.package_share_mode || '',
      package_share_value: typeof item.package_share_value === 'undefined' ? '' : String(item.package_share_value || ''),
      session_share_mode: item.session_share_mode || '',
      session_share_value: typeof item.session_share_value === 'undefined' ? '' : String(item.session_share_value || ''),
    });
    setActiveTab('staff');
  };

  const handleDeletePTUser = async item => {
    if (!item?.id) {
      return;
    }

    const confirm = await Swal.fire({
      title: 'Hapus akun PT ini?',
      text: `Akun login untuk ${item.pt_name || item.username || 'PT'} akan dihapus.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await performRequest(
        'pt-user/delete',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ id: Number(item.id) }),
        },
        'Gagal menghapus akun PT'
      );

      if (String(ptUserForm.id || '') === String(item.id)) {
        resetPTUserForm();
      }
      await fetchPTUsers();
      Swal.fire('Berhasil', data.message || 'Akun PT berhasil dihapus', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menghapus akun PT', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSale = async event => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const data = await performRequest(
        'pt-sale/create',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            client_id: Number(saleForm.client_id || 0),
            pt_user_id: Number(saleForm.pt_user_id || 0),
            product_id: Number(saleForm.product_id || 0),
            payment_method: saleForm.payment_method,
            discount_amount: Number(saleForm.discount_amount || 0),
            notes: saleForm.notes,
          }),
        },
        'Gagal membuat session sale'
      );

      resetSaleForm();
      await fetchSales();
      if (data?.data?.id) {
        setSelectedSaleId(data.data.id);
      }
      Swal.fire('Berhasil', data.message || 'Session sale berhasil dibuat', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal membuat session sale', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectSale = item => {
    if (!item?.id) {
      return;
    }
    setSelectedSaleId(item.id);
  };

  const handleAddSalePayment = async event => {
    event.preventDefault();
    if (!selectedSale?.id) {
      Swal.fire('Warning', 'Pilih session sale terlebih dahulu.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await performRequest(
        'pt-sale-payment/add',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            sale_id: Number(selectedSale.id),
            amount: Number(salePaymentForm.amount || 0),
            payment_method: salePaymentForm.payment_method,
            payment_reference: salePaymentForm.payment_reference,
            notes: salePaymentForm.notes,
            paid_at: salePaymentForm.paid_at,
            auto_release: salePaymentForm.auto_release,
          }),
        },
        'Gagal menambahkan payment session sale'
      );

      const updatedSaleID = data?.data?.sale?.id || selectedSale.id;
      await fetchSales();
      setSelectedSaleId(updatedSaleID);
      await fetchSalePayments(updatedSaleID);
      Swal.fire('Berhasil', data.message || 'Payment session sale berhasil ditambahkan', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menambahkan payment session sale', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = async item => {
    const confirm = await Swal.fire({
      title: 'Hapus session client?',
      text: item.full_name,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const data = await performRequest(
        'pt-client/delete',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ id: item.id }),
        },
        'Gagal menghapus session client'
      );
      await fetchClients();
      Swal.fire('Berhasil', data.message || 'Session client berhasil dihapus', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menghapus session client', 'error');
    }
  };

  const handleMarkPaid = async item => {
    const confirm = await Swal.fire({
      title: 'Lunasi session sale ini?',
      text: `${item.invoice_no} • ${item.client_name} • Sisa ${formatCurrency(item.outstanding_amount)}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Lunasi',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const data = await performRequest(
        'pt-sale/mark-paid',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: item.id,
            payment_method: item.payment_method,
          }),
        },
        'Gagal menandai session sale paid'
      );
      await fetchSales();
      if (item?.id) {
        setSelectedSaleId(item.id);
        await fetchSalePayments(item.id);
      }
      Swal.fire('Berhasil', data.message || 'Session sale berhasil ditandai paid', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menandai session sale paid', 'error');
    }
  };

  const handleChangeSalePaymentStatus = async (payment, action) => {
    if (!payment?.id) {
      return;
    }

    const config = action === 'refund'
      ? {
          endpoint: 'pt-sale-payment/refund',
          title: 'Refund payment ini?',
          confirmText: 'Refund',
          successMessage: 'Payment session sale berhasil di-refund',
          failureMessage: 'Gagal refund payment session sale',
        }
      : {
          endpoint: 'pt-sale-payment/void',
          title: 'Void payment ini?',
          confirmText: 'Void',
          successMessage: 'Payment session sale berhasil di-void',
          failureMessage: 'Gagal void payment session sale',
        };

    const confirm = await Swal.fire({
      title: config.title,
      text: `${selectedSale?.invoice_no || '-'} • ${formatCurrency(payment.amount)}`,
      icon: 'warning',
      showCancelButton: true,
      input: 'text',
      inputPlaceholder: 'Catatan',
      confirmButtonText: config.confirmText,
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const data = await performRequest(
        config.endpoint,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: payment.id,
            notes: confirm.value || '',
          }),
        },
        config.failureMessage
      );
      await fetchSales();
      if (selectedSale?.id) {
        await fetchSalePayments(selectedSale.id);
      }
      Swal.fire('Berhasil', data.message || config.successMessage, 'success');
    } catch (error) {
      Swal.fire('Error', error.message || config.failureMessage, 'error');
    }
  };

  const handleCancelSale = async item => {
    const confirm = await Swal.fire({
      title: 'Batalkan session sale?',
      text: `${item.invoice_no} • ${item.client_name}`,
      icon: 'warning',
      showCancelButton: true,
      input: 'text',
      inputPlaceholder: 'Catatan pembatalan',
      confirmButtonText: 'Batalkan',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const data = await performRequest(
        'pt-sale/cancel',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: item.id,
            notes: confirm.value || '',
          }),
        },
        'Gagal membatalkan session sale'
      );
      await fetchSales();
      Swal.fire('Berhasil', data.message || 'Session sale berhasil dibatalkan', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal membatalkan session sale', 'error');
    }
  };

  const handleRunBackfill = async () => {
    const confirm = await Swal.fire({
      title: 'Jalankan backfill fase 2?',
      text: 'Backfill ini membaca tabel legacy global tanpa outlet_id. Jalankan saat Anda siap migrasi contract dan booking.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Jalankan Backfill',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await performRequest(
        'pt-migration/backfill-phase2',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({}),
        },
        'Gagal menjalankan backfill legacy session'
      );

      setLastMigrationActionName('Backfill Fase 2');
      setLastMigrationResult(data.data || null);
      await Promise.all([fetchMigrationSummary(), fetchSales(), fetchClients(), fetchProducts()]);
      Swal.fire('Berhasil', data.message || 'Backfill legacy session selesai', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menjalankan backfill legacy session', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRunCompletedSync = async () => {
    const confirm = await Swal.fire({
      title: 'Sinkronkan jadwal completed legacy?',
      text: 'Aksi ini akan menghitung ulang status completed, complete, dan complate dari jadwal legacy, lalu menyesuaikan used_sessions dan remaining_sessions di skema fase 2.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sinkronkan Completed',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await performRequest(
        'pt-migration/sync-completed-phase2',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({}),
        },
        'Gagal menjalankan sinkronisasi completed legacy session'
      );

      setLastMigrationActionName('Sinkronisasi Completed Legacy');
      setLastMigrationResult(data.data || null);
      await Promise.all([fetchMigrationSummary(), fetchSales(), fetchClients(), fetchProducts()]);
      Swal.fire('Berhasil', data.message || 'Sinkronisasi completed legacy session selesai', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menjalankan sinkronisasi completed legacy session', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearCoachPayoutComposer = useCallback(() => {
    setSelectedCoachPayableNames([]);
    setCoachPayoutForm(createCoachPayoutForm());
  }, []);

  const handleCloseCoachAdjustmentModal = useCallback(() => {
    setShowCoachAdjustmentModal(false);
    setCoachAdjustmentForm(createCoachAdjustmentForm());
  }, []);

  const handleToggleCoachPayableSelection = useCallback(item => {
    const coachName = String(item?.coach_name || '').trim();
    if (!coachName) {
      return;
    }

    setSelectedCoachPayableNames(previous => {
      const targetKey = normalizeCoachPayableKey(coachName);
      const exists = previous.some(value => normalizeCoachPayableKey(value) === targetKey);
      if (exists) {
        return previous.filter(value => normalizeCoachPayableKey(value) !== targetKey);
      }
      return [...previous, coachName];
    });

    setCoachPayoutForm(previous => ({
      ...previous,
      coach_name: '',
      amount: '',
    }));
  }, []);

  const handleToggleAllCoachPayables = useCallback(() => {
    setSelectedCoachPayableNames(previous => {
      if (coachPayables.length > 0 && previous.length === coachPayables.length) {
        return [];
      }
      return coachPayables
        .map(item => String(item.coach_name || '').trim())
        .filter(Boolean);
    });

    setCoachPayoutForm(previous => ({
      ...previous,
      coach_name: '',
      amount: '',
    }));
  }, [coachPayables]);

  const handlePrefillSingleCoachPayout = useCallback(item => {
    const normalizedCoachName = resolveCoachPayableName(item);
    setSelectedCoachPayableNames([]);
    setCoachPayoutForm(previous => ({
      ...previous,
      coach_name: normalizedCoachName,
      amount: String(Number(item?.outstanding_amount || 0)),
    }));
  }, []);

  const handleOpenCoachAdjustmentModal = useCallback(item => {
    const normalizedCoachName = resolveCoachPayableName(item);
    if (!normalizedCoachName) {
      Swal.fire('Warning', 'Nama coach belum tersedia pada baris ini.', 'warning');
      return;
    }

    setCoachAdjustmentForm({
      coach_name: normalizedCoachName,
      adjustment_type: 'decrease',
      amount: '',
      adjustment_date: formatDateInput(),
      notes: '',
    });
    setShowCoachAdjustmentModal(true);
  }, []);

  const handleCloseCoachPayableDetail = useCallback(() => {
    setShowCoachPayableDetailModal(false);
    setSelectedCoachPayableDetail(null);
    setCoachPayableDetails([]);
  }, []);

  const handleOpenCoachPayableDetail = useCallback(async item => {
    const normalizedItem = normalizeCoachPayableItem(item);
    if (!normalizedItem.coach_name) {
      Swal.fire('Warning', 'Nama coach belum tersedia pada baris ini.', 'warning');
      return;
    }

    setSelectedCoachPayableDetail(normalizedItem);
    setCoachPayableDetails([]);
    setShowCoachPayableDetailModal(true);

    try {
      await fetchCoachPayableDetails(normalizedItem.coach_name);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal mengambil detail transaksi coach', 'error');
    }
  }, [fetchCoachPayableDetails]);

  const handleCoachPayableAdjustment = async event => {
    event.preventDefault();

    if (!coachAdjustmentForm.coach_name) {
      Swal.fire('Warning', 'Pilih coach yang mau dikoreksi terlebih dahulu.', 'warning');
      return;
    }
    if (Number(coachAdjustmentForm.amount || 0) <= 0) {
      Swal.fire('Warning', 'Isi nominal koreksi terlebih dahulu.', 'warning');
      return;
    }

    setIsCoachPayableAdjustmentSaving(true);
    try {
      const payload = {
        outlet_id: selectedOutletNumber,
        coach_name: String(coachAdjustmentForm.coach_name || '').trim(),
        adjustment_type: String(coachAdjustmentForm.adjustment_type || '').trim(),
        adjustment_date: coachAdjustmentForm.adjustment_date,
        amount: Number(coachAdjustmentForm.amount || 0),
        notes: String(coachAdjustmentForm.notes || '').trim(),
      };

      const data = await performRequest(
        'fitness-coach-payable/adjust',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(payload),
        },
        'Gagal memproses koreksi hutang coach'
      );

      const remainingAmount = Number(data?.payable?.outstanding_amount || 0);
      const directionLabel = coachAdjustmentForm.adjustment_type === 'increase'
        ? 'menambah hutang coach'
        : 'mengurangi hutang coach';
      const accountingLabel = coachAdjustmentForm.adjustment_type === 'increase'
        ? 'Beban coach bertambah dan coach payable ikut naik.'
        : 'Coach payable berkurang dan beban coach ikut dikurangi.';

      Swal.fire({
        icon: 'success',
        title: 'Koreksi hutang coach berhasil dipost',
        html: `
          <strong>${coachAdjustmentForm.coach_name}</strong><br/>
          Koreksi ${directionLabel}: <strong>${formatCurrency(coachAdjustmentForm.amount)}</strong><br/>
          Outstanding terbaru: <strong>${formatCurrency(remainingAmount)}</strong><br/>
          ${accountingLabel}
        `,
      });

      handleCloseCoachAdjustmentModal();
      await Promise.all([fetchCoachPayables(), fetchCoachPayouts()]);
      if (selectedCoachPayableDetail?.coach_name) {
        await fetchCoachPayableDetails(selectedCoachPayableDetail.coach_name);
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memproses koreksi hutang coach', 'error');
    } finally {
      setIsCoachPayableAdjustmentSaving(false);
    }
  };

  const handleExportCoachPayableDetailPdf = useCallback(() => {
    if (!selectedCoachPayableDetail) {
      return;
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });
    const marginX = 40;
    let cursorY = 44;
    const coachName = selectedCoachPayableDetail.coach_name || 'Coach';

    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Detail Transaksi Coach', marginX, cursorY);

    cursorY += 22;
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text(`Coach: ${coachName}`, marginX, cursorY);
    cursorY += 16;
    doc.text(
      `Outstanding: ${formatCurrency(selectedCoachPayableDetail.outstanding_amount)} | Sudah dibayar: ${formatCurrency(selectedCoachPayableDetail.paid_amount)}`,
      marginX,
      cursorY
    );
    cursorY += 16;
    doc.text(
      `Share transaksi: ${formatCurrency(totalCoachPayableDetailSalesShare)} | Koreksi: ${formatSignedCurrency(totalCoachPayableDetailAdjustment)} | Payout: ${formatCurrency(totalCoachPayableDetailPayout)} | Net hutang: ${formatCurrency(totalCoachPayableDetailNet)}`,
      marginX,
      cursorY
    );
    cursorY += 16;
    doc.text(
      `Total transaksi: ${Number(selectedCoachPayableDetail.transaction_count || 0).toLocaleString('id-ID')} | Koreksi: ${Number(selectedCoachPayableDetail.adjustment_count || 0).toLocaleString('id-ID')} | Payout: ${Number(selectedCoachPayableDetail.payout_count || 0).toLocaleString('id-ID')}`,
      marginX,
      cursorY
    );

    autoTable(doc, {
      startY: cursorY + 18,
      head: [['Tanggal', 'Client', 'Channel', 'Keterangan', 'Nilai', 'Mutasi Hutang', 'Saldo Hutang', 'Referensi', 'Status']],
      body: coachPayableDetailPrintRows.length
        ? coachPayableDetailPrintRows.map(item => [
            item.date,
            item.client,
            item.channel,
            item.program,
            item.gross,
            item.share,
            item.balance,
            item.payment,
            item.status,
          ])
        : [['-', '-', '-', '-', '-', '-', '-', '-', '-']],
      styles: {
        fontSize: 9,
        cellPadding: 6,
        valign: 'middle',
      },
      headStyles: {
        fillColor: [33, 37, 41],
      },
      theme: 'grid',
      margin: { left: marginX, right: marginX },
    });

    doc.save(
      `coach-payable-detail-${sanitizeCoachPayableDetailFilename(coachName)}-${formatDateInput(new Date())}.pdf`
    );
  }, [
    coachPayableDetailPrintRows,
    selectedCoachPayableDetail,
    totalCoachPayableDetailAdjustment,
    totalCoachPayableDetailNet,
    totalCoachPayableDetailPayout,
    totalCoachPayableDetailSalesShare,
  ]);

  const handleExportCoachPayableDetailExcel = useCallback(() => {
    if (!selectedCoachPayableDetail) {
      return;
    }

    if (!coachPayableDetails.length) {
      Swal.fire('Warning', 'Belum ada transaksi detail untuk diexport.', 'warning');
      return;
    }

    const coachName = selectedCoachPayableDetail.coach_name || 'Coach';
    const detailRows = coachPayableDetails.map(item => ({
      Coach: coachName,
      Tanggal: formatDateTime(item.created_at),
      JenisDetail: item.detail_type || '-',
      Client: item.detail_type === 'sale' ? item.client_name || '-' : '-',
      Channel: formatFitnessCoachTransactionTypeLabel(item.transaction_type),
      Keterangan: item.detail_type === 'adjustment'
        ? [
            item.adjustment_type === 'increase' ? 'Tambah hutang coach' : 'Kurangi hutang coach',
            item.notes || '',
          ].filter(Boolean).join(' • ') || '-'
        : item.detail_type === 'payout'
          ? [
              'Pembayaran coach',
              item.notes || item.payment_method || '',
            ].filter(Boolean).join(' • ') || '-'
          : item.class_name || item.pt_name || '-',
      Nilai: formatCurrency(item.gross_amount),
      MutasiHutang: item.detail_type === 'sale'
        ? formatCurrency(item.coach_share_amount)
        : formatSignedCurrency(item.coach_share_amount),
      SaldoSebelum: formatCurrency(item.outstanding_before_amount),
      SaldoSesudah: formatCurrency(item.outstanding_after_amount),
      Referensi: item.reference_no || `#${item.id}`,
      Jurnal: item.journal_id || '-',
      Pembayaran: item.detail_type === 'sale'
        ? item.payment_method || '-'
        : item.detail_type === 'payout'
          ? [item.payment_method || '-', item.notes || ''].filter(Boolean).join(' • ')
          : item.notes || '-',
      Status: item.status || '-',
    }));

    const workbook = XLSX.utils.book_new();
    const detailSheet = XLSX.utils.json_to_sheet(detailRows);

    detailSheet['!cols'] = [
      { wch: 24 },
      { wch: 22 },
      { wch: 18 },
      { wch: 22 },
      { wch: 18 },
      { wch: 42 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 12 },
      { wch: 24 },
      { wch: 14 },
    ];

    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detail Transaksi');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `coach-payable-detail-${sanitizeCoachPayableDetailFilename(coachName)}-${formatDateInput(new Date())}.xlsx`
    );
  }, [
    coachPayableDetails,
    selectedCoachPayableDetail,
  ]);

  const handlePrintCoachPayableDetail = useCallback(() => {
    if (!selectedCoachPayableDetail) {
      return;
    }

    const coachName = selectedCoachPayableDetail.coach_name || 'Coach';
    const popup = window.open('', '_blank', 'noopener,noreferrer,width=1200,height=800');
    if (!popup) {
      Swal.fire('Warning', 'Popup print diblok browser. Izinkan popup lalu coba lagi.', 'warning');
      return;
    }

    const rowsHtml = coachPayableDetailPrintRows.length
      ? coachPayableDetailPrintRows
          .map(
            item => `
              <tr>
                <td>${escapeHtml(item.date)}</td>
                <td>${escapeHtml(item.client)}</td>
                <td>${escapeHtml(item.channel)}</td>
                <td>${escapeHtml(item.program)}</td>
                <td style="text-align:right">${escapeHtml(item.gross)}</td>
                <td style="text-align:right">${escapeHtml(item.share)}</td>
                <td style="text-align:right">${escapeHtml(item.balance)}</td>
                <td>${escapeHtml(item.payment)}</td>
                <td>${escapeHtml(item.status)}</td>
              </tr>
            `
          )
          .join('')
      : '<tr><td colspan="9" style="text-align:center">Belum ada detail transaksi untuk coach ini.</td></tr>';

    popup.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Detail Transaksi Coach - ${escapeHtml(coachName)}</title>
          <style>
            body { font-family: Arial, sans-serif; color: #1f2937; margin: 24px; }
            h1 { margin: 0 0 8px; font-size: 24px; }
            .meta { margin-bottom: 18px; line-height: 1.6; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; vertical-align: top; }
            th { background: #111827; color: #fff; text-align: left; }
            .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 18px 0; }
            .card { border: 1px solid #d1d5db; border-radius: 10px; padding: 12px; }
            .label { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
            .value { font-size: 18px; font-weight: 700; margin-top: 6px; }
          </style>
        </head>
        <body>
          <h1>Detail Transaksi Coach</h1>
          <div class="meta">
            <div><strong>Coach:</strong> ${escapeHtml(coachName)}</div>
            <div><strong>Dicetak:</strong> ${escapeHtml(formatDateTime(new Date()))}</div>
          </div>
          <div class="summary">
            <div class="card">
              <div class="label">Outstanding</div>
              <div class="value">${escapeHtml(formatCurrency(selectedCoachPayableDetail.outstanding_amount))}</div>
            </div>
            <div class="card">
              <div class="label">Sudah Dibayar</div>
              <div class="value">${escapeHtml(formatCurrency(selectedCoachPayableDetail.paid_amount))}</div>
            </div>
            <div class="card">
              <div class="label">Share Transaksi</div>
              <div class="value">${escapeHtml(formatCurrency(totalCoachPayableDetailSalesShare))}</div>
            </div>
            <div class="card">
              <div class="label">Koreksi Hutang</div>
              <div class="value">${escapeHtml(formatSignedCurrency(totalCoachPayableDetailAdjustment))}</div>
            </div>
            <div class="card">
              <div class="label">Coach Payout</div>
              <div class="value">${escapeHtml(formatCurrency(totalCoachPayableDetailPayout))}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Client</th>
                <th>Channel</th>
                <th>Keterangan</th>
                <th>Nilai</th>
                <th>Mutasi Hutang</th>
                <th>Saldo Hutang</th>
                <th>Referensi</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
  }, [
    coachPayableDetailPrintRows,
    selectedCoachPayableDetail,
    totalCoachPayableDetailAdjustment,
    totalCoachPayableDetailPayout,
    totalCoachPayableDetailSalesShare,
  ]);

  const handleCoachPayout = async event => {
    event.preventDefault();

    if (isBatchCoachPayoutMode && selectedCoachPayables.length === 0) {
      Swal.fire('Warning', 'Centang minimal satu hutang coach terlebih dahulu.', 'warning');
      return;
    }

    if (!coachPayoutForm.payment_account_id) {
      Swal.fire('Warning', 'Pilih akun pembayaran terlebih dahulu.', 'warning');
      return;
    }

    if (!isBatchCoachPayoutMode && (!coachPayoutForm.coach_name || Number(coachPayoutForm.amount || 0) <= 0)) {
      Swal.fire('Warning', 'Pilih coach dan nominal payout terlebih dahulu.', 'warning');
      return;
    }

    if (
      selectedCoachPaymentAccountIsCash &&
      coachPayoutForm.deduct_from_shift_cash &&
      Number(coachPayoutForm.shift_cashier_id || 0) <= 0
    ) {
      Swal.fire('Warning', 'Pilih staff shift yang akan dikurangi terlebih dahulu.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = isBatchCoachPayoutMode
        ? {
            outlet_id: selectedOutletNumber,
            coach_names: selectedCoachPayables.map(item => item.coach_name),
            payout_date: coachPayoutForm.payout_date,
            payment_method: selectedCoachPaymentAccount?.name || coachPayoutForm.payment_account_id,
            payment_account_id: coachPayoutForm.payment_account_id,
            deduct_from_shift_cash:
              selectedCoachPaymentAccountIsCash && Boolean(coachPayoutForm.deduct_from_shift_cash),
            shift_cashier_id:
              selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash
                ? Number(coachPayoutForm.shift_cashier_id || 0)
                : 0,
            shift_cashier_name:
              selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash
                ? String(coachPayoutForm.shift_cashier_name || '').trim()
                : '',
            notes: String(coachPayoutForm.notes || '').trim(),
          }
        : {
            outlet_id: selectedOutletNumber,
            coach_name: coachPayoutForm.coach_name,
            payout_date: coachPayoutForm.payout_date,
            amount: Number(coachPayoutForm.amount || 0),
            payment_method: selectedCoachPaymentAccount?.name || coachPayoutForm.payment_account_id,
            payment_account_id: coachPayoutForm.payment_account_id,
            deduct_from_shift_cash:
              selectedCoachPaymentAccountIsCash && Boolean(coachPayoutForm.deduct_from_shift_cash),
            shift_cashier_id:
              selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash
                ? Number(coachPayoutForm.shift_cashier_id || 0)
                : 0,
            shift_cashier_name:
              selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash
                ? String(coachPayoutForm.shift_cashier_name || '').trim()
                : '',
            notes: String(coachPayoutForm.notes || '').trim(),
          };

      const data = await performRequest(
        'fitness-coach-payout/create',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(payload),
        },
        'Gagal memproses coach payout'
      );

      if (isBatchCoachPayoutMode) {
        const result = data?.data || {};
        const processedCoachCount = Number(result.coach_count || selectedCoachPayables.length || 0);
        const processedAmount = Number(result.total_amount || selectedCoachPayableTotal || 0);
        const coachNames = selectedCoachPayables.map(item => item.coach_name).slice(0, 4);
        const moreCoachCount = Math.max(selectedCoachPayables.length - coachNames.length, 0);

        Swal.fire({
          icon: 'success',
          title: 'Coach payout batch berhasil dipost',
          html: `
            <strong>${processedCoachCount}</strong> coach dibayar sekaligus<br/>
            Total payout: <strong>${formatCurrency(processedAmount)}</strong><br/>
            Coach: <strong>${coachNames.join(', ')}${moreCoachCount > 0 ? ` +${moreCoachCount} lainnya` : ''}</strong><br/>
            ${selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash
              ? `Kas shift <strong>${selectedCoachShiftStaffOption?.staff_name || coachPayoutForm.shift_cashier_name || '-'}</strong> akan ikut berkurang pada closing aktif.`
              : 'Payout dipost tanpa mengurangi kas shift aktif.'}
          `,
        });
      } else {
        const remainingAmount = Number(data?.payable?.outstanding_amount || 0);

        Swal.fire({
          icon: 'success',
          title: 'Coach payout berhasil dipost',
          html: `
            <strong>${coachPayoutForm.coach_name}</strong><br/>
            Nominal payout: <strong>${formatCurrency(coachPayoutForm.amount)}</strong><br/>
            Sisa hutang coach: <strong>${formatCurrency(remainingAmount)}</strong><br/>
            ${selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash
              ? `Kas shift <strong>${selectedCoachShiftStaffOption?.staff_name || coachPayoutForm.shift_cashier_name || '-'}</strong> akan ikut berkurang pada closing aktif.`
              : 'Payout dipost tanpa mengurangi kas shift aktif.'}
          `,
        });
      }

      clearCoachPayoutComposer();
      await Promise.all([fetchCoachPayables(), fetchCoachPayouts(), fetchCoachShiftStaffOptions()]);
      if (selectedCoachPayableDetail?.coach_name) {
        await fetchCoachPayableDetails(selectedCoachPayableDetail.coach_name);
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memproses coach payout', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveSchedule = async event => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const data = await performRequest(
        scheduleForm.id ? 'pt-schedule/update' : 'pt-schedule/add',
        {
          method: scheduleForm.id ? 'PUT' : 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            id: scheduleForm.id,
            outlet_id: selectedOutletNumber,
            pt_name: String(scheduleForm.pt_name || '').trim(),
            client_name: String(scheduleForm.client_name || '').trim(),
            schedule_date: scheduleForm.schedule_date,
            schedule_time: scheduleForm.schedule_time,
            status: scheduleForm.status,
            notes: String(scheduleForm.notes || '').trim(),
          }),
        },
        'Gagal menyimpan jadwal session'
      );

      resetScheduleForm();
      await fetchSchedules();
      if (data.notification_sent || data.notification_warning) {
        Swal.fire({
          icon: data.notification_warning ? 'warning' : 'success',
          title: data.message || 'Jadwal session berhasil disimpan',
          html: `
            ${data.notification_sent ? '<div>Report progress otomatis berhasil dikirim ke WhatsApp client.</div>' : ''}
            ${data.notification_warning ? `<div class="mt-2">${data.notification_warning}</div>` : ''}
          `,
        });
      } else {
        Swal.fire('Berhasil', data.message || 'Jadwal session berhasil disimpan', 'success');
      }
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan jadwal session', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSchedule = async item => {
    const confirm = await Swal.fire({
      title: 'Hapus jadwal session?',
      text: `${item.schedule_date} ${item.schedule_time} • ${item.client_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
    });
    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const data = await performRequest(
        'pt-schedule/delete',
        {
          method: 'DELETE',
          headers: authHeaders,
          body: JSON.stringify({ id: item.id }),
        },
        'Gagal menghapus jadwal session'
      );
      await fetchSchedules();
      Swal.fire('Berhasil', data.message || 'Jadwal session berhasil dihapus', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menghapus jadwal session', 'error');
    }
  };

  const handleEditClient = item => {
    const memberID = item?.member_id ? String(item.member_id) : '';
    setClientForm({
      id: item.id,
      member_id: memberID,
      full_name: item.full_name || '',
      client_type: item.client_type || 'non_member',
      phone: item.phone || '',
      email: item.email || '',
      notes: item.notes || '',
      is_active: Boolean(item.is_active),
    });
    if ((item.client_type || 'non_member') === 'member' && memberID) {
      const memberName = String(item.member_name || item.full_name || '').trim();
      const memberLabel = [memberName || `Member #${memberID}`, item.phone, item.email]
        .filter(Boolean)
        .join(' • ');
      setSelectedClientMemberOption({
        value: memberID,
        label: memberLabel || `Member #${memberID}`,
        data: {
          id: Number(memberID),
          first_name: memberName,
          hp: item.phone || '',
          email: item.email || '',
        },
      });
    } else {
      setSelectedClientMemberOption(null);
    }
    setActiveTab('clients');
  };

  const handleOpenClientProgress = async item => {
    if (!item?.id) {
      return;
    }

    setActiveTab('progress');
    setClientProgressDetailTab('overview');
    setClientGoalTrendMetric('body_fat_percent');
    setSelectedProgressClientId(item.id);
    try {
      await fetchClientProgressReport(item.id);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal mengambil report progress client', 'error');
    }
  };

  const copyTextToClipboard = async text => {
    if (!text) {
      throw new Error('Belum ada isi report yang bisa disalin');
    }

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleCopyClientProgress = async () => {
    try {
      await copyTextToClipboard(clientProgressShareText);
      Swal.fire('Berhasil', 'Ringkasan progress client berhasil disalin', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyalin ringkasan progress client', 'error');
    }
  };

  const handleSendClientProgress = async () => {
    const clientID = Number(clientProgressReport?.client?.id || selectedProgressClientId || 0);
    const outletID = Number(selectedOutletNumber || clientProgressReport?.client?.outlet_id || selectedProgressClient?.outlet_id || 0);

    if (!clientID) {
      Swal.fire('Warning', 'Pilih client terlebih dahulu.', 'warning');
      return;
    }
    if (!outletID) {
      Swal.fire('Warning', 'Pilih outlet terlebih dahulu sebelum mengirim progress client.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await performRequest(
        'openclaw/pt-progress/send',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: outletID,
            client_id: clientID,
          }),
        },
        'Gagal mengirim report progress ke WhatsApp client'
      );

      if (shouldLoadOpenClaw) {
        await fetchOpenClawConversations();
      }
      Swal.fire('Berhasil', data.message || 'Report progress berhasil dikirim ke WhatsApp client', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal mengirim report progress ke WhatsApp client', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveOpenClawSettings = async event => {
    event.preventDefault();
    if (!hasSelectedOutletForOpenClaw) {
      Swal.fire('Warning', 'Pilih outlet terlebih dahulu sebelum menyimpan pengaturan WhatsApp.', 'warning');
      return;
    }

    setIsOpenClawSettingsSaving(true);
    try {
      const data = await performRequest(
        'openclaw/settings/save',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            is_enabled: Boolean(openClawSettingsForm.is_enabled),
            auto_send_pt_completed: Boolean(openClawSettingsForm.auto_send_pt_completed),
            openclaw_bin: openClawSettingsForm.openclaw_bin,
            channel: openClawSettingsForm.channel,
            account_id: openClawSettingsForm.account_id,
            control_url: openClawSettingsForm.control_url,
            webhook_secret: openClawSettingsForm.webhook_secret,
          }),
        },
        'Gagal menyimpan pengaturan OpenClaw'
      );

      const nextSettings = data.data || null;
      if (nextSettings) {
        setOpenClawSettingsForm({
          is_enabled: Boolean(nextSettings.is_enabled),
          auto_send_pt_completed: typeof nextSettings.auto_send_pt_completed === 'undefined'
            ? true
            : Boolean(nextSettings.auto_send_pt_completed),
          openclaw_bin: nextSettings.openclaw_bin || 'openclaw',
          channel: nextSettings.channel || 'whatsapp',
          account_id: nextSettings.account_id || '',
          control_url: nextSettings.control_url || '',
          webhook_secret: nextSettings.webhook_secret || '',
        });
      }

      Swal.fire('Berhasil', data.message || 'Pengaturan OpenClaw berhasil disimpan', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan pengaturan OpenClaw', 'error');
    } finally {
      setIsOpenClawSettingsSaving(false);
    }
  };

  const handleOpenOpenClawKnowledgeModal = item => {
    if (!item) {
      setOpenClawKnowledgeForm(createOpenClawKnowledgeForm());
      setShowOpenClawKnowledgeModal(true);
      return;
    }

    setOpenClawKnowledgeForm({
      id: item.id || null,
      category: item.category || 'general',
      question: item.question || '',
      answer: item.answer || '',
      bullets: joinTextAreaLines(item.bullets),
      keywords: joinTextAreaLines(item.keywords),
      example_questions: joinTextAreaLines(item.example_questions),
      priority: String(item.priority || 100),
      is_active: typeof item.is_active === 'undefined' ? true : Boolean(item.is_active),
      notes: item.notes || '',
    });
    setShowOpenClawKnowledgeModal(true);
  };

  const handleCloseOpenClawKnowledgeModal = () => {
    if (isOpenClawKnowledgeSaving) {
      return;
    }
    setShowOpenClawKnowledgeModal(false);
    setOpenClawKnowledgeForm(createOpenClawKnowledgeForm());
  };

  const handleSaveOpenClawKnowledge = async event => {
    event.preventDefault();
    if (!selectedOutletNumber) {
      Swal.fire('Warning', 'Pilih outlet terlebih dahulu sebelum menyimpan knowledge base.', 'warning');
      return;
    }
    if (!String(openClawKnowledgeForm.question || '').trim()) {
      Swal.fire('Warning', 'Pertanyaan knowledge base wajib diisi.', 'warning');
      return;
    }
    if (!String(openClawKnowledgeForm.answer || '').trim()) {
      Swal.fire('Warning', 'Jawaban knowledge base wajib diisi.', 'warning');
      return;
    }

    setIsOpenClawKnowledgeSaving(true);
    try {
      const data = await performRequest(
        'openclaw/knowledge/save',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            id: Number(openClawKnowledgeForm.id || 0),
            category: openClawKnowledgeForm.category,
            question: openClawKnowledgeForm.question,
            answer: openClawKnowledgeForm.answer,
            bullets: normalizeTextAreaLines(openClawKnowledgeForm.bullets),
            keywords: normalizeKeywordLines(openClawKnowledgeForm.keywords),
            example_questions: normalizeTextAreaLines(openClawKnowledgeForm.example_questions),
            priority: Number(openClawKnowledgeForm.priority || 100),
            is_active: Boolean(openClawKnowledgeForm.is_active),
            notes: openClawKnowledgeForm.notes,
          }),
        },
        'Gagal menyimpan knowledge base WhatsApp'
      );

      await fetchOpenClawKnowledge();
      handleCloseOpenClawKnowledgeModal();
      Swal.fire('Berhasil', data.message || 'Knowledge base berhasil disimpan', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menyimpan knowledge base WhatsApp', 'error');
    } finally {
      setIsOpenClawKnowledgeSaving(false);
    }
  };

  const handleDeleteOpenClawKnowledge = async item => {
    if (!item?.id) {
      return;
    }

    const result = await Swal.fire({
      title: 'Hapus knowledge base?',
      text: 'Knowledge yang dihapus tidak akan dipakai lagi oleh chat WhatsApp.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus',
      cancelButtonText: 'Batal',
    });
    if (!result.isConfirmed) {
      return;
    }

    try {
      const data = await performRequest(
        'openclaw/knowledge/delete',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            id: Number(item.id || 0),
          }),
        },
        'Gagal menghapus knowledge base WhatsApp'
      );
      await fetchOpenClawKnowledge();
      Swal.fire('Berhasil', data.message || 'Knowledge base berhasil dihapus', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal menghapus knowledge base WhatsApp', 'error');
    }
  };

  const handleSelectOpenClawConversation = item => {
    if (!item?.conversation_key) {
      return;
    }
    setSelectedOpenClawConversationKey(item.conversation_key);
    setOpenClawReplyBody('');
  };

  const handleSendOpenClawReply = async event => {
    event.preventDefault();

    if (!selectedOpenClawConversation) {
      Swal.fire('Warning', 'Pilih percakapan WhatsApp terlebih dahulu.', 'warning');
      return;
    }
    if (!String(openClawReplyBody || '').trim()) {
      Swal.fire('Warning', 'Tulis balasan terlebih dahulu.', 'warning');
      return;
    }

    setIsOpenClawReplySending(true);
    try {
      const data = await performRequest(
        'openclaw/inbox/reply',
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            outlet_id: selectedOutletNumber,
            conversation_key: selectedOpenClawConversation.conversation_key,
            client_id: Number(selectedOpenClawConversation.client_id || 0),
            client_name: selectedOpenClawConversation.client_name || '',
            participant_name: selectedOpenClawConversation.participant_name || selectedOpenClawConversation.client_name || '',
            phone: selectedOpenClawConversation.phone || '',
            body: String(openClawReplyBody || '').trim(),
          }),
        },
        'Gagal mengirim balasan WhatsApp'
      );

      setOpenClawReplyBody('');
      await Promise.all([
        fetchOpenClawConversations(),
        fetchOpenClawMessages(selectedOpenClawConversation.conversation_key, { markRead: false }),
      ]);
      Swal.fire('Berhasil', data.message || 'Balasan WhatsApp berhasil dikirim', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal mengirim balasan WhatsApp', 'error');
    } finally {
      setIsOpenClawReplySending(false);
    }
  };

  const renderStatusBadge = (value, variantMap) => {
    const variant = variantMap[value] || 'secondary';
    return <Badge bg={variant}>{value || '-'}</Badge>;
  };

  const renderSalePaymentStatusBadge = value => renderStatusBadge(value, {
    pending: 'warning',
    partial_paid: 'info',
    paid: 'success',
    cancelled: 'secondary',
    failed: 'danger',
    refunded: 'dark',
  });

  const renderContractStatusBadge = value => renderStatusBadge(value, {
    not_created: 'secondary',
    frozen: 'warning',
    active: 'success',
    completed: 'primary',
    expired: 'dark',
    cancelled: 'secondary',
  });

  const renderSalePaymentRecordStatusBadge = value => renderStatusBadge(value, {
    confirmed: 'success',
    void: 'secondary',
    refunded: 'dark',
  });

  const renderScheduleStatusBadge = value => {
    const normalized = String(value || '').trim().toLowerCase();
    const config = {
      requested: { label: 'Requested', variant: 'warning' },
      on_schedule: { label: 'On Schedule', variant: 'info' },
      scheduled: { label: 'Scheduled', variant: 'primary' },
      completed: { label: 'Completed', variant: 'success' },
      cancelled: { label: 'Cancelled', variant: 'secondary' },
      rescheduled: { label: 'Rescheduled', variant: 'dark' },
    }[normalized] || { label: value || '-', variant: 'secondary' };

    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const renderProgressAttendanceBadge = value => renderStatusBadge(formatPTProgressAttendanceLabel(value), {
    Pending: 'warning',
    'Checked In': 'info',
    Completed: 'success',
    'No Show': 'dark',
    Cancelled: 'secondary',
  });

  const renderProgressBookingBadge = value => renderStatusBadge(formatPTProgressBookingLabel(value), {
    Requested: 'warning',
    Confirmed: 'info',
    Rescheduled: 'dark',
    Cancelled: 'secondary',
  });

  const activeClientCount = clients.filter(item => item.is_active).length;

  const summaryStats = isClientsOnlyMode
    ? [
        {
          label: 'Session Clients',
          value: clients.length,
          icon: FaUserFriends,
          hint: `${activeClientCount} client aktif siap dipakai di front desk`,
        },
        {
          label: 'Walk-in Ready',
          value: clients.filter(item => item.client_type === 'non_member' && item.is_active).length,
          icon: FaUserTie,
          hint: 'Client non-member siap dipilih saat penjualan PT',
        },
      ]
    : [
        {
          label: 'Session Staff',
          value: ptUsers.length,
          icon: FaUserTie,
          hint: `${ptUsers.filter(item => item.is_active).length} akun login aktif`,
        },
        {
          label: 'Session Clients',
          value: clients.length,
          icon: FaUserFriends,
          hint: 'Master client aktif dan legacy',
        },
        {
          label: 'Session Services',
          value: products.length,
          icon: FaBoxOpen,
          hint: 'Katalog layanan per outlet',
        },
        {
          label: 'Session Sales',
          value: sales.length,
          icon: FaCreditCard,
          hint: 'Dokumen penjualan layanan',
        },
        ...(canManageCoachPayables ? [{
          label: 'Coach Outstanding',
          value: formatCurrency(totalCoachPayableOutstanding),
          icon: FaMoneyBillWave,
          hint: `${coachPayables.length} coach masih punya saldo hutang`,
        }] : []),
        ...(canManageOpenClaw ? [{
          label: 'WhatsApp Inbox',
          value: totalOpenClawUnread,
          icon: FaPaperPlane,
          hint: `${openClawConversations.length} percakapan tercatat di OpenClaw`,
        }] : []),
        {
          label: 'Pending Legacy',
          value: migrationSummary?.pending_package_total || 0,
          icon: FaCalendarAlt,
          hint: 'Paket legacy belum jadi contract',
        },
      ];

  const pageHeader = isClientsOnlyMode
    ? {
        eyebrow: 'Client workspace',
        title: 'Clients Session',
        description: 'Kelola nama client private session agar staf front desk bisa memilih walk-in client saat menjual layanan PT.',
        breadcrumbs: [
          { label: 'Dashboard', to: '/dashboardadmin' },
          { label: 'Clients Session' },
        ],
        meta: (
          <div className="d-flex flex-wrap gap-2">
            <span className="soft-stat">
              <FaUserFriends /> {clients.length} client
            </span>
            <span className="soft-stat">
              <FaCheckCircle /> {activeClientCount} aktif
            </span>
          </div>
        ),
      }
    : {
        eyebrow: 'Program workspace',
        title: 'Private Session Setup',
        description: 'Kelola client private session, katalog layanan, penjualan, dan migrasi legacy dari satu workspace yang dibuka melalui halaman Program Setup.',
        breadcrumbs: [
          { label: 'Dashboard', to: '/dashboardadmin' },
          { label: 'Program Setup', to: '/fitness-transactions/setup' },
          { label: 'Private Session Setup' },
        ],
        meta: (
          <div className="d-flex flex-wrap gap-2">
            <span className="soft-stat">
              <FaCreditCard /> {sales.length} sale
            </span>
            {canManageOpenClaw ? (
              <span className="soft-stat">
                <FaPaperPlane /> {totalOpenClawUnread} WA unread
              </span>
            ) : null}
            <span className="soft-stat">
              <FaCalendarAlt /> {migrationSummary?.pending_schedule_total || 0} schedule pending
            </span>
          </div>
        ),
      };

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell pt-phase15-page mt-4 px-4">
        <AdminPageHeader
          icon={FaUserTie}
          eyebrow={pageHeader.eyebrow}
          title={pageHeader.title}
          description={pageHeader.description}
          breadcrumbs={pageHeader.breadcrumbs}
          meta={pageHeader.meta}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              {!isScopedActor ? (
                <Form.Select
                  value={selectedOutlet}
                  onChange={event => setSelectedOutlet(event.target.value)}
                  style={{ minWidth: 220 }}
                >
                  <option value="">Semua outlet</option>
                  {outlets.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.outlet_name}
                    </option>
                  ))}
                </Form.Select>
              ) : null}
              <Button variant="outline-secondary" onClick={bootstrapPage} disabled={isBootstrapping}>
                <FaRedoAlt className="me-2" />
                Refresh
              </Button>
            </div>
          )}
        />

        {!isClientsOnlyMode ? (
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="d-flex flex-wrap gap-2">
              <Button as={Link} to="/fitness-transactions/setup" variant="outline-secondary">
                <FaSlidersH className="me-2" />
                Program Setup
              </Button>
              <Button variant="dark" disabled>
                <FaUserTie className="me-2" />
                Private Session Setup
              </Button>
            </Card.Body>
          </Card>
        ) : null}

        <Row className="g-3 mb-4">
          {summaryStats.map(item => {
            const Icon = item.icon;
            return (
              <Col key={item.label} md={6} xl={3}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="text-muted small">{item.label}</div>
                        <div className="fs-3 fw-semibold">{item.value}</div>
                        <div className="small text-secondary">{item.hint}</div>
                      </div>
                      <Badge bg="light" text="dark" pill>
                        <Icon />
                      </Badge>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="d-flex flex-wrap gap-2">
            {availableTabs.length > 1 ? availableTabs.map(item => {
              const Icon = item.icon;
              const active = activeTab === item.key;
              return (
                <Button
                  key={item.key}
                  variant={active ? 'dark' : 'outline-secondary'}
                  onClick={() => setActiveTab(item.key)}
                >
                  <Icon className="me-2" />
                  {item.label}
                </Button>
              );
            }) : (
              <div className="small text-muted">
                Staff bisa mengelola nama client session di halaman ini tanpa membuka seluruh workspace program.
              </div>
            )}
          </Card.Body>
        </Card>

        {isBootstrapping ? (
          <Card className="shadow-sm border-0">
            <Card.Body className="text-center py-5">
              <Spinner animation="border" />
              <div className="mt-3">Memuat workspace private session...</div>
            </Card.Body>
          </Card>
        ) : null}

        {!isBootstrapping && activeTab === 'staff' ? (
          <Row className="g-4">
            <Col xl={5}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <strong>{ptUserForm.id ? 'Edit Session Staff' : 'Tambah Session Staff'}</strong>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSavePTUser}>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Label>Nama PT</Form.Label>
                        <Form.Control
                          value={ptUserForm.pt_name}
                          onChange={event => setPTUserForm(prev => ({ ...prev, pt_name: event.target.value }))}
                          placeholder="Contoh: Anggo"
                          required
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label>Username login</Form.Label>
                        <Form.Control
                          value={ptUserForm.username}
                          onChange={event => setPTUserForm(prev => ({ ...prev, username: event.target.value }))}
                          placeholder="Username untuk login PT"
                          autoComplete="off"
                          required
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label>
                          Password
                          {ptUserForm.id ? ' baru' : ''}
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={ptUserForm.password}
                          onChange={event => setPTUserForm(prev => ({ ...prev, password: event.target.value }))}
                          placeholder={ptUserForm.id ? 'Kosongkan jika tidak ingin ganti password' : 'Password login PT'}
                          autoComplete="new-password"
                          required={!ptUserForm.id}
                        />
                        <div className="small text-muted mt-2">
                          {ptUserForm.id
                            ? 'Saat edit akun, password boleh dikosongkan jika tidak ingin diubah.'
                            : 'Password ini dipakai PT untuk login di halaman PT Login.'}
                        </div>
                      </Col>
                      <Col md={12}>
                        <Form.Check
                          type="switch"
                          label={ptUserForm.is_active ? 'Akun aktif' : 'Akun nonaktif'}
                          checked={ptUserForm.is_active}
                          onChange={event => setPTUserForm(prev => ({ ...prev, is_active: event.target.checked }))}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Default share paket</Form.Label>
                        <Form.Control
                          value={ptUserForm.package_share_mode}
                          onChange={event => setPTUserForm(prev => ({ ...prev, package_share_mode: event.target.value }))}
                          placeholder="Opsional"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Nilai share paket</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={ptUserForm.package_share_value}
                          onChange={event => setPTUserForm(prev => ({ ...prev, package_share_value: event.target.value }))}
                          placeholder="0"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Default share sesi</Form.Label>
                        <Form.Control
                          value={ptUserForm.session_share_mode}
                          onChange={event => setPTUserForm(prev => ({ ...prev, session_share_mode: event.target.value }))}
                          placeholder="Opsional"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Nilai share sesi</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={ptUserForm.session_share_value}
                          onChange={event => setPTUserForm(prev => ({ ...prev, session_share_value: event.target.value }))}
                          placeholder="0"
                        />
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-4">
                      <Button type="submit" disabled={isSubmitting}>
                        <FaPlus className="me-2" />
                        {ptUserForm.id ? 'Update Staff' : 'Simpan Staff'}
                      </Button>
                      <Button variant="outline-secondary" onClick={resetPTUserForm} type="button">
                        Reset
                      </Button>
                    </div>
                    <div className="small text-muted mt-3">
                      Akun di tab ini dipakai PT untuk login ke halaman PT Login. Nonaktifkan akun jika PT sudah tidak bertugas.
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <strong>Daftar Session Staff</strong>
                  <Button variant="outline-secondary" onClick={fetchPTUsers}>
                    <FaRedoAlt className="me-2" />
                    Refresh
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>PT</th>
                        <th>Username</th>
                        <th>Share Default</th>
                        <th>Status</th>
                        <th>Updated</th>
                        <th className="text-end">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ptUsers.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-muted">
                            Belum ada akun PT login.
                          </td>
                        </tr>
                      ) : ptUsers.map(item => (
                        <tr key={item.id}>
                          <td>
                            <div className="fw-semibold">{item.pt_name || '-'}</div>
                            <div className="small text-muted">ID {item.id}</div>
                          </td>
                          <td>
                            <div>{item.username || '-'}</div>
                            <div className="small text-muted">Dipakai saat login PT</div>
                          </td>
                          <td>
                            <div className="small">
                              Paket: {item.package_share_mode || '-'}
                              {item.package_share_mode ? ` • ${Number(item.package_share_value || 0).toLocaleString('id-ID')}` : ''}
                            </div>
                            <div className="small text-muted">
                              Sesi: {item.session_share_mode || '-'}
                              {item.session_share_mode ? ` • ${Number(item.session_share_value || 0).toLocaleString('id-ID')}` : ''}
                            </div>
                          </td>
                          <td>
                            {renderStatusBadge(item.is_active ? 'active' : 'inactive', {
                              active: 'success',
                              inactive: 'secondary',
                            })}
                          </td>
                          <td>{formatDateTime(item.updated_at)}</td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEditPTUser(item)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDeletePTUser(item)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        {!isBootstrapping && activeTab === 'clients' ? (
          <Row className="g-4">
            <Col xl={5}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <strong>{clientForm.id ? 'Edit Session Client' : 'Tambah Session Client'}</strong>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSaveClient}>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Label>Tipe client</Form.Label>
                        <Form.Select
                          value={clientForm.client_type}
                          onChange={event => {
                            const nextType = event.target.value;
                            setClientForm(prev => ({
                              ...prev,
                              client_type: nextType,
                              member_id: '',
                              full_name: nextType === 'member' ? '' : prev.full_name,
                              phone: nextType === 'member' ? '' : prev.phone,
                              email: nextType === 'member' ? '' : prev.email,
                            }));
                            setSelectedClientMemberOption(null);
                          }}
                          disabled={!canUseMemberLookup}
                        >
                          <option value="non_member">Non Member</option>
                          {canUseMemberLookup || clientForm.client_type === 'member' ? (
                            <option value="member">Member</option>
                          ) : null}
                        </Form.Select>
                        {!canUseMemberLookup ? (
                          <div className="small text-muted mt-2">
                            Akun ini fokus untuk input client walk-in/non-member. Hubungkan ke member lewat akun yang punya akses client member.
                          </div>
                        ) : null}
                      </Col>
                      {canUseMemberLookup && clientForm.client_type === 'member' ? (
                        <>
                          <Col md={12}>
                            <Form.Label>Pilih member</Form.Label>
                            <AsyncSelect
                              cacheOptions
                              defaultOptions={memberOptions}
                              loadOptions={loadMemberOptions}
                              value={selectedClientMemberOption}
                              onChange={syncClientFormWithMember}
                              isDisabled={!selectedOutlet}
                              placeholder={selectedOutlet ? 'Cari nama, HP, email, atau identitas member' : 'Pilih outlet lebih dulu'}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue ? 'Member tidak ditemukan' : 'Ketik nama, HP, email, atau identitas member'
                              }
                              loadingMessage={() => 'Mencari member...'}
                              styles={memberSelectStyles}
                            />
                            <div className="small text-muted mt-2">
                              Nama, HP, dan email akan diambil otomatis dari member yang dipilih.
                            </div>
                          </Col>
                          <Col md={6}>
                            <Form.Label>Nama member</Form.Label>
                            <Form.Control
                              value={clientForm.full_name}
                              readOnly
                              placeholder="Pilih member terlebih dahulu"
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>No. HP</Form.Label>
                            <Form.Control
                              value={clientForm.phone}
                              readOnly
                              placeholder="-"
                            />
                          </Col>
                          <Col md={12}>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                              value={clientForm.email}
                              readOnly
                              placeholder="-"
                            />
                          </Col>
                        </>
                      ) : (
                        <>
                          <Col md={12}>
                            <Form.Label>Nama client</Form.Label>
                            <Form.Control
                              value={clientForm.full_name}
                              onChange={event => setClientForm(prev => ({ ...prev, full_name: event.target.value }))}
                              placeholder="Nama lengkap client layanan"
                              required
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>No. HP</Form.Label>
                            <Form.Control
                              value={clientForm.phone}
                              onChange={event => setClientForm(prev => ({ ...prev, phone: event.target.value }))}
                              placeholder="08xxxxxxxxxx"
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                              type="email"
                              value={clientForm.email}
                              onChange={event => setClientForm(prev => ({ ...prev, email: event.target.value }))}
                              placeholder="client@email.com"
                            />
                          </Col>
                        </>
                      )}
                      <Col md={6}>
                        <Form.Label>Status</Form.Label>
                        <Form.Check
                          type="switch"
                          label={clientForm.is_active ? 'Aktif' : 'Nonaktif'}
                          checked={clientForm.is_active}
                          onChange={event => setClientForm(prev => ({ ...prev, is_active: event.target.checked }))}
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label>Catatan</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={clientForm.notes}
                          onChange={event => setClientForm(prev => ({ ...prev, notes: event.target.value }))}
                        />
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-4">
                      <Button type="submit" disabled={isSubmitting}>
                        <FaPlus className="me-2" />
                        {clientForm.id ? 'Update Client' : 'Simpan Client'}
                      </Button>
                      <Button variant="outline-secondary" onClick={resetClientForm} type="button">
                        Reset
                      </Button>
                    </div>
                    <div className="small text-muted mt-3">
                      Pilih tipe <strong>Member</strong> untuk mengambil profil client langsung dari data member.
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <strong>Daftar Session Client</strong>
                  <div className="d-flex gap-2">
                    <Form.Control
                      value={clientSearch}
                      onChange={event => setClientSearch(event.target.value)}
                      placeholder="Cari client"
                    />
                    <Button variant="outline-secondary" onClick={fetchClients}>
                      <FaRedoAlt />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Kontak</th>
                        <th>Outlet</th>
                        <th className="text-center">Total Sesi</th>
                        <th className="text-center">Terpakai</th>
                        <th className="text-center">Sisa Sesi</th>
                        <th>Status</th>
                        <th className="text-end">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4 text-muted">
                            Belum ada data session client.
                          </td>
                        </tr>
                      ) : clients.map(item => (
                        <tr key={item.id}>
                          <td>
                            <div className="fw-semibold">{item.full_name}</div>
                            <div className="small text-muted">
                              {item.client_type === 'member' ? 'Member' : 'Non-member'}
                              {item.member_name ? ` • ${item.member_name}` : ''}
                            </div>
                          </td>
                          <td>
                            <div>{item.phone || '-'}</div>
                            <div className="small text-muted">{item.email || '-'}</div>
                          </td>
                          <td>{outletNameMap.get(String(item.outlet_id)) || `Outlet #${item.outlet_id || 0}`}</td>
                          <td className="text-center">
                            <div className="fw-semibold">{Number(item.total_sessions || 0).toLocaleString('id-ID')}</div>
                            <div className="small text-muted">akumulasi sesi</div>
                          </td>
                          <td className="text-center">
                            <div className="fw-semibold">{Number(item.used_sessions || 0).toLocaleString('id-ID')}</div>
                            <div className="small text-muted">sudah dipakai</div>
                          </td>
                          <td className="text-center">
                            <div className={`fw-semibold ${Number(item.remaining_sessions || 0) > 0 ? 'text-success' : 'text-muted'}`}>
                              {Number(item.remaining_sessions || 0).toLocaleString('id-ID')}
                            </div>
                            <div className="small text-muted">masih tersedia</div>
                          </td>
                          <td>
                            {renderStatusBadge(item.is_active ? 'active' : 'inactive', {
                              active: 'success',
                              inactive: 'secondary',
                            })}
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              {canOpenClientProgress ? (
                                <Button size="sm" variant="outline-success" onClick={() => handleOpenClientProgress(item)}>
                                  Report
                                </Button>
                              ) : null}
                              {canAdjustSessionBalance ? (
                                <Button size="sm" variant="outline-warning" onClick={() => handleOpenSessionBalanceModal(item)}>
                                  Edit sesi
                                </Button>
                              ) : null}
                              <Button size="sm" variant="outline-primary" onClick={() => handleEditClient(item)}>
                                Edit
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDeleteClient(item)}>
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Modal
              show={showSessionBalanceModal}
              onHide={handleCloseSessionBalanceModal}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  Edit Sesi
                  {sessionBalanceClient?.full_name ? ` • ${sessionBalanceClient.full_name}` : ''}
                </Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleSubmitSessionBalanceAdjustment}>
                <Modal.Body>
                  {isSessionBalanceLoading ? (
                    <div className="text-center py-4 text-muted">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Memuat detail paket sesi...
                    </div>
                  ) : sessionBalancePackages.length === 0 ? (
                    <div className="text-muted">
                      Client ini belum punya paket sesi yang bisa dikoreksi.
                    </div>
                  ) : (
                    <>
                      <div className="small text-muted mb-3">
                        Koreksi ini dipakai untuk membenahi sesi dobel atau salah saldo. Contract fase baru akan menulis ledger adjustment, sedangkan package legacy akan disesuaikan total sesinya.
                      </div>
                      <Form.Group className="mb-3">
                        <Form.Label>Pilih paket / kontrak</Form.Label>
                        <Form.Select
                          value={sessionBalanceForm.package_key}
                          onChange={event => {
                            const nextPackage = sessionBalancePackages.find(
                              item => buildSessionBalancePackageKey(item) === event.target.value
                            ) || null;
                            applySessionBalancePackageSelection(nextPackage);
                          }}
                        >
                          {sessionBalancePackages.map(item => (
                            <option key={buildSessionBalancePackageKey(item)} value={buildSessionBalancePackageKey(item)}>
                              {formatSessionBalancePackageLabel(item)}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      {selectedSessionBalancePackage ? (
                        <Row className="g-3 mb-3">
                          <Col md={4}>
                            <div className="border rounded-3 p-3 h-100">
                              <div className="small text-muted">Terpakai saat ini</div>
                              <div className="fw-semibold fs-5">
                                {Number(selectedSessionBalancePackage.used_sessions || 0).toLocaleString('id-ID')}
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="border rounded-3 p-3 h-100">
                              <div className="small text-muted">Sisa saat ini</div>
                              <div className="fw-semibold fs-5 text-success">
                                {Number(selectedSessionBalancePackage.remaining_sessions || 0).toLocaleString('id-ID')}
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="border rounded-3 p-3 h-100">
                              <div className="small text-muted">Status paket</div>
                              <div className="fw-semibold text-capitalize">
                                {selectedSessionBalancePackage.status || '-'}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      ) : null}
                      <Form.Group className="mb-3">
                        <Form.Label>Total sesi baru</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          value={sessionBalanceForm.total_sessions}
                          onChange={event => setSessionBalanceForm(prev => ({
                            ...prev,
                            total_sessions: event.target.value,
                          }))}
                          placeholder="Contoh: 10"
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Catatan koreksi</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={sessionBalanceForm.notes}
                          onChange={event => setSessionBalanceForm(prev => ({
                            ...prev,
                            notes: event.target.value,
                          }))}
                          placeholder="Contoh: koreksi sesi dobel saat payment PT"
                        />
                      </Form.Group>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="outline-secondary" type="button" onClick={handleCloseSessionBalanceModal}>
                    Tutup
                  </Button>
                  <Button
                    variant="warning"
                    type="submit"
                    disabled={isSessionBalanceLoading || isSessionBalanceSaving || sessionBalancePackages.length === 0}
                  >
                    {isSessionBalanceSaving ? 'Menyimpan...' : 'Simpan koreksi sesi'}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

          </Row>
        ) : null}

        <Modal
          show={showContractEditModal}
          onHide={handleCloseContractEditModal}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Edit Contract
              {contractEditForm.contract_no ? ` • ${contractEditForm.contract_no}` : ''}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmitContractEdit}>
            <Modal.Body>
              <div className="small text-muted mb-3">
                Gunakan form ini untuk menutup contract aktif ganda, membetulkan periode contract, dan mengoreksi sisa sesi yang masih tersedia.
              </div>
              {contractEditConflictingPackages.length > 0 ? (
                <div className="border border-warning rounded-3 p-3 mb-3">
                  <div className="fw-semibold mb-1">Masih ada contract active / frozen lain</div>
                  <div className="small text-muted">
                    Sebaiknya contract lama diubah ke completed, expired, cancelled, atau frozen sesuai kondisi supaya saldo client tidak terbaca dari paket yang salah.
                  </div>
                  <div className="small mt-2">
                    {contractEditConflictingPackages.slice(0, 3).map(item => (
                      <div key={`conflict-${item.contract_id || item.id}`}>
                        {(item.contract_no || item.product_name || 'Contract PT')}
                        {item.status ? ` • ${String(item.status).toUpperCase()}` : ''}
                      </div>
                    ))}
                    {contractEditConflictingPackages.length > 3 ? (
                      <div>+{contractEditConflictingPackages.length - 3} contract lain</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <div className="border rounded-3 p-3 h-100">
                    <div className="small text-muted">Paket</div>
                    <div className="fw-semibold">{contractEditForm.product_name || 'PT Session'}</div>
                    <div className="small text-muted">{contractEditForm.contract_no || '-'}</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="border rounded-3 p-3 h-100">
                    <div className="small text-muted">PT</div>
                    <div className="fw-semibold">{contractEditForm.pt_name || '-'}</div>
                    <div className="small text-muted">
                      {Number(contractEditForm.used_sessions || 0).toLocaleString('id-ID')}
                      {' / '}
                      {Number(contractEditComputedTotalSessions || 0).toLocaleString('id-ID')} sesi
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="border rounded-3 p-3 h-100">
                    <div className="small text-muted">Sisa sesi tersedia</div>
                    <div className="fw-semibold fs-5 text-success">
                      {Number(contractEditForm.remaining_sessions || 0).toLocaleString('id-ID')}
                    </div>
                    <div className="small text-muted">Total contract saat ini</div>
                    <div>{Number(contractEditForm.total_sessions || 0).toLocaleString('id-ID')} sesi</div>
                    <div className="small text-muted mt-2">Status sekarang</div>
                    <div>{renderContractStatusBadge(contractEditForm.status)}</div>
                  </div>
                </Col>
              </Row>
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Status contract</Form.Label>
                    <Form.Select
                      value={contractEditForm.status}
                      onChange={event => setContractEditForm(prev => ({
                        ...prev,
                        status: event.target.value,
                      }))}
                    >
                      {PT_CONTRACT_STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Sisa sesi tersedia</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      value={contractEditForm.remaining_sessions}
                      onChange={event => setContractEditForm(prev => ({
                        ...prev,
                        remaining_sessions: event.target.value,
                      }))}
                    />
                    <Form.Text className="text-muted">
                      Total contract baru otomatis menjadi {Number(contractEditComputedTotalSessions || 0).toLocaleString('id-ID')} sesi.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Mulai contract</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={contractEditForm.start_at}
                      onChange={event => setContractEditForm(prev => ({
                        ...prev,
                        start_at: event.target.value,
                      }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Expire contract</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={contractEditForm.expire_at}
                      onChange={event => setContractEditForm(prev => ({
                        ...prev,
                        expire_at: event.target.value,
                      }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mt-3">
                <Form.Label>Catatan</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={contractEditForm.notes}
                  onChange={event => setContractEditForm(prev => ({
                    ...prev,
                    notes: event.target.value,
                  }))}
                  placeholder="Contoh: contract lama ditutup supaya hanya paket terbaru yang active"
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" type="button" onClick={handleCloseContractEditModal}>
                Tutup
              </Button>
              <Button variant="primary" type="submit" disabled={isContractEditSaving}>
                {isContractEditSaving ? 'Menyimpan...' : 'Simpan contract'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {!isBootstrapping && activeTab === 'progress' ? (
          <Row className="g-4">
            <Col xl={4}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <strong>Report Client</strong>
                    <div className="small text-muted mt-1">Diurutkan dari sisa sesi paling sedikit agar follow up lebih cepat.</div>
                  </div>
                  <div className="d-flex gap-2">
                    <Form.Control
                      value={clientProgressSearch}
                      onChange={event => setClientProgressSearch(event.target.value)}
                      placeholder="Cari client"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        fetchClients({ search: '' }).catch(error => {
                          Swal.fire('Error', error.message || 'Gagal mengambil daftar session client', 'error');
                        });
                      }}
                    >
                      <FaRedoAlt />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th className="text-center">Sisa</th>
                        <th className="text-end">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProgressClients.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-4 text-muted">
                            Tidak ada client yang cocok dengan pencarian.
                          </td>
                        </tr>
                      ) : filteredProgressClients.map(item => (
                        <tr
                          key={item.id}
                          className={String(selectedProgressClientId) === String(item.id) ? 'table-active' : ''}
                        >
                          <td>
                            <div className="fw-semibold">{item.full_name}</div>
                            <div className="small text-muted">
                              {item.member_name || item.phone || item.email || 'Tanpa kontak'}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="fw-semibold">{Number(item.remaining_sessions || 0).toLocaleString('id-ID')}</div>
                            <div className="small text-muted">sesi</div>
                          </td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-success" onClick={() => handleOpenClientProgress(item)}>
                              Lihat
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={8}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <strong>
                      {clientProgressReport?.client?.full_name
                        ? `Progress Client • ${clientProgressReport.client.full_name}`
                        : selectedProgressClient?.full_name
                          ? `Progress Client • ${selectedProgressClient.full_name}`
                          : 'Progress Client'}
                    </strong>
                    <div className="small text-muted mt-1">
                      Ringkasan progres sesi, paket aktif, dan catatan PT per kehadiran.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={!selectedProgressClientId || isClientProgressLoading}
                      onClick={() => {
                        fetchClientProgressReport(selectedProgressClientId).catch(error => {
                          Swal.fire('Error', error.message || 'Gagal mengambil report progress client', 'error');
                        });
                      }}
                    >
                      <FaRedoAlt className="me-2" />
                      Refresh
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!clientProgressReport || isClientProgressLoading}
                      onClick={handleCopyClientProgress}
                    >
                      <FaCopy className="me-2" />
                      Copy Follow Up
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      disabled={!clientProgressReport || isClientProgressLoading}
                      onClick={handleSendClientProgress}
                    >
                      <FaPaperPlane className="me-2" />
                      Kirim via WhatsApp
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {!selectedProgressClientId ? (
                    <div className="text-center py-5 text-muted">
                      Pilih salah satu client dari panel kiri untuk melihat report progress dan catatan PT.
                    </div>
                  ) : isClientProgressLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" />
                      <div className="mt-3">Memuat report progress client...</div>
                    </div>
                  ) : !clientProgressReport ? (
                    <div className="text-center py-5 text-muted">
                      Report progress client belum tersedia. Tekan refresh untuk mencoba lagi.
                    </div>
                  ) : (
                    <>
                      <div className="pt-phase15-goal-hero mb-4">
                        <div className="pt-phase15-goal-hero-copy">
                          <div className="pt-phase15-goal-kicker">
                            {formatGoalTypeLabel(clientGoalWorkspace?.goal_profile?.primary_goal || 'fat_loss')}
                          </div>
                          <h3>{clientGoalHeroClient?.full_name || 'Client PT'}</h3>
                          <p>
                            Admin bisa membaca pace target, hasil measurement terbaru, dan rekomendasi AI PT dalam satu dashboard.
                          </p>
                          <div className="pt-phase15-goal-chip-row">
                            <span className="pt-phase15-goal-chip">
                              Sisa sesi
                              <strong>{Number(clientProgressReport.summary?.remaining_sessions || 0).toLocaleString('id-ID')}</strong>
                            </span>
                            <span className="pt-phase15-goal-chip">
                              Completed 28 hari
                              <strong>{Number(clientGoalWorkspaceSummary?.recent_completed_sessions || 0).toLocaleString('id-ID')}</strong>
                            </span>
                            <span className="pt-phase15-goal-chip">
                              No-show 28 hari
                              <strong>{Number(clientGoalWorkspaceSummary?.recent_no_show_sessions || 0).toLocaleString('id-ID')}</strong>
                            </span>
                            <span className="pt-phase15-goal-chip">
                              Target date
                              <strong>{clientGoalWorkspace?.goal_profile?.target_date ? formatDateTime(clientGoalWorkspace.goal_profile.target_date) : '-'}</strong>
                            </span>
                            <span className="pt-phase15-goal-chip">
                              Measurement terakhir
                              <strong>{clientGoalWorkspace?.latest_measurement?.measured_at ? formatDateTime(clientGoalWorkspace.latest_measurement.measured_at) : '-'}</strong>
                            </span>
                          </div>
                        </div>
                        <div className="pt-phase15-goal-score">
                          <span>Overall progress</span>
                          <strong>{formatGoalMetricValue(clientGoalWorkspaceSummary?.overall_progress_percent, 1, '%')}</strong>
                          <div className={`pt-phase15-goal-score-status is-${getGoalStatusTone(clientGoalWorkspaceSummary?.goal_status)}`}>
                            {clientGoalWorkspaceSummary?.goal_status_label || 'Belum ada status'}
                          </div>
                          <div className="pt-phase15-goal-score-bar">
                            <span style={{ width: `${clampPercentage(clientGoalWorkspaceSummary?.overall_progress_percent)}%` }} />
                          </div>
                        </div>
                      </div>

                      <Row className="g-3 mb-4">
                        {clientGoalFocusMeters.map(item => (
                          <Col md={6} xl={4} key={item.key}>
                            <div className="pt-phase15-goal-focus-card h-100">
                              <div className="pt-phase15-goal-focus-head">
                                <span>{item.label}</span>
                                <strong>{item.valueLabel}</strong>
                              </div>
                              <div className="pt-phase15-goal-progress-bar">
                                <span style={{ width: `${item.value}%` }} />
                              </div>
                              <small>{item.note}</small>
                            </div>
                          </Col>
                        ))}
                      </Row>

                      <PTGoalTrendChart
                        workspace={clientGoalWorkspace}
                        metricKey={activeGoalTrendMetric}
                        onMetricChange={setClientGoalTrendMetric}
                      />

                      <div className="pt-phase15-goal-detail-tabs mt-4 mb-4">
                        <button
                          type="button"
                          className={`pt-phase15-goal-detail-tab ${clientProgressDetailTab === 'overview' ? 'is-active' : ''}`}
                          onClick={() => setClientProgressDetailTab('overview')}
                        >
                          Overview
                        </button>
                        <button
                          type="button"
                          className={`pt-phase15-goal-detail-tab ${clientProgressDetailTab === 'goal-ai' ? 'is-active' : ''}`}
                          onClick={() => setClientProgressDetailTab('goal-ai')}
                        >
                          Goal & AI
                        </button>
                        <button
                          type="button"
                          className={`pt-phase15-goal-detail-tab ${clientProgressDetailTab === 'history' ? 'is-active' : ''}`}
                          onClick={() => setClientProgressDetailTab('history')}
                        >
                          Riwayat
                        </button>
                      </div>

                      {clientProgressDetailTab === 'overview' ? (
                        <>
                          <Card className="bg-light border-0 mb-4">
                            <Card.Body>
                              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                                <div>
                                  <div className="fw-semibold">Draft follow up siap copy</div>
                                  <div className="small text-muted">
                                    Disusun dari overview progress, sisa sesi, measurement terakhir, dan rekomendasi AI.
                                  </div>
                                </div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  disabled={!clientProgressShareText}
                                  onClick={handleCopyClientProgress}
                                >
                                  <FaCopy className="me-2" />
                                  Copy Follow Up
                                </Button>
                              </div>
                              <Form.Control
                                as="textarea"
                                rows={12}
                                readOnly
                                value={clientProgressShareText}
                                className="pt-phase15-followup-textarea"
                              />
                            </Card.Body>
                          </Card>

                          <Row className="g-3 mb-4">
                            {clientGoalOverviewCards.map(item => (
                              <Col md={6} xl={4} key={item.key}>
                                <div className={`pt-phase15-goal-overview-card h-100 is-${item.tone}`}>
                                  <div className="small text-muted">{item.label}</div>
                                  <div className="fs-5 fw-semibold">{item.value}</div>
                                </div>
                              </Col>
                            ))}
                          </Row>

                          <div className="pt-phase15-goal-metric-grid mb-4">
                            {clientGoalWorkspaceMetrics.length ? clientGoalWorkspaceMetrics.map(item => (
                              <div key={item.key || item.label} className="pt-phase15-goal-metric-card">
                                <div className="pt-phase15-goal-metric-head">
                                  <strong>{item.label || '-'}</strong>
                                  <span>{formatGoalMetricValue(item.progress_percent, 1, '%')}</span>
                                </div>
                                <div className="pt-phase15-goal-progress-bar">
                                  <span style={{ width: `${clampPercentage(item.progress_percent)}%` }} />
                                </div>
                                <div className="pt-phase15-goal-metric-meta">
                                  <span>Awal: {formatGoalMetricValue(item.start_value, 1, item.unit || '')}</span>
                                  <span>Sekarang: {formatGoalMetricValue(item.current_value, 1, item.unit || '')}</span>
                                  <span>Target: {formatGoalMetricValue(item.target_value, 1, item.unit || '')}</span>
                                </div>
                              </div>
                            )) : (
                              <div className="text-muted">Belum ada metric progress yang bisa dihitung.</div>
                            )}
                          </div>

                          <Row className="g-3">
                            <Col lg={6}>
                              <div className="pt-phase15-ai-note-card h-100">
                                <div className="pt-phase15-ai-note-title">Rekomendasi AI</div>
                                {clientGoalWorkspaceSuggestions.length ? (
                                  <ul className="pt-phase15-ai-note-list mb-0">
                                    {clientGoalWorkspaceSuggestions.map((item, index) => (
                                      <li key={`ai-suggestion-${index}`}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-muted">Belum ada rekomendasi tambahan untuk client ini.</div>
                                )}
                              </div>
                            </Col>
                            <Col lg={6}>
                              <div className="pt-phase15-ai-note-card h-100 is-warning">
                                <div className="pt-phase15-ai-note-title">Alert & hambatan</div>
                                {clientGoalWorkspaceAlerts.length ? (
                                  <ul className="pt-phase15-ai-note-list mb-0">
                                    {clientGoalWorkspaceAlerts.map((item, index) => (
                                      <li key={`ai-alert-${index}`}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <div className="text-muted">Belum ada alert utama. Kondisi client relatif stabil.</div>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </>
                      ) : null}

                      {clientProgressDetailTab === 'goal-ai' ? (
                        <>
                          <Row className="g-3 mb-4">
                            {clientGoalMeasurementHighlights.map(item => (
                              <Col md={6} xl={3} key={item.key}>
                                <div className="pt-phase15-goal-overview-card h-100">
                                  <div className="small text-muted">{item.label}</div>
                                  <div className="fs-5 fw-semibold">{item.value}</div>
                                  <div className="small text-muted mt-2">{item.helper}</div>
                                </div>
                              </Col>
                            ))}
                          </Row>

                          <Card className="bg-light border-0 mb-4">
                            <Card.Body>
                              <div className="fw-semibold mb-3">Target goal & measurement tracking</div>
                              <Row className="g-3">
                                <Col md={6}>
                                  <div className="pt-phase15-goal-detail-card">
                                    <div className="small text-muted">Goal utama</div>
                                    <div className="fw-semibold">{formatGoalTypeLabel(clientGoalWorkspace?.goal_profile?.primary_goal || 'fat_loss')}</div>
                                    <div className="small text-muted mt-3">Target date</div>
                                    <div className="fw-semibold">{clientGoalWorkspace?.goal_profile?.target_date ? formatDateTime(clientGoalWorkspace.goal_profile.target_date) : '-'}</div>
                                    <div className="small text-muted mt-3">Target sesi / minggu</div>
                                    <div className="fw-semibold">{Number(clientGoalWorkspace?.goal_profile?.weekly_training_target || 0).toLocaleString('id-ID')}</div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className="pt-phase15-goal-detail-card">
                                    <div className="small text-muted">Target nutrisi & recovery</div>
                                    <div className="fw-semibold">
                                      Kalori {Number(clientGoalWorkspace?.goal_profile?.daily_calorie_target || 0).toLocaleString('id-ID')} kkal
                                    </div>
                                    <div className="small text-muted mt-2">
                                      Protein {formatGoalMetricValue(clientGoalWorkspace?.goal_profile?.daily_protein_target_g, 0, 'g')} •
                                      {' '}Langkah {Number(clientGoalWorkspace?.goal_profile?.daily_step_target || 0).toLocaleString('id-ID')} •
                                      {' '}Tidur {formatGoalMetricValue(clientGoalWorkspace?.goal_profile?.sleep_target_hours, 1, 'jam')}
                                    </div>
                                    <div className="small text-muted mt-3">Catatan coach</div>
                                    <div>{clientGoalWorkspace?.goal_profile?.notes || '-'}</div>
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>

                          <div className="pt-phase15-goal-metric-grid">
                            {clientGoalWorkspaceMetrics.length ? clientGoalWorkspaceMetrics.map(item => (
                              <div key={`goal-metric-${item.key || item.label}`} className="pt-phase15-goal-metric-card">
                                <div className="pt-phase15-goal-metric-head">
                                  <strong>{item.label || '-'}</strong>
                                  <span>{formatGoalMetricValue(item.progress_percent, 1, '%')}</span>
                                </div>
                                <div className="pt-phase15-goal-progress-bar">
                                  <span style={{ width: `${clampPercentage(item.progress_percent)}%` }} />
                                </div>
                                <div className="pt-phase15-goal-metric-meta">
                                  <span>Awal: {formatGoalMetricValue(item.start_value, 1, item.unit || '')}</span>
                                  <span>Sekarang: {formatGoalMetricValue(item.current_value, 1, item.unit || '')}</span>
                                  <span>Target: {formatGoalMetricValue(item.target_value, 1, item.unit || '')}</span>
                                </div>
                              </div>
                            )) : (
                              <div className="text-muted">Belum ada metric progress yang bisa ditampilkan.</div>
                            )}
                          </div>
                        </>
                      ) : null}

                      {clientProgressDetailTab === 'history' ? (
                        <>
                          <Card className="bg-light border-0 mb-4">
                            <Card.Body>
                              <div className="fw-semibold mb-3">Riwayat measurement</div>
                              <Table responsive className="mb-0 align-middle">
                                <thead>
                                  <tr>
                                    <th>Waktu</th>
                                    <th>Berat</th>
                                    <th>Body Fat</th>
                                    <th>Waist</th>
                                    <th>Muscle</th>
                                    <th>Kalori</th>
                                    <th>Protein</th>
                                    <th>Sleep</th>
                                    <th>Steps</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {clientGoalWorkspace?.recent_measurements?.length ? clientGoalWorkspace.recent_measurements.map(item => (
                                    <tr key={`measurement-${item.id || item.measured_at}`}>
                                      <td>{formatDateTime(item.measured_at)}</td>
                                      <td>{formatGoalMetricValue(item.body_weight_kg, 1, 'kg')}</td>
                                      <td>{formatGoalMetricValue(item.body_fat_percent, 1, '%')}</td>
                                      <td>{formatGoalMetricValue(item.waist_cm, 1, 'cm')}</td>
                                      <td>{formatGoalMetricValue(item.muscle_mass_kg, 1, 'kg')}</td>
                                      <td>{formatGoalMetricValue(item.calories_avg, 0, '')}</td>
                                      <td>{formatGoalMetricValue(item.protein_g_avg, 0, 'g')}</td>
                                      <td>{formatGoalMetricValue(item.sleep_hours_avg, 1, 'jam')}</td>
                                      <td>{formatGoalMetricValue(item.steps_avg, 0, '')}</td>
                                    </tr>
                                  )) : (
                                    <tr>
                                      <td colSpan={9} className="text-center py-4 text-muted">
                                        Belum ada riwayat measurement untuk client ini.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>

                          <Card className="bg-light border-0 mb-4">
                            <Card.Body>
                              <div className="fw-semibold mb-3">Paket & kontrak client</div>
                              <Table responsive className="mb-0 align-middle">
                                <thead>
                                  <tr>
                                    <th>Paket</th>
                                    <th>PT</th>
                                    <th>Progress</th>
                                    <th>Status</th>
                                    <th>Catatan</th>
                                    {canAdjustSessionBalance ? <th className="text-end">Aksi</th> : null}
                                  </tr>
                                </thead>
                                <tbody>
                                  {clientProgressReport.packages?.length ? clientProgressReport.packages.map(item => (
                                    <tr key={`${item.source}-${item.id}`}>
                                      <td>
                                        <div className="fw-semibold">{item.product_name || 'PT Session'}</div>
                                        <div className="small text-muted">
                                          {item.contract_no || item.invoice_no || 'Tanpa nomor dokumen'}
                                        </div>
                                        <div className="small text-muted">
                                          {formatDateTime(item.start_at)}
                                          {item.expire_at ? ` • Exp ${formatDateTime(item.expire_at)}` : ''}
                                        </div>
                                      </td>
                                      <td>
                                        <div>{item.pt_name || '-'}</div>
                                        <div className="small text-muted">
                                          {item.source === 'legacy_package' ? 'Legacy package' : 'Contract phase 2'}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fw-semibold">
                                          {Number(item.used_sessions || 0).toLocaleString('id-ID')}
                                          {' / '}
                                          {Number(item.total_sessions || 0).toLocaleString('id-ID')}
                                        </div>
                                        <div className="small text-muted">
                                          Sisa {Number(item.remaining_sessions || 0).toLocaleString('id-ID')} sesi
                                        </div>
                                      </td>
                                      <td>
                                        {item.source === 'legacy_package'
                                          ? renderStatusBadge(item.status, { active: 'success', inactive: 'secondary' })
                                          : renderContractStatusBadge(item.status)}
                                      </td>
                                      <td className="small text-muted">{item.notes || '-'}</td>
                                      {canAdjustSessionBalance ? (
                                        <td className="text-end">
                                          {item.source === 'legacy_package' ? (
                                            <span className="text-muted small">-</span>
                                          ) : (
                                            <Button
                                              size="sm"
                                              variant="outline-secondary"
                                              className="d-inline-flex align-items-center gap-2"
                                              onClick={() => handleOpenContractEditModal(item)}
                                            >
                                              <FaEdit />
                                              Edit contract
                                            </Button>
                                          )}
                                        </td>
                                      ) : null}
                                    </tr>
                                  )) : (
                                    <tr>
                                      <td colSpan={canAdjustSessionBalance ? 6 : 5} className="text-center py-4 text-muted">
                                        Belum ada paket atau kontrak client.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>

                          <Card className="bg-light border-0">
                            <Card.Body>
                              <div className="fw-semibold mb-3">Riwayat kehadiran & catatan PT</div>
                              <Table responsive className="mb-0 align-middle">
                                <thead>
                                  <tr>
                                    <th>Waktu</th>
                                    <th>PT / Paket</th>
                                    <th>Status</th>
                                    <th>Catatan PT</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {clientProgressReport.attendances?.length ? clientProgressReport.attendances.map(item => (
                                    <tr key={`${item.source}-${item.source_id}`}>
                                      <td>
                                        <div className="fw-semibold">{formatDateTime(item.start_at)}</div>
                                        <div className="small text-muted">
                                          {item.source === 'legacy_schedule' ? 'Legacy schedule' : item.booking_code || 'Phase 2 booking'}
                                        </div>
                                      </td>
                                      <td>
                                        <div>{item.pt_name || '-'}</div>
                                        <div className="small text-muted">
                                          {item.product_name || '-'}
                                          {item.contract_no ? ` • ${item.contract_no}` : ''}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex flex-wrap gap-2">
                                          {renderProgressAttendanceBadge(item.attendance_status)}
                                          {renderProgressBookingBadge(item.booking_status)}
                                        </div>
                                      </td>
                                      <td>
                                        <div>{item.notes || '-'}</div>
                                        <div className="small text-muted mt-1">
                                          {formatClientProgressSessionStatus(item)}
                                        </div>
                                      </td>
                                    </tr>
                                  )) : (
                                    <tr>
                                      <td colSpan={4} className="text-center py-4 text-muted">
                                        Belum ada riwayat kehadiran untuk client ini.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>
                        </>
                      ) : null}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        {!isBootstrapping && activeTab === 'whatsapp-admin' ? (
          <Row className="g-4">
            <Col xl={4}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <strong>Pengaturan OpenClaw</strong>
                </Card.Header>
                <Card.Body>
                  {!hasSelectedOutletForOpenClaw ? (
                    <div className="text-muted">
                      Pilih outlet terlebih dahulu agar pengaturan WhatsApp dan inbox OpenClaw bisa dibaca per outlet.
                    </div>
                  ) : (
                    <Form onSubmit={handleSaveOpenClawSettings}>
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Check
                            type="switch"
                            label={openClawSettingsForm.is_enabled ? 'OpenClaw aktif' : 'OpenClaw nonaktif'}
                            checked={openClawSettingsForm.is_enabled}
                            onChange={event => setOpenClawSettingsForm(prev => ({
                              ...prev,
                              is_enabled: event.target.checked,
                            }))}
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                          <div className="small text-muted mt-2">
                            Saat aktif, admin bisa balas WhatsApp dari inbox ini dan auto-send report progress PT akan menggunakan OpenClaw.
                          </div>
                        </Col>
                        <Col md={12}>
                          <Form.Check
                            type="switch"
                            label={openClawSettingsForm.auto_send_pt_completed ? 'Auto-send report saat PT completed aktif' : 'Auto-send report saat PT completed nonaktif'}
                            checked={openClawSettingsForm.auto_send_pt_completed}
                            onChange={event => setOpenClawSettingsForm(prev => ({
                              ...prev,
                              auto_send_pt_completed: event.target.checked,
                            }))}
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                        </Col>
                        <Col md={12}>
                          <Form.Label>Binary path</Form.Label>
                          <Form.Control
                            value={openClawSettingsForm.openclaw_bin}
                            onChange={event => setOpenClawSettingsForm(prev => ({ ...prev, openclaw_bin: event.target.value }))}
                            placeholder="openclaw"
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Channel</Form.Label>
                          <Form.Control
                            value={openClawSettingsForm.channel}
                            onChange={event => setOpenClawSettingsForm(prev => ({ ...prev, channel: event.target.value }))}
                            placeholder="whatsapp"
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Account ID</Form.Label>
                          <Form.Control
                            value={openClawSettingsForm.account_id}
                            onChange={event => setOpenClawSettingsForm(prev => ({ ...prev, account_id: event.target.value }))}
                            placeholder="opsional"
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                        </Col>
                        <Col md={12}>
                          <Form.Label>Control URL</Form.Label>
                          <Form.Control
                            value={openClawSettingsForm.control_url}
                            onChange={event => setOpenClawSettingsForm(prev => ({ ...prev, control_url: event.target.value }))}
                            placeholder="https://chat.zenter.co.id"
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                        </Col>
                        <Col md={12}>
                          <Form.Label>Webhook secret</Form.Label>
                          <Form.Control
                            value={openClawSettingsForm.webhook_secret}
                            onChange={event => setOpenClawSettingsForm(prev => ({ ...prev, webhook_secret: event.target.value }))}
                            placeholder="secret untuk sync inbox"
                            disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                          />
                        </Col>
                      </Row>

                      <div className="d-flex gap-2 mt-4">
                        <Button type="submit" disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}>
                          <FaCheckCircle className="me-2" />
                          {isOpenClawSettingsSaving ? 'Menyimpan...' : 'Simpan Settings'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline-secondary"
                          onClick={() => {
                            fetchOpenClawSettings().catch(error => {
                              Swal.fire('Error', error.message || 'Gagal mengambil pengaturan OpenClaw', 'error');
                            });
                          }}
                          disabled={isOpenClawSettingsLoading || isOpenClawSettingsSaving}
                        >
                          <FaRedoAlt className="me-2" />
                          Refresh
                        </Button>
                      </div>

                      <div className="small text-muted mt-4">
                        Sync URL inbox WhatsApp:
                      </div>
                      <code className="d-block mt-2 p-2 rounded-3 bg-light text-break">{openClawSyncURL}</code>
                      <div className="small text-muted mt-3">
                        Pasang hook OpenClaw dari folder <strong>ops/openclaw/zenter-sync-hook</strong> pada server aplikasi, lalu isi outlet id dan secret yang sama agar pesan masuk bisa muncul di inbox ini.
                      </div>
                      {openClawSettingsForm.control_url ? (
                        <div className="mt-3">
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => window.open(openClawSettingsForm.control_url, '_blank', 'noopener,noreferrer')}
                          >
                            Buka Dashboard OpenClaw
                          </Button>
                        </div>
                      ) : null}
                    </Form>
                  )}
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <strong>Inbox WhatsApp</strong>
                  <Badge bg="light" text="dark">
                    {totalOpenClawUnread.toLocaleString('id-ID')} unread
                  </Badge>
                </Card.Header>
                <Card.Body>
                  {!hasSelectedOutletForOpenClaw ? (
                    <div className="text-muted">
                      Pilih outlet terlebih dahulu untuk melihat daftar percakapan WhatsApp.
                    </div>
                  ) : (
                    <>
                      <div className="d-flex gap-2 mb-3">
                        <Form.Control
                          value={openClawConversationSearch}
                          onChange={event => setOpenClawConversationSearch(event.target.value)}
                          placeholder="Cari nama, nomor, atau isi chat"
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => {
                            fetchOpenClawConversations().catch(error => {
                              Swal.fire('Error', error.message || 'Gagal mengambil inbox WhatsApp', 'error');
                            });
                          }}
                          disabled={isOpenClawInboxLoading}
                        >
                          <FaRedoAlt />
                        </Button>
                      </div>
                      <div className="pt-phase15-openclaw-list">
                        {isOpenClawInboxLoading ? (
                          <div className="text-center py-4 text-muted">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Memuat inbox WhatsApp...
                          </div>
                        ) : openClawConversations.length === 0 ? (
                          <div className="text-center py-4 text-muted">
                            Belum ada percakapan WhatsApp yang tersinkron.
                          </div>
                        ) : openClawConversations.map(item => {
                          const isActive = item.conversation_key === selectedOpenClawConversationKey;
                          return (
                            <button
                              key={item.conversation_key}
                              type="button"
                              className={`pt-phase15-openclaw-conversation ${isActive ? 'is-active' : ''}`}
                              onClick={() => handleSelectOpenClawConversation(item)}
                            >
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div>
                                  <div className="fw-semibold">
                                    {item.participant_name || item.client_name || item.phone || 'Percakapan WhatsApp'}
                                  </div>
                                  <div className="small text-muted">
                                    {item.client_name && item.participant_name && item.client_name !== item.participant_name
                                      ? `${item.client_name} • `
                                      : ''}
                                    {item.phone || 'Tanpa nomor'}
                                  </div>
                                </div>
                                {Number(item.unread_count || 0) > 0 ? (
                                  <Badge bg="warning" text="dark" pill>
                                    {Number(item.unread_count || 0).toLocaleString('id-ID')}
                                  </Badge>
                                ) : null}
                              </div>
                              <div className="small text-muted mt-2">{item.last_message_preview || '-'}</div>
                              <div className="small text-muted mt-2">
                                {formatDateTime(item.last_occurred_at)} • {item.channel || 'whatsapp'}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={8}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <strong>
                      {selectedOpenClawConversation
                        ? (selectedOpenClawConversation.participant_name || selectedOpenClawConversation.client_name || selectedOpenClawConversation.phone || 'Percakapan WhatsApp')
                        : 'Thread WhatsApp'}
                    </strong>
                    <div className="small text-muted mt-1">
                      {selectedOpenClawConversation
                        ? `${selectedOpenClawConversation.phone || 'Tanpa nomor'}${selectedOpenClawConversation.client_name ? ` • Client: ${selectedOpenClawConversation.client_name}` : ''}`
                        : 'Pilih salah satu percakapan dari panel kiri untuk membaca dan membalas pesan client.'}
                    </div>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!selectedOpenClawConversationKey || isOpenClawMessagesLoading}
                    onClick={() => {
                      fetchOpenClawMessages(selectedOpenClawConversationKey, { markRead: false }).catch(error => {
                        Swal.fire('Error', error.message || 'Gagal mengambil percakapan WhatsApp', 'error');
                      });
                    }}
                  >
                    <FaRedoAlt className="me-2" />
                    Refresh Thread
                  </Button>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                  {!hasSelectedOutletForOpenClaw ? (
                    <div className="text-center py-5 text-muted">
                      Pilih outlet terlebih dahulu untuk mengaktifkan inbox WhatsApp.
                    </div>
                  ) : !selectedOpenClawConversation ? (
                    <div className="text-center py-5 text-muted">
                      Belum ada percakapan yang dipilih.
                    </div>
                  ) : (
                    <>
                      <div className="pt-phase15-openclaw-thread flex-grow-1 mb-4">
                        {isOpenClawMessagesLoading ? (
                          <div className="text-center py-5 text-muted">
                            <Spinner animation="border" className="me-2" />
                            Memuat thread WhatsApp...
                          </div>
                        ) : openClawMessages.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            Belum ada pesan pada percakapan ini.
                          </div>
                        ) : openClawMessages.map(item => {
                          const isOutbound = item.direction === 'outbound';
                          return (
                            <div
                              key={item.id}
                              className={`pt-phase15-openclaw-message ${isOutbound ? 'is-outbound' : 'is-inbound'}`}
                            >
                              <div className="pt-phase15-openclaw-bubble">
                                <div className="small text-muted mb-2">
                                  {isOutbound ? (item.actor_name || 'Admin') : (item.participant_name || item.client_name || item.phone || 'Client')}
                                  {' • '}
                                  {formatDateTime(item.occurred_at)}
                                </div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{item.body || '-'}</div>
                                <div className="small text-muted mt-2">
                                  {item.delivery_status || (isOutbound ? 'sent' : 'received')}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <Card className="bg-light border-0">
                        <Card.Body>
                          <Form onSubmit={handleSendOpenClawReply}>
                            <Form.Label>Balas client</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={openClawReplyBody}
                              onChange={event => setOpenClawReplyBody(event.target.value)}
                              placeholder="Tulis balasan admin ke WhatsApp client..."
                              disabled={isOpenClawReplySending}
                            />
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                              <div className="small text-muted">
                                Balasan akan dikirim lewat OpenClaw dan langsung tercatat di thread ini.
                              </div>
                              <Button type="submit" disabled={isOpenClawReplySending || !String(openClawReplyBody || '').trim()}>
                                <FaPaperPlane className="me-2" />
                                {isOpenClawReplySending ? 'Mengirim...' : 'Kirim Balasan'}
                              </Button>
                            </div>
                          </Form>
                        </Card.Body>
                      </Card>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        {!isBootstrapping && activeTab === 'knowledge-base' ? (
          <>
            <Row className="g-4">
              <Col xl={4}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center gap-2">
                    <strong>Knowledge Base WhatsApp</strong>
                    <Badge bg="light" text="dark">
                      {openClawKnowledgeItems.length.toLocaleString('id-ID')} item
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    {!hasSelectedOutletForOpenClaw ? (
                      <div className="text-muted">
                        Pilih outlet terlebih dahulu agar knowledge base WhatsApp bisa dikelola per outlet.
                      </div>
                    ) : (
                      <>
                        <div className="small text-muted mb-3">
                          Isi pertanyaan yang sering masuk, kata kunci, dan jawaban yang ingin dipakai chatbot. Data ini dibaca dulu sebelum fallback ke AI atau pencarian web.
                        </div>
                        <div className="d-grid gap-2">
                          <Button onClick={() => handleOpenOpenClawKnowledgeModal(null)}>
                            <FaPlus className="me-2" />
                            Tambah Knowledge
                          </Button>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              fetchOpenClawKnowledge().catch(error => {
                                Swal.fire('Error', error.message || 'Gagal mengambil knowledge base WhatsApp', 'error');
                              });
                            }}
                            disabled={isOpenClawKnowledgeLoading}
                          >
                            <FaRedoAlt className="me-2" />
                            Refresh
                          </Button>
                        </div>

                        <div className="border rounded-3 p-3 mt-4">
                          <div className="small text-muted">Urutan jawaban</div>
                          <div className="fw-semibold mt-1">Knowledge outlet → data live sistem → AI/web search</div>
                          <div className="small text-muted mt-2">
                            Gunakan prioritas kecil untuk pertanyaan yang paling penting. Contoh: harga PT, jam operasional, jadwal class, dan lokasi.
                          </div>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col xl={8}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <strong>Daftar Knowledge</strong>
                    <div className="d-flex gap-2">
                      <Form.Control
                        value={openClawKnowledgeSearch}
                        onChange={event => setOpenClawKnowledgeSearch(event.target.value)}
                        placeholder="Cari pertanyaan, keyword, atau jawaban"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          fetchOpenClawKnowledge().catch(error => {
                            Swal.fire('Error', error.message || 'Gagal mengambil knowledge base WhatsApp', 'error');
                          });
                        }}
                        disabled={isOpenClawKnowledgeLoading}
                      >
                        <FaRedoAlt />
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {!hasSelectedOutletForOpenClaw ? (
                      <div className="text-muted">
                        Pilih outlet terlebih dahulu untuk melihat knowledge base WhatsApp.
                      </div>
                    ) : isOpenClawKnowledgeLoading ? (
                      <div className="text-center py-5 text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Memuat knowledge base WhatsApp...
                      </div>
                    ) : openClawKnowledgeItems.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        Belum ada knowledge base yang tersimpan untuk outlet ini.
                      </div>
                    ) : (
                      <Table responsive hover className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Pertanyaan</th>
                            <th>Kategori</th>
                            <th>Kata kunci</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th>Update</th>
                            <th className="text-end">Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {openClawKnowledgeItems.map(item => (
                            <tr key={item.id}>
                              <td style={{ minWidth: 260 }}>
                                <div className="fw-semibold">{item.question || '-'}</div>
                                <div className="small text-muted mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                                  {item.answer || '-'}
                                </div>
                              </td>
                              <td>
                                <Badge bg="light" text="dark">
                                  {item.category || 'general'}
                                </Badge>
                              </td>
                              <td style={{ minWidth: 220 }}>
                                <div className="small">
                                  {Array.isArray(item.keywords) && item.keywords.length
                                    ? item.keywords.join(', ')
                                    : '-'}
                                </div>
                                {Array.isArray(item.example_questions) && item.example_questions.length ? (
                                  <div className="small text-muted mt-1">
                                    Contoh: {item.example_questions.slice(0, 2).join(' • ')}
                                  </div>
                                ) : null}
                              </td>
                              <td>{Number(item.priority || 100).toLocaleString('id-ID')}</td>
                              <td>
                                {renderStatusBadge(item.is_active ? 'active' : 'inactive', {
                                  active: 'success',
                                  inactive: 'secondary',
                                })}
                              </td>
                              <td>
                                <div>{formatDateTime(item.updated_at)}</div>
                                <div className="small text-muted">{item.updated_by_name || '-'}</div>
                              </td>
                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    type="button"
                                    onClick={() => handleOpenOpenClawKnowledgeModal(item)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    type="button"
                                    onClick={() => handleDeleteOpenClawKnowledge(item)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Modal
              show={showOpenClawKnowledgeModal}
              onHide={handleCloseOpenClawKnowledgeModal}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  {openClawKnowledgeForm.id ? 'Edit Knowledge Base' : 'Tambah Knowledge Base'}
                </Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleSaveOpenClawKnowledge}>
                <Modal.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label>Kategori</Form.Label>
                      <Form.Control
                        value={openClawKnowledgeForm.category}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          category: event.target.value,
                        }))}
                        placeholder="Contoh: pt, class, membership, operasional"
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Priority</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        value={openClawKnowledgeForm.priority}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          priority: event.target.value,
                        }))}
                        disabled={isOpenClawKnowledgeSaving}
                      />
                      <div className="small text-muted mt-1">Angka kecil diprioritaskan lebih dulu.</div>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <Form.Check
                        type="switch"
                        label={openClawKnowledgeForm.is_active ? 'Knowledge aktif' : 'Knowledge nonaktif'}
                        checked={openClawKnowledgeForm.is_active}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          is_active: event.target.checked,
                        }))}
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Pertanyaan utama</Form.Label>
                      <Form.Control
                        value={openClawKnowledgeForm.question}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          question: event.target.value,
                        }))}
                        placeholder="Contoh: Harga PT berapa?"
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Jawaban utama</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={openClawKnowledgeForm.answer}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          answer: event.target.value,
                        }))}
                        placeholder="Jawaban inti yang ingin dipakai bot"
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Bullets tambahan</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={openClawKnowledgeForm.bullets}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          bullets: event.target.value,
                        }))}
                        placeholder={'Satu baris satu poin\nContoh: Free trial 1x di awal'}
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Kata kunci</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={openClawKnowledgeForm.keywords}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          keywords: event.target.value,
                        }))}
                        placeholder={'Pisahkan dengan koma atau baris baru\nContoh: harga pt, biaya pt, personal trainer'}
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Contoh pertanyaan user</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={openClawKnowledgeForm.example_questions}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          example_questions: event.target.value,
                        }))}
                        placeholder={'Satu baris satu contoh\nContoh: Kak PT 10 sesi berapa ya?'}
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Catatan admin</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={openClawKnowledgeForm.notes}
                        onChange={event => setOpenClawKnowledgeForm(prev => ({
                          ...prev,
                          notes: event.target.value,
                        }))}
                        placeholder="Catatan internal, tidak dikirim ke client"
                        disabled={isOpenClawKnowledgeSaving}
                      />
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={handleCloseOpenClawKnowledgeModal}
                    disabled={isOpenClawKnowledgeSaving}
                  >
                    Tutup
                  </Button>
                  <Button type="submit" disabled={isOpenClawKnowledgeSaving}>
                    {isOpenClawKnowledgeSaving ? 'Menyimpan...' : 'Simpan Knowledge'}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>
          </>
        ) : null}

        {!isBootstrapping && activeTab === 'sales' ? (
          <Row className="g-4">
            <Col xl={5}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white">
                  <strong>Buat Session Sale</strong>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleCreateSale}>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Label>Session client</Form.Label>
                        <Form.Select
                          value={saleForm.client_id}
                          onChange={event => setSaleForm(prev => ({ ...prev, client_id: event.target.value }))}
                          required
                        >
                          <option value="">Pilih session client</option>
                          {clientOptions.map(item => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={12}>
                        <Form.Label>Session staff</Form.Label>
                        <Form.Select
                          value={saleForm.pt_user_id}
                          onChange={event =>
                            setSaleForm(prev => ({
                              ...prev,
                              pt_user_id: event.target.value,
                              product_id: '',
                            }))
                          }
                          required
                        >
                          <option value="">Pilih staff layanan</option>
                          {ptUserOptions.map(item => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={12}>
                        <Form.Label>Session service</Form.Label>
                        <Form.Select
                          value={saleForm.product_id}
                          onChange={event => setSaleForm(prev => ({ ...prev, product_id: event.target.value }))}
                          disabled={!saleForm.pt_user_id}
                          required
                        >
                          <option value="">
                            {saleForm.pt_user_id ? 'Pilih layanan session' : 'Pilih staff layanan dulu'}
                          </option>
                          {saleProductOptions.map(item => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={12}>
                        <Form.Label>Akun pembayaran default</Form.Label>
                        <Form.Select
                          value={saleForm.payment_method}
                          onChange={event => setSaleForm(prev => ({ ...prev, payment_method: event.target.value }))}
                          required
                        >
                          <option value="">Pilih akun pembayaran</option>
                          {paymentAccounts.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </Form.Select>
                        {selectedPaymentAccount ? (
                          <div className="small text-muted mt-2">
                            Dipakai sebagai akun default saat payment atau pelunasan: <strong>{selectedPaymentAccount.name}</strong>
                          </div>
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <Form.Label>Diskon</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={saleForm.discount_amount}
                          onChange={event => setSaleForm(prev => ({ ...prev, discount_amount: event.target.value }))}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Catatan</Form.Label>
                        <Form.Control
                          value={saleForm.notes}
                          onChange={event => setSaleForm(prev => ({ ...prev, notes: event.target.value }))}
                        />
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-4">
                      <Button type="submit" disabled={isSubmitting}>
                        <FaPlus className="me-2" />
                        Buat Sale
                      </Button>
                      <Button variant="outline-secondary" onClick={resetSaleForm} type="button">
                        Reset
                      </Button>
                    </div>
                    <div className="small text-muted mt-3">
                      Sale baru akan langsung membuat contract <strong>frozen</strong>. Payment dicatat belakangan dari panel detail di sebelah kanan.
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <strong>Daftar Session Sale</strong>
                  <div className="d-flex flex-wrap gap-2">
                    <Form.Control
                      value={saleSearch}
                      onChange={event => setSaleSearch(event.target.value)}
                      placeholder="Cari invoice, client, atau staff"
                    />
                    <Form.Select value={saleStatus} onChange={event => setSaleStatus(event.target.value)}>
                      {SALE_STATUS_OPTIONS.map(item => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button variant="outline-secondary" onClick={fetchSales}>
                      <FaRedoAlt />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Client / Staff</th>
                        <th>Produk</th>
                        <th>Nilai / Saldo</th>
                        <th>Status</th>
                        <th className="text-end">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4 text-muted">
                            Belum ada data session sale.
                          </td>
                        </tr>
                      ) : sales.map(item => (
                        <tr
                          key={item.id}
                          className={String(selectedSaleId) === String(item.id) ? 'table-active' : ''}
                        >
                          <td>
                            <div className="fw-semibold">{item.invoice_no}</div>
                            <div className="small text-muted">{formatDateTime(item.created_at)}</div>
                          </td>
                          <td>
                            <div>{item.client_name}</div>
                            <div className="small text-muted">{item.pt_name}</div>
                          </td>
                          <td>
                            <div>{item.product_name}</div>
                            <div className="small text-muted">{item.session_count} sesi</div>
                          </td>
                          <td>
                            <div className="fw-semibold">{formatCurrency(item.net_amount)}</div>
                            <div className="small text-muted">
                              Paid {formatCurrency(item.paid_amount)} • Sisa {formatCurrency(item.outstanding_amount)}
                            </div>
                          </td>
                          <td>
                            <div>{renderSalePaymentStatusBadge(item.payment_status)}</div>
                            <div className="small text-muted mt-1">
                              Contract: {renderContractStatusBadge(item.contract_status)}
                            </div>
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <Button size="sm" variant="outline-primary" onClick={() => handleSelectSale(item)}>
                                Detail
                              </Button>
                              {['pending', 'partial_paid'].includes(String(item.payment_status || '')) && Number(item.outstanding_amount || 0) > 0 ? (
                                <Button size="sm" variant="outline-success" onClick={() => handleMarkPaid(item)}>
                                  Lunasi
                                </Button>
                              ) : null}
                              {item.payment_status === 'pending' && Number(item.paid_amount || 0) <= 0 ? (
                                <Button size="sm" variant="outline-danger" onClick={() => handleCancelSale(item)}>
                                  <FaTrash />
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0 mt-4">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <strong>
                    {selectedSale ? `Detail Session Sale • ${selectedSale.invoice_no}` : 'Detail Session Sale'}
                  </strong>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedSale ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setSalePaymentForm(createSalePaymentForm(selectedSale));
                          fetchSalePayments(selectedSale.id).catch(error => {
                            Swal.fire('Error', error.message || 'Gagal mengambil riwayat payment session sale', 'error');
                          });
                        }}
                      >
                        <FaRedoAlt className="me-2" />
                        Refresh Payment
                      </Button>
                    ) : null}
                  </div>
                </Card.Header>
                <Card.Body>
                  {!selectedSale ? (
                    <div className="text-muted">
                      Pilih salah satu session sale dari tabel di atas untuk melihat payment, outstanding, dan riwayat perubahan status.
                    </div>
                  ) : (
                    <>
                      <Row className="g-3 mb-4">
                        <Col md={6} xl={3}>
                          <div className="border rounded-3 p-3 h-100">
                            <div className="small text-muted">Total Sale</div>
                            <div className="fs-5 fw-semibold">{formatCurrency(selectedSale.net_amount)}</div>
                            <div className="small text-muted">{selectedSale.product_name}</div>
                          </div>
                        </Col>
                        <Col md={6} xl={3}>
                          <div className="border rounded-3 p-3 h-100">
                            <div className="small text-muted">Paid Amount</div>
                            <div className="fs-5 fw-semibold">{formatCurrency(selectedSale.paid_amount)}</div>
                            <div className="small text-muted">{selectedSale.payment_method || '-'}</div>
                          </div>
                        </Col>
                        <Col md={6} xl={3}>
                          <div className="border rounded-3 p-3 h-100">
                            <div className="small text-muted">Outstanding</div>
                            <div className="fs-5 fw-semibold">{formatCurrency(selectedSale.outstanding_amount)}</div>
                            <div className="small text-muted">{selectedSale.session_count} sesi</div>
                          </div>
                        </Col>
                        <Col md={6} xl={3}>
                          <div className="border rounded-3 p-3 h-100">
                            <div className="small text-muted">Status</div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {renderSalePaymentStatusBadge(selectedSale.payment_status)}
                              {renderContractStatusBadge(selectedSale.contract_status)}
                            </div>
                            <div className="small text-muted mt-2">
                              Release: {selectedSale.released_at ? formatDateTime(selectedSale.released_at) : '-'}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row className="g-4">
                        <Col lg={5}>
                          <Card className="bg-light border-0">
                            <Card.Body>
                              <div className="fw-semibold mb-3">Tambah Payment</div>
                              <Form onSubmit={handleAddSalePayment}>
                                <Row className="g-3">
                                  <Col md={12}>
                                    <Form.Label>Client / staff</Form.Label>
                                    <Form.Control
                                      value={`${selectedSale.client_name} • ${selectedSale.pt_name}`}
                                      readOnly
                                    />
                                  </Col>
                                  <Col md={12}>
                                    <Form.Label>Jumlah payment</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      max={Number(selectedSale.outstanding_amount || 0)}
                                      value={salePaymentForm.amount}
                                      onChange={event => setSalePaymentForm(prev => ({ ...prev, amount: event.target.value }))}
                                      disabled={
                                        isSubmitting ||
                                        ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || '')) ||
                                        Number(selectedSale.outstanding_amount || 0) <= 0
                                      }
                                      required
                                    />
                                    <div className="small text-muted mt-2">
                                      Sisa yang bisa dibayar: <strong>{formatCurrency(selectedSale.outstanding_amount)}</strong>
                                    </div>
                                  </Col>
                                  <Col md={12}>
                                    <Form.Label>Akun pembayaran</Form.Label>
                                    <Form.Select
                                      value={salePaymentForm.payment_method}
                                      onChange={event => setSalePaymentForm(prev => ({ ...prev, payment_method: event.target.value }))}
                                      disabled={
                                        isSubmitting ||
                                        ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || ''))
                                      }
                                      required
                                    >
                                      <option value="">Pilih akun pembayaran</option>
                                      {paymentAccounts.map(item => (
                                        <option key={item.id} value={item.id}>
                                          {item.name}
                                        </option>
                                      ))}
                                    </Form.Select>
                                    {selectedSalePaymentAccount ? (
                                      <div className="small text-muted mt-2">
                                        Payment akan memakai akun: <strong>{selectedSalePaymentAccount.name}</strong>
                                      </div>
                                    ) : null}
                                  </Col>
                                  <Col md={12}>
                                    <Form.Label>Referensi payment</Form.Label>
                                    <Form.Control
                                      value={salePaymentForm.payment_reference}
                                      onChange={event => setSalePaymentForm(prev => ({ ...prev, payment_reference: event.target.value }))}
                                      disabled={
                                        isSubmitting ||
                                        ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || ''))
                                      }
                                      placeholder="Invoice, transfer ref, atau catatan kasir"
                                    />
                                  </Col>
                                  <Col md={12}>
                                    <Form.Label>Tanggal payment</Form.Label>
                                    <Form.Control
                                      type="datetime-local"
                                      value={salePaymentForm.paid_at}
                                      onChange={event => setSalePaymentForm(prev => ({ ...prev, paid_at: event.target.value }))}
                                      disabled={
                                        isSubmitting ||
                                        ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || ''))
                                      }
                                      required
                                    />
                                  </Col>
                                  <Col md={12}>
                                    <Form.Label>Catatan</Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={salePaymentForm.notes}
                                      onChange={event => setSalePaymentForm(prev => ({ ...prev, notes: event.target.value }))}
                                      disabled={
                                        isSubmitting ||
                                        ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || ''))
                                      }
                                      placeholder="Catatan tambahan payment"
                                    />
                                  </Col>
                                  <Col md={12}>
                                    <Form.Check
                                      type="switch"
                                      label={salePaymentForm.auto_release ? 'Aktifkan contract saat payment masuk' : 'Biarkan contract tetap frozen'}
                                      checked={salePaymentForm.auto_release}
                                      onChange={event => setSalePaymentForm(prev => ({ ...prev, auto_release: event.target.checked }))}
                                      disabled={
                                        isSubmitting ||
                                        ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || ''))
                                      }
                                    />
                                  </Col>
                                </Row>
                                <div className="d-flex gap-2 mt-4">
                                  <Button
                                    type="submit"
                                    disabled={
                                      isSubmitting ||
                                      ['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || '')) ||
                                      Number(selectedSale.outstanding_amount || 0) <= 0
                                    }
                                  >
                                    <FaMoneyBillWave className="me-2" />
                                    Tambah Payment
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline-secondary"
                                    onClick={() => setSalePaymentForm(createSalePaymentForm(selectedSale))}
                                  >
                                    Reset Form
                                  </Button>
                                </div>
                                {['paid', 'cancelled', 'refunded'].includes(String(selectedSale.payment_status || '')) || Number(selectedSale.outstanding_amount || 0) <= 0 ? (
                                  <div className="small text-muted mt-3">
                                    Payment tambahan dinonaktifkan karena sale ini sudah selesai atau tidak punya outstanding.
                                  </div>
                                ) : null}
                              </Form>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col lg={7}>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <div className="fw-semibold">Riwayat Payment</div>
                              <div className="small text-muted">
                                Semua payment untuk sale ini dicatat di tab yang sama tanpa pindah halaman.
                              </div>
                            </div>
                          </div>
                          <Table responsive hover className="align-middle mb-0">
                            <thead>
                              <tr>
                                <th>Tanggal</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Referensi</th>
                                <th className="text-end">Aksi</th>
                              </tr>
                            </thead>
                            <tbody>
                              {isSalePaymentLoading ? (
                                <tr>
                                  <td colSpan={5} className="text-center py-4 text-muted">
                                    Memuat payment...
                                  </td>
                                </tr>
                              ) : salePayments.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="text-center py-4 text-muted">
                                    Belum ada payment untuk session sale ini.
                                  </td>
                                </tr>
                              ) : salePayments.map(payment => {
                                const canChangePaymentStatus = payment.payment_status === 'confirmed'
                                  && !payment.fitness_transaction_id
                                  && !payment.journal_id;

                                return (
                                  <tr key={payment.id}>
                                    <td>
                                      <div>{formatDateTime(payment.paid_at)}</div>
                                      <div className="small text-muted">{payment.payment_method || '-'}</div>
                                    </td>
                                    <td>
                                      <div className="fw-semibold">{formatCurrency(payment.amount)}</div>
                                      <div className="small text-muted">
                                        {payment.notes || '-'}
                                      </div>
                                    </td>
                                    <td>
                                      <div>{renderSalePaymentRecordStatusBadge(payment.payment_status)}</div>
                                      <div className="small text-muted mt-1">
                                        {payment.fitness_transaction_id || payment.journal_id
                                          ? 'Sudah masuk accounting'
                                          : 'Belum diposting ke accounting'}
                                      </div>
                                    </td>
                                    <td>{payment.payment_reference || '-'}</td>
                                    <td className="text-end">
                                      <div className="d-flex justify-content-end gap-2">
                                        {canChangePaymentStatus ? (
                                          <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => handleChangeSalePaymentStatus(payment, 'void')}
                                          >
                                            Void
                                          </Button>
                                        ) : null}
                                        {canChangePaymentStatus ? (
                                          <Button
                                            size="sm"
                                            variant="outline-dark"
                                            onClick={() => handleChangeSalePaymentStatus(payment, 'refund')}
                                          >
                                            Refund
                                          </Button>
                                        ) : null}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        {!isBootstrapping && activeTab === 'coach-payable' ? (
          <Row className="g-4">
            <Col xl={4}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white">
                  <strong>Post Coach Payout</strong>
                </Card.Header>
                <Card.Body>
                  <div className="small text-muted mb-3">
                    Expense coach sudah diakui saat transaksi class/PT dipost. Payout di sini hanya
                    mengurangi <strong>Coach Payable</strong> dan mengeluarkan kas atau bank, jadi tidak
                    menambah expense kedua kali.
                  </div>

                  <Row className="g-3 mb-4">
                    <Col sm={6}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted">Accrued</div>
                        <div className="fs-5 fw-semibold">{formatCurrency(totalCoachPayableAccrued)}</div>
                        <div className="small text-muted">Akumulasi share coach yang belum lunas penuh</div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="border rounded-3 p-3 h-100">
                        <div className="small text-muted">Sudah dibayar</div>
                        <div className="fs-5 fw-semibold">{formatCurrency(totalCoachPayablePaid)}</div>
                        <div className="small text-muted">Total payout coach yang sudah dipost</div>
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className="border rounded-3 p-3 h-100 bg-light">
                        <div className="small text-muted">Outstanding</div>
                        <div className="fs-4 fw-semibold text-success">{formatCurrency(totalCoachPayableOutstanding)}</div>
                        <div className="small text-muted">
                          {coachPayables.length} coach masih punya saldo hutang
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Form onSubmit={handleCoachPayout}>
                    <Row className="g-3">
                      <Col md={12}>
                        <div className={`border rounded-3 p-3 h-100 ${isBatchCoachPayoutMode ? 'bg-light' : ''}`}>
                          <div className="small text-muted mb-2">
                            {isBatchCoachPayoutMode ? 'Mode payout batch' : 'Mode payout single'}
                          </div>
                          {isBatchCoachPayoutMode ? (
                            <>
                              <div className="fw-semibold">
                                {selectedCoachPayables.length} coach dipilih
                              </div>
                              <div className="fs-5 fw-semibold text-success mt-1">
                                {formatCurrency(selectedCoachPayableTotal)}
                              </div>
                              <div className="small text-muted mt-2">
                                {selectedCoachPayables
                                  .map(item => item.coach_name)
                                  .slice(0, 4)
                                  .join(', ')}
                                {selectedCoachPayables.length > 4
                                  ? ` +${selectedCoachPayables.length - 4} lainnya`
                                  : ''}
                              </div>
                              <div className="small text-muted mt-2">
                                Semua coach yang dicentang akan dibayar lunas sesuai outstanding masing-masing.
                              </div>
                            </>
                          ) : (
                            <>
                              <Form.Label>Coach outstanding</Form.Label>
                              <Form.Select
                                value={coachPayoutForm.coach_name}
                                onChange={event => {
                                  const nextCoachName = event.target.value;
                                  const matchedCoach = coachPayables.find(
                                    item => String(item.coach_name) === String(nextCoachName)
                                  );
                                  setSelectedCoachPayableNames([]);
                                  setCoachPayoutForm(prev => ({
                                    ...prev,
                                    coach_name: nextCoachName,
                                    amount: matchedCoach ? String(Number(matchedCoach.outstanding_amount || 0)) : '',
                                  }));
                                }}
                                required
                              >
                                <option value="">Pilih coach</option>
                                {coachPayables.map(item => (
                                  <option key={item.coach_name} value={item.coach_name}>
                                    {item.coach_name} • outstanding {formatCurrency(item.outstanding_amount)}
                                  </option>
                                ))}
                              </Form.Select>
                              <div className="small text-muted mt-2">
                                Klik tombol <strong>Bayar</strong> di tabel untuk payout parsial satu coach, atau centang beberapa coach untuk payout batch.
                              </div>
                            </>
                          )}
                        </div>
                      </Col>
                      <Col md={6}>
                        <Form.Label>Tanggal payout</Form.Label>
                        <Form.Control
                          type="date"
                          value={coachPayoutForm.payout_date}
                          onChange={event => setCoachPayoutForm(prev => ({ ...prev, payout_date: event.target.value }))}
                          required
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>{isBatchCoachPayoutMode ? 'Total payout terpilih' : 'Nominal payout'}</Form.Label>
                        {isBatchCoachPayoutMode ? (
                          <>
                            <Form.Control
                              type="text"
                              value={formatCurrency(selectedCoachPayableTotal)}
                              readOnly
                            />
                            <div className="small text-muted mt-2">
                              Total ini dihitung otomatis dari semua hutang coach yang dicentang.
                            </div>
                          </>
                        ) : (
                          <>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              value={coachPayoutForm.amount}
                              onChange={event => setCoachPayoutForm(prev => ({ ...prev, amount: event.target.value }))}
                              required
                            />
                            <div className="small text-muted mt-2">
                              Outstanding coach terpilih:{' '}
                              <strong>{formatCurrency(selectedCoachPayable?.outstanding_amount || 0)}</strong>
                            </div>
                          </>
                        )}
                      </Col>
                      <Col md={12}>
                        <Form.Label>Akun pembayaran</Form.Label>
                        <Form.Select
                          value={coachPayoutForm.payment_account_id}
                          onChange={event =>
                            setCoachPayoutForm(prev => {
                              const nextAccountId = event.target.value;
                              const nextAccount = paymentAccounts.find(item => String(item.id) === String(nextAccountId));
                              const nextIsCash = isCashLikeAccount(nextAccount);
                              return {
                                ...prev,
                                payment_account_id: nextAccountId,
                                deduct_from_shift_cash: nextIsCash ? prev.deduct_from_shift_cash : false,
                                shift_cashier_id: nextIsCash ? prev.shift_cashier_id : '',
                                shift_cashier_name: nextIsCash ? prev.shift_cashier_name : '',
                              };
                            })
                          }
                          required
                        >
                          <option value="">Pilih akun asset</option>
                          {paymentAccounts.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.id} - {item.name}
                            </option>
                          ))}
                        </Form.Select>
                        {selectedCoachPaymentAccount ? (
                          <div className="small text-muted mt-2">
                            Payout akan memakai akun: <strong>{selectedCoachPaymentAccount.name}</strong>
                          </div>
                        ) : null}
                      </Col>
                      <Col md={12}>
                        <Form.Check
                          type="switch"
                          id="coach-payout-deduct-shift-cash"
                          label="Kurangi kas shift operator saat payout ini dipost"
                          checked={Boolean(coachPayoutForm.deduct_from_shift_cash)}
                          disabled={!selectedCoachPaymentAccountIsCash}
                          onChange={event =>
                            setCoachPayoutForm(prev => ({
                              ...prev,
                              deduct_from_shift_cash: event.target.checked,
                              shift_cashier_id: event.target.checked ? prev.shift_cashier_id : '',
                              shift_cashier_name: event.target.checked ? prev.shift_cashier_name : '',
                            }))
                          }
                        />
                        <div className="small text-muted mt-2">
                          {selectedCoachPaymentAccount
                            ? selectedCoachPaymentAccountIsCash
                              ? 'Aktifkan opsi ini kalau payout benar-benar dibayar tunai dari laci kas shift saat ini.'
                              : 'Akun yang dipilih bukan kas tunai, jadi payout ini tidak akan mengurangi kas shift.'
                            : 'Pilih akun pembayaran dulu untuk menentukan apakah payout ini boleh mengurangi kas shift.'}
                        </div>
                      </Col>
                      {selectedCoachPaymentAccountIsCash && coachPayoutForm.deduct_from_shift_cash ? (
                        <Col md={12}>
                          <Form.Label>Shift staff yang dikurangi</Form.Label>
                          <Form.Select
                            value={coachPayoutForm.shift_cashier_id}
                            onChange={event => {
                              const nextStaffId = event.target.value;
                              const nextStaff = coachShiftStaffOptions.find(
                                item => String(item.staff_id) === String(nextStaffId)
                              );
                              setCoachPayoutForm(prev => ({
                                ...prev,
                                shift_cashier_id: nextStaffId,
                                shift_cashier_name: nextStaff?.staff_name || '',
                              }));
                            }}
                            required
                          >
                            <option value="">Pilih staff shift</option>
                            {coachShiftStaffOptions.map(item => (
                              <option key={item.staff_id} value={item.staff_id}>
                                {item.staff_name}
                                {item.job_title ? ` • ${item.job_title}` : ''}
                              </option>
                            ))}
                          </Form.Select>
                          <div className="small text-muted mt-2">
                            Pilih staff yang benar-benar sedang pegang laci kas supaya pengurang shift masuk ke closing yang tepat.
                          </div>
                        </Col>
                      ) : null}
                      <Col md={12}>
                        <Form.Label>Catatan</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={coachPayoutForm.notes}
                          onChange={event => setCoachPayoutForm(prev => ({ ...prev, notes: event.target.value }))}
                          placeholder="Contoh: Payout coach minggu ke-4 Maret"
                        />
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 mt-4">
                      <Button type="submit" disabled={isSubmitting || coachPayables.length === 0}>
                        <FaMoneyBillWave className="me-2" />
                        {isSubmitting
                          ? 'Memproses...'
                          : isBatchCoachPayoutMode
                            ? `Bayar ${selectedCoachPayables.length.toLocaleString('id-ID')} Coach`
                            : 'Post Coach Payout'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={clearCoachPayoutComposer}
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={8}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <strong>Daftar Hutang Coach</strong>
                    <div className="small text-muted mt-1">
                      Fokus pada saldo hutang yang masih terbuka. Riwayat payout yang sudah dibayar ada di panel bawah.
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedCoachPayables.length > 0 ? (
                      <Badge bg="light" text="dark">
                        {selectedCoachPayables.length.toLocaleString('id-ID')} dipilih • {formatCurrency(selectedCoachPayableTotal)}
                      </Badge>
                    ) : null}
                    <Button variant="outline-secondary" size="sm" onClick={() => {
                      Promise.all([
                        fetchCoachPayables(),
                        fetchCoachPayouts(),
                        fetchCoachShiftStaffOptions(),
                        selectedCoachPayableDetail?.coach_name
                          ? fetchCoachPayableDetails(selectedCoachPayableDetail.coach_name)
                          : Promise.resolve(),
                      ]).catch(error => {
                        Swal.fire('Error', error.message || 'Gagal mengambil data coach payable', 'error');
                      });
                    }}>
                      <FaRedoAlt className="me-2" />
                      Refresh
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th className="text-center" style={{ width: '56px' }}>
                          <Form.Check
                            type="checkbox"
                            checked={areAllCoachPayablesSelected}
                            onChange={handleToggleAllCoachPayables}
                            disabled={coachPayables.length === 0}
                            aria-label="Pilih semua hutang coach"
                          />
                        </th>
                        <th>Coach</th>
                        <th>Class</th>
                        <th>PT</th>
                        <th>Accrued</th>
                        <th>Paid</th>
                        <th>Outstanding</th>
                        <th>Status</th>
                        <th className="text-end">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coachPayables.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-4 text-muted">
                            Belum ada hutang coach outstanding di outlet ini.
                          </td>
                        </tr>
                      ) : coachPayables.map((item, index) => {
                        const coachName = resolveCoachPayableName(item);
                        const coachPayableKey = item.coach_payable_key || normalizeCoachPayableKey(coachName) || `coach-payable-${index}`;
                        const isSelected = selectedCoachPayableKeySet.has(normalizeCoachPayableKey(coachName));

                        return (
                        <tr key={coachPayableKey} className={isSelected ? 'table-active' : ''}>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleCoachPayableSelection(item)}
                              aria-label={`Pilih hutang coach ${coachName || 'tanpa nama'}`}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">{coachName || '-'}</div>
                            <div className="small text-muted">
                              {Number(item.transaction_count || 0).toLocaleString('id-ID')} transaksi
                              {Number(item.adjustment_count || 0) > 0 ? ` • ${Number(item.adjustment_count || 0).toLocaleString('id-ID')} koreksi` : ''}
                            </div>
                          </td>
                          <td>{formatCurrency(item.class_accrued_amount)}</td>
                          <td>{formatCurrency(item.pt_accrued_amount)}</td>
                          <td>{formatCurrency(item.accrued_amount)}</td>
                          <td>{formatCurrency(item.paid_amount)}</td>
                          <td className="fw-semibold text-success">{formatCurrency(item.outstanding_amount)}</td>
                          <td>
                            {renderStatusBadge(
                              Number(item.outstanding_amount || 0) > 0 ? 'outstanding' : 'paid',
                              { outstanding: 'warning', paid: 'success' }
                            )}
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                type="button"
                                onClick={event => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleOpenCoachPayableDetail(item);
                                }}
                                disabled={!coachName}
                              >
                                Detail
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-warning"
                                type="button"
                                onClick={() => handleOpenCoachAdjustmentModal(item)}
                              >
                                Koreksi
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-success"
                                type="button"
                                onClick={() => handlePrefillSingleCoachPayout(item)}
                              >
                                Bayar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <strong>Riwayat Coach Payout</strong>
                    <div className="small text-muted mt-1">
                      Menampilkan payout coach yang sudah dipost ke accounting beserta jurnalnya.
                    </div>
                  </div>
                  <Badge bg="light" text="dark">
                    {sortedCoachPayouts.length.toLocaleString('id-ID')} payout
                  </Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Tanggal</th>
                        <th>Coach</th>
                        <th>Nominal</th>
                        <th>Akun bayar</th>
                        <th>Shift</th>
                        <th>Journal</th>
                        <th>Status</th>
                        <th>Catatan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCoachPayouts.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4 text-muted">
                            Belum ada coach payout yang tercatat.
                          </td>
                        </tr>
                      ) : sortedCoachPayouts.map(item => (
                        <tr key={item.id}>
                          <td>
                            <div className="fw-semibold">{item.payment_no || '-'}</div>
                            <div className="small text-muted">
                              {formatDateTime(item.payout_date || item.created_at)}
                            </div>
                          </td>
                          <td>{item.coach_name || '-'}</td>
                          <td>{formatCurrency(item.amount)}</td>
                          <td>
                            <div>{item.payment_account_name || item.payment_method || '-'}</div>
                            <div className="small text-muted">{item.payment_account_id || '-'}</div>
                          </td>
                          <td>
                            {item.deduct_from_shift_cash ? (
                              <>
                                {renderStatusBadge('Mengurangi shift', { 'Mengurangi shift': 'warning' })}
                                <div className="small text-muted mt-1">{item.shift_cashier_name || '-'}</div>
                              </>
                            ) : (
                              <span className="small text-muted">Tidak</span>
                            )}
                          </td>
                          <td>{item.journal_id || '-'}</td>
                          <td>
                            {renderStatusBadge(item.accounting_status || 'posted', {
                              posted: 'success',
                              draft: 'secondary',
                              failed: 'danger',
                            })}
                          </td>
                          <td>{item.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        <Modal
          show={showCoachAdjustmentModal}
          onHide={handleCloseCoachAdjustmentModal}
          centered
        >
          <Form onSubmit={handleCoachPayableAdjustment}>
            <Modal.Header closeButton>
              <Modal.Title>Koreksi Hutang Coach</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Coach</Form.Label>
                    <Form.Control value={coachAdjustmentForm.coach_name} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Jenis koreksi</Form.Label>
                    <Form.Select
                      value={coachAdjustmentForm.adjustment_type}
                      onChange={event => setCoachAdjustmentForm(prev => ({
                        ...prev,
                        adjustment_type: event.target.value,
                      }))}
                    >
                      <option value="decrease">Kurangi hutang coach</option>
                      <option value="increase">Tambah hutang coach</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tanggal koreksi</Form.Label>
                    <Form.Control
                      type="date"
                      value={coachAdjustmentForm.adjustment_date}
                      onChange={event => setCoachAdjustmentForm(prev => ({
                        ...prev,
                        adjustment_date: event.target.value,
                      }))}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Nominal koreksi</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={coachAdjustmentForm.amount}
                      onChange={event => setCoachAdjustmentForm(prev => ({
                        ...prev,
                        amount: event.target.value,
                      }))}
                      placeholder="Contoh: 50000"
                      required
                    />
                  </Form.Group>
                  <div className="small text-muted mt-2">
                    {coachAdjustmentForm.adjustment_type === 'increase'
                      ? 'Jurnal akan menambah beban coach dan menaikkan Coach Payable.'
                      : 'Jurnal akan mengurangi Coach Payable dan mengurangi beban coach.'}
                  </div>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Catatan koreksi</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={coachAdjustmentForm.notes}
                      onChange={event => setCoachAdjustmentForm(prev => ({
                        ...prev,
                        notes: event.target.value,
                      }))}
                      placeholder="Contoh: koreksi share coach terinput dobel"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" type="button" onClick={handleCloseCoachAdjustmentModal}>
                Tutup
              </Button>
              <Button variant="warning" type="submit" disabled={isCoachPayableAdjustmentSaving}>
                {isCoachPayableAdjustmentSaving ? 'Menyimpan...' : 'Simpan koreksi'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal
          show={showCoachPayableDetailModal}
          onHide={handleCloseCoachPayableDetail}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Detail Transaksi Coach
              {selectedCoachPayableDetail?.coach_name ? ` • ${selectedCoachPayableDetail.coach_name}` : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCoachPayableDetail ? (
              <>
                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="small text-muted">Coach</div>
                      <div className="fs-5 fw-semibold">{selectedCoachPayableDetail.coach_name || '-'}</div>
                      <div className="small text-muted mt-2">
                        {Number(selectedCoachPayableDetail.transaction_count || 0).toLocaleString('id-ID')} transaksi
                        {' • '}
                        {Number(selectedCoachPayableDetail.adjustment_count || 0).toLocaleString('id-ID')} koreksi
                        {' • '}
                        {Number(selectedCoachPayableDetail.payout_count || 0).toLocaleString('id-ID')} payout
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="small text-muted">Outstanding</div>
                      <div className="fs-5 fw-semibold text-success">
                        {formatCurrency(selectedCoachPayableDetail.outstanding_amount)}
                      </div>
                      <div className="small text-muted mt-2">
                        Sudah dibayar {formatCurrency(selectedCoachPayableDetail.paid_amount)}
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="small text-muted">Net hutang coach</div>
                      <div className="fs-5 fw-semibold">{formatCurrency(totalCoachPayableDetailNet)}</div>
                      <div className="small text-muted mt-2">
                        Share transaksi {formatCurrency(totalCoachPayableDetailSalesShare)}
                        {' • '}
                        Koreksi {formatSignedCurrency(totalCoachPayableDetailAdjustment)}
                        {' • '}
                        Payout {formatCurrency(totalCoachPayableDetailPayout)}
                      </div>
                    </div>
                  </Col>
                </Row>

                {isCoachPayableDetailLoading ? (
                  <div className="py-5 text-center text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Memuat detail transaksi coach...
                  </div>
                ) : (
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Tanggal</th>
                        <th>Client</th>
                        <th>Channel</th>
                        <th>Keterangan</th>
                        <th>Nilai</th>
                        <th>Mutasi Hutang</th>
                        <th>Saldo Hutang</th>
                        <th>Referensi</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coachPayableDetails.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-4 text-muted">
                            Belum ada detail transaksi untuk coach ini.
                          </td>
                        </tr>
                      ) : coachPayableDetails.map(item => (
                        <tr key={`${item.detail_type}-${item.id}`}>
                          <td>
                            <div className="fw-semibold">{formatDateTime(item.created_at)}</div>
                            <div className="small text-muted">{item.reference_no || `#${item.id}`}</div>
                          </td>
                          <td>{item.detail_type === 'sale' ? item.client_name || '-' : '-'}</td>
                          <td>{formatFitnessCoachTransactionTypeLabel(item.transaction_type)}</td>
                          <td>
                            {item.detail_type === 'adjustment' ? (
                              <>
                                <div className="fw-semibold">
                                  {item.adjustment_type === 'increase' ? 'Tambah hutang coach' : 'Kurangi hutang coach'}
                                </div>
                                <div className="small text-muted">{item.notes || '-'}</div>
                              </>
                            ) : item.detail_type === 'payout' ? (
                              <>
                                <div className="fw-semibold">Pembayaran coach</div>
                                <div className="small text-muted">{item.notes || item.payment_method || '-'}</div>
                              </>
                            ) : (
                              item.class_name || item.pt_name || '-'
                            )}
                          </td>
                          <td>{formatCurrency(item.gross_amount)}</td>
                          <td className={`fw-semibold ${Number(item.coach_share_amount || 0) < 0 ? 'text-danger' : 'text-success'}`}>
                            {item.detail_type === 'sale'
                              ? formatCurrency(item.coach_share_amount)
                              : formatSignedCurrency(item.coach_share_amount)}
                          </td>
                          <td>
                            <div className="fw-semibold">{formatCurrency(item.outstanding_after_amount)}</div>
                            <div className="small text-muted">
                              Dari {formatCurrency(item.outstanding_before_amount)}
                            </div>
                          </td>
                          <td>
                            {item.detail_type === 'adjustment' ? (
                              <>
                                <div>{item.reference_no || '-'}</div>
                                <div className="small text-muted">
                                  {item.journal_id ? `Jurnal ${item.journal_id}` : '-'}
                                </div>
                              </>
                            ) : item.detail_type === 'payout' ? (
                              <>
                                <div>{item.reference_no || '-'}</div>
                                <div className="small text-muted">
                                  {[item.payment_method || '-', item.journal_id ? `Jurnal ${item.journal_id}` : '']
                                    .filter(Boolean)
                                    .join(' • ')}
                                </div>
                              </>
                            ) : (
                              item.payment_method || '-'
                            )}
                          </td>
                          <td>{item.status || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-warning"
              type="button"
              onClick={() => handleOpenCoachAdjustmentModal(selectedCoachPayableDetail)}
              disabled={!selectedCoachPayableDetail}
            >
              <FaEdit className="me-2" />
              Koreksi Hutang
            </Button>
            <Button
              variant="outline-dark"
              type="button"
              onClick={handlePrintCoachPayableDetail}
              disabled={!selectedCoachPayableDetail || isCoachPayableDetailLoading}
            >
              <FaPrint className="me-2" />
              Print
            </Button>
            <Button
              variant="outline-success"
              type="button"
              onClick={handleExportCoachPayableDetailExcel}
              disabled={!selectedCoachPayableDetail || isCoachPayableDetailLoading}
            >
              <FaFileExcel className="me-2" />
              Export Excel
            </Button>
            <Button
              variant="outline-danger"
              type="button"
              onClick={handleExportCoachPayableDetailPdf}
              disabled={!selectedCoachPayableDetail || isCoachPayableDetailLoading}
            >
              <FaFilePdf className="me-2" />
              Export PDF
            </Button>
            <Button variant="outline-secondary" onClick={handleCloseCoachPayableDetail}>
              Tutup
            </Button>
          </Modal.Footer>
        </Modal>

        {!isBootstrapping && activeTab === 'schedule' ? (
          <Row className="g-4">
            <Col xl={5}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white">
                  <strong>{scheduleForm.id ? 'Edit Jadwal Session' : 'Tambah Jadwal Session'}</strong>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSaveSchedule}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label>Tanggal</Form.Label>
                        <Form.Control
                          type="date"
                          value={scheduleForm.schedule_date}
                          onChange={event => setScheduleForm(prev => ({ ...prev, schedule_date: event.target.value }))}
                          required
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label>Jam</Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduleForm.schedule_time}
                          onChange={event => setScheduleForm(prev => ({ ...prev, schedule_time: event.target.value }))}
                          required
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label>Session staff</Form.Label>
                        <Form.Select
                          value={scheduleForm.pt_name}
                          onChange={event => setScheduleForm(prev => ({ ...prev, pt_name: event.target.value }))}
                          required
                          disabled={schedulePTOptions.length === 0}
                        >
                          <option value="">Pilih staff layanan</option>
                          {schedulePTOptions.map(item => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                        {schedulePTOptions.length === 0 ? (
                          <div className="small text-muted mt-2">
                            Belum ada staff layanan aktif. Siapkan dulu pada master layanan.
                          </div>
                        ) : null}
                      </Col>
                      <Col md={12}>
                        <Form.Label>Session client</Form.Label>
                        <Form.Select
                          value={scheduleForm.client_name}
                          onChange={event => setScheduleForm(prev => ({ ...prev, client_name: event.target.value }))}
                          required
                          disabled={scheduleClientOptions.length === 0}
                        >
                          <option value="">Pilih session client</option>
                          {scheduleClientOptions.map(item => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </Form.Select>
                        {scheduleClientOptions.length === 0 ? (
                          <div className="small text-muted mt-2">
                            Belum ada session client aktif. Tambahkan dulu pada tab Session Clients.
                          </div>
                        ) : null}
                      </Col>
                      <Col md={12}>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={scheduleForm.status}
                          onChange={event => setScheduleForm(prev => ({ ...prev, status: event.target.value }))}
                        >
                          {PT_SCHEDULE_STATUS_OPTIONS.map(item => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={12}>
                        <Form.Label>Catatan</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={scheduleForm.notes}
                          onChange={event => setScheduleForm(prev => ({ ...prev, notes: event.target.value }))}
                          placeholder="Catatan internal jadwal session"
                        />
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-4">
                      <Button type="submit" disabled={isSubmitting}>
                        <FaPlus className="me-2" />
                        {scheduleForm.id ? 'Update Jadwal' : 'Simpan Jadwal'}
                      </Button>
                      <Button variant="outline-secondary" onClick={resetScheduleForm} type="button">
                        Reset
                      </Button>
                      <Button as={Link} to="/pt-schedule-setting" variant="outline-dark">
                        <FaCalendarAlt className="me-2" />
                        Lihat Calendar
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={7}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <strong>Daftar Jadwal Session</strong>
                  <div className="d-flex flex-wrap gap-2">
                    <Form.Control
                      type="date"
                      value={scheduleFilterDate}
                      onChange={event => setScheduleFilterDate(event.target.value)}
                    />
                    <Button variant="outline-secondary" onClick={fetchSchedules}>
                      <FaRedoAlt />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table responsive hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Tanggal</th>
                        <th>Jam</th>
                        <th>Staff</th>
                        <th>Client</th>
                        <th>Status</th>
                        <th>Sisa sesi</th>
                        <th className="text-end">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            Belum ada jadwal session pada tanggal ini.
                          </td>
                        </tr>
                      ) : schedules.map(item => (
                        <tr key={item.id}>
                          <td>{item.schedule_date}</td>
                          <td>{item.schedule_time}</td>
                          <td>{item.pt_name}</td>
                          <td>{item.client_name}</td>
                          <td>{renderScheduleStatusBadge(item.status)}</td>
                          <td>{item.remaining_sessions}</td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  setScheduleForm({
                                    id: item.id,
                                    pt_name: item.pt_name || '',
                                    client_name: item.client_name || '',
                                    schedule_date: item.schedule_date || formatDateInput(),
                                    schedule_time: item.schedule_time || '07:00',
                                    status: item.status || 'scheduled',
                                    notes: item.notes || '',
                                  });
                                  setActiveTab('schedule');
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDeleteSchedule(item)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}

        {!isBootstrapping && activeTab === 'migration' ? (
          <Row className="g-4">
            <Col xl={8}>
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                  <strong>Ringkasan Legacy Backfill</strong>
                  <Button variant="outline-secondary" onClick={() => fetchMigrationSummary()} disabled={isMigrationSummaryLoading}>
                    <FaRedoAlt className="me-2" />
                    {isMigrationSummaryLoading ? 'Memuat...' : 'Refresh'}
                  </Button>
                </Card.Header>
                <Card.Body>
                  {isMigrationSummaryLoading && !migrationSummary ? (
                    <div className="text-muted small mb-3">Memuat ringkasan migrasi...</div>
                  ) : null}
                  <Row className="g-3">
                    {[
                      { label: 'Legacy Packages', value: migrationSummary?.legacy_package_total || 0, icon: FaBoxOpen },
                      { label: 'Legacy Schedules', value: migrationSummary?.legacy_schedule_total || 0, icon: FaCalendarAlt },
                      { label: 'Contracts Migrated', value: migrationSummary?.legacy_contract_total || 0, icon: FaCheckCircle },
                      { label: 'Bookings Migrated', value: migrationSummary?.legacy_booking_total || 0, icon: FaPlay },
                      { label: 'Pending Packages', value: migrationSummary?.pending_package_total || 0, icon: FaMoneyBillWave },
                      { label: 'Pending Schedules', value: migrationSummary?.pending_schedule_total || 0, icon: FaCreditCard },
                    ].map(item => {
                      const Icon = item.icon;
                      return (
                        <Col md={6} xl={4} key={item.label}>
                          <Card className="h-100 bg-light border-0">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div className="text-muted small">{item.label}</div>
                                  <div className="fs-4 fw-semibold">{item.value}</div>
                                </div>
                                <Badge bg="white" text="dark" pill>
                                  <Icon />
                                </Badge>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>

              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white">
                  <strong>Migration Result</strong>
                </Card.Header>
                <Card.Body>
                  {lastMigrationResult ? (
                    <Row className="g-3">
                      <Col xs={12}>
                        <div className="small text-muted">
                          Aksi terakhir: <strong>{lastMigrationActionName || 'Legacy Migration'}</strong>
                        </div>
                      </Col>
                      {migrationResultMetrics.map(([label, value]) => (
                        <Col md={6} key={label}>
                          <div className="border rounded-3 p-3 h-100">
                            <div className="small text-muted">{label}</div>
                            <div className="fs-5 fw-semibold">{value || 0}</div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="text-muted">
                      Belum ada eksekusi backfill pada sesi ini.
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white">
                  <strong>Jalankan Fase 2</strong>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    Backfill membaca tabel legacy global, membuat sale legacy sintetis,
                    contract, credit ledger, lalu booking + consume ledger dari jadwal lama.
                  </p>
                  <ul className="small text-muted ps-3">
                    <li>Data legacy tanpa paket akan ditandai sebagai orphan dan dilewati.</li>
                    <li>Backfill aman dijalankan ulang untuk insert yang belum ada.</li>
                    <li>Sinkronisasi completed aman dijalankan ulang untuk mengurangi sisa sesi sesuai jadwal lama yang sudah selesai.</li>
                    <li>Schema fase 2 sudah otomatis dibuat saat backend start.</li>
                  </ul>
                  <div className="d-grid gap-2 mt-4">
                    <Button onClick={handleRunBackfill} disabled={isSubmitting}>
                      <FaPlay className="me-2" />
                      Jalankan Backfill Fase 2
                    </Button>
                    <Button variant="outline-success" onClick={handleRunCompletedSync} disabled={isSubmitting}>
                      <FaCheckCircle className="me-2" />
                      Sinkronkan Completed Legacy
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : null}
      </Container>
    </div>
  );
}

export default PTPhase15Manager;
